/**
 * The Workshop — bespoke form sprite templates (op-lists).
 *
 * Authored per the ITEM_AUTHORING_TEMPLATE: each is an ordered op-list over
 * the baseline palette, recolored at compile time by the material profile
 * (`fill`/`shade` tokens). A form NOT listed here falls back to its family
 * template (`sprites.ts`), so coverage is complete from day one and this table
 * fills in over W3/W7 for the forms that deserve bespoke art.
 *
 * Style budget (authoring §5): 6–16 ops, flat fills + one shade pass + ink
 * outline, item touches the bottom margin (ground anchor). Max 3 dominant
 * color families; `ROBOT.accent` only as a small dot.
 */

import type { SpriteOpList } from '../../art/spriteOps';
import type { FormId } from './forms';

export const FORM_TEMPLATES: Partial<Record<FormId, SpriteOpList>> = {
  // bench — two posts + two plank lines + grain (authoring §5 exemplar).
  bench: {
    canvas: [256, 224],
    ops: [
      { op: 'line', x0: 64, y0: 208, x1: 70, y1: 128, width: 12, color: 'shade' },
      { op: 'line', x0: 192, y0: 208, x1: 186, y1: 128, width: 12, color: 'shade' },
      {
        op: 'path',
        points: [
          [36, 124],
          [220, 120],
          [222, 142],
          [38, 146],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'path',
        points: [
          [36, 96],
          [220, 92],
          [222, 110],
          [38, 114],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'line', x0: 70, y0: 132, x1: 90, y1: 131, width: 2, color: 'shade' },
      { op: 'line', x0: 150, y0: 132, x1: 170, y1: 131, width: 2, color: 'shade' },
    ],
  },

  // lantern — dark stem, paper cone, warm halo, accent dot (authoring §5).
  lantern: {
    canvas: [192, 288],
    ops: [
      { op: 'halo', cx: 96, cy: 120, r: 92 },
      { op: 'line', x0: 96, y0: 272, x1: 96, y1: 150, width: 7, color: 'shade' },
      {
        op: 'path',
        points: [
          [60, 150],
          [132, 150],
          [116, 92],
          [76, 92],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 132,
        rx: 28,
        ry: 9,
        fill: 'CLAY.pale',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 110,
        rx: 6,
        ry: 6,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 2,
      },
    ],
  },

  // birdbath — pedestal + bowl + water + tiny bird (authoring §5).
  birdbath: {
    canvas: [224, 288],
    ops: [
      {
        op: 'path',
        points: [
          [96, 120],
          [88, 200],
          [76, 252],
          [148, 252],
          [136, 200],
          [128, 120],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 112,
        cy: 258,
        rx: 52,
        ry: 13,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'blob',
        cx: 112,
        cy: 112,
        rx: 84,
        ry: 26,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 112,
        cy: 106,
        rx: 62,
        ry: 14,
        fill: 'WATER.mid',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 150,
        cy: 88,
        rx: 14,
        ry: 10,
        fill: 'CLAY.blossom',
        outline: 'INK.line',
        lineWidth: 3,
      },
    ],
  },

  // post — a single upright timber with a capped top.
  post: {
    canvas: [128, 256],
    ops: [
      { op: 'line', x0: 64, y0: 244, x1: 66, y1: 60, width: 22, color: 'fill' },
      { op: 'line', x0: 64, y0: 244, x1: 66, y1: 60, width: 22, color: 'fill', jitter: 0 },
      {
        op: 'blob',
        cx: 65,
        cy: 56,
        rx: 18,
        ry: 10,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 4,
      },
      { op: 'line', x0: 54, y0: 200, x1: 58, y1: 100, width: 2, color: 'shade' },
    ],
  },

  // beam — a horizontal timber lying on supports.
  beam: {
    canvas: [256, 128],
    ops: [
      {
        op: 'path',
        points: [
          [24, 70],
          [232, 64],
          [232, 96],
          [24, 102],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'line', x0: 60, y0: 84, x1: 200, y1: 80, width: 2, color: 'shade' },
    ],
  },

  // cairn — a balanced stack of three stones.
  cairn: {
    canvas: [160, 224],
    ops: [
      {
        op: 'blob',
        cx: 80,
        cy: 190,
        rx: 46,
        ry: 28,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'blob',
        cx: 80,
        cy: 138,
        rx: 34,
        ry: 22,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'blob',
        cx: 80,
        cy: 96,
        rx: 22,
        ry: 16,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 4,
      },
    ],
  },

  // brush — a short handle with a soft, splayed head.
  brush: {
    canvas: [192, 224],
    ops: [
      { op: 'line', x0: 76, y0: 212, x1: 104, y1: 112, width: 14, color: 'shade' },
      {
        op: 'path',
        points: [
          [78, 116],
          [112, 84],
          [142, 108],
          [108, 142],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'line', x0: 112, y0: 92, x1: 132, y1: 70, width: 5, color: 'fill' },
      { op: 'line', x0: 120, y0: 100, x1: 146, y1: 82, width: 5, color: 'fill' },
      { op: 'line', x0: 126, y0: 110, x1: 154, y1: 98, width: 5, color: 'fill' },
      { op: 'line', x0: 86, y0: 180, x1: 94, y1: 150, width: 2, color: 'INK.soft' },
    ],
  },

  // wayfinder — a handmade compass resting on a low foot.
  wayfinder: {
    canvas: [192, 224],
    ops: [
      { op: 'line', x0: 96, y0: 212, x1: 96, y1: 176, width: 10, color: 'shade' },
      {
        op: 'blob',
        cx: 96,
        cy: 204,
        rx: 42,
        ry: 10,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 118,
        rx: 62,
        ry: 62,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 6,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 118,
        rx: 44,
        ry: 44,
        fill: 'PAPER.floor',
        outline: 'INK.soft',
        lineWidth: 3,
      },
      {
        op: 'path',
        points: [
          [96, 72],
          [108, 120],
          [96, 112],
          [84, 120],
        ],
        close: true,
        fill: 'ROBOT.accent',
        outline: 'INK.line',
        lineWidth: 3,
      },
      { op: 'line', x0: 60, y0: 118, x1: 132, y1: 118, width: 3, color: 'INK.soft' },
      {
        op: 'blob',
        cx: 96,
        cy: 118,
        rx: 6,
        ry: 6,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 2,
      },
    ],
  },

  // field glass — twin lenses on a small shared observation stand.
  'field-glass': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 112, y0: 210, x1: 112, y1: 132, width: 9, color: 'shade' },
      { op: 'line', x0: 112, y0: 170, x1: 78, y1: 212, width: 6, color: 'shade' },
      { op: 'line', x0: 112, y0: 170, x1: 146, y1: 212, width: 6, color: 'shade' },
      {
        op: 'path',
        points: [
          [44, 82],
          [102, 92],
          [102, 132],
          [44, 142],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'path',
        points: [
          [122, 92],
          [180, 82],
          [180, 142],
          [122, 132],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'rect',
        x: 98,
        y: 104,
        w: 28,
        h: 18,
        r: 7,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 3,
      },
      {
        op: 'blob',
        cx: 48,
        cy: 112,
        rx: 12,
        ry: 28,
        fill: 'WATER.edge',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'blob',
        cx: 176,
        cy: 112,
        rx: 12,
        ry: 28,
        fill: 'WATER.edge',
        outline: 'INK.line',
        lineWidth: 4,
      },
    ],
  },

  // play ball — a soft wrapped ball with visible hand-tied bands.
  'play-ball': {
    canvas: [192, 224],
    ops: [
      {
        op: 'blob',
        cx: 96,
        cy: 170,
        rx: 48,
        ry: 44,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'path',
        points: [
          [58, 148],
          [82, 132],
          [124, 206],
          [104, 214],
        ],
        close: true,
        fill: 'shade',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'path',
        points: [
          [132, 142],
          [144, 164],
          [64, 196],
          [54, 178],
        ],
        close: true,
        fill: 'shade',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      { op: 'line', x0: 64, y0: 156, x1: 132, y1: 190, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 72, y0: 196, x1: 128, y1: 146, width: 3, color: 'INK.soft' },
      {
        op: 'blob',
        cx: 98,
        cy: 172,
        rx: 7,
        ry: 6,
        fill: 'ROBOT.accent',
        outline: 'INK.line',
        lineWidth: 2,
      },
    ],
  },

  // cache box — a lidded trail chest with one quiet amber latch.
  'cache-box': {
    canvas: [256, 224],
    ops: [
      {
        op: 'rect',
        x: 32,
        y: 92,
        w: 192,
        h: 120,
        r: 14,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 6,
      },
      {
        op: 'path',
        points: [
          [28, 92],
          [48, 62],
          [208, 62],
          [228, 92],
        ],
        close: true,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 6,
      },
      { op: 'line', x0: 42, y0: 116, x1: 214, y1: 116, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 72, y0: 104, x1: 72, y1: 204, width: 4, color: 'shade' },
      { op: 'line', x0: 184, y0: 104, x1: 184, y1: 204, width: 4, color: 'shade' },
      {
        op: 'rect',
        x: 116,
        y: 104,
        w: 24,
        h: 34,
        r: 5,
        fill: 'ROBOT.accent',
        outline: 'INK.line',
        lineWidth: 3,
      },
      {
        op: 'speckle',
        x: 42,
        y: 142,
        w: 168,
        h: 56,
        count: 18,
        color: 'INK.grain',
        alpha: 0.1,
        maxR: 1.2,
      },
    ],
  },

  // drinking bowl — a broad low bowl holding a calm water ellipse.
  'drinking-bowl': {
    canvas: [224, 160],
    ops: [
      {
        op: 'blob',
        cx: 112,
        cy: 122,
        rx: 86,
        ry: 34,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'path',
        points: [
          [34, 114],
          [50, 146],
          [174, 146],
          [190, 114],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 112,
        cy: 108,
        rx: 76,
        ry: 24,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 112,
        cy: 106,
        rx: 62,
        ry: 15,
        fill: 'WATER.mid',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      { op: 'line', x0: 74, y0: 108, x1: 96, y1: 104, width: 2, color: 'WATER.edge' },
      { op: 'line', x0: 120, y0: 110, x1: 148, y1: 106, width: 2, color: 'WATER.edge' },
    ],
  },

  // bug hotel — a tiny roofed cabinet with varied nesting holes.
  'bug-hotel': {
    canvas: [224, 288],
    ops: [
      {
        op: 'rect',
        x: 48,
        y: 92,
        w: 128,
        h: 180,
        r: 8,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 6,
      },
      {
        op: 'path',
        points: [
          [34, 98],
          [112, 42],
          [190, 98],
        ],
        close: true,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 6,
      },
      { op: 'line', x0: 112, y0: 100, x1: 112, y1: 260, width: 5, color: 'INK.soft' },
      { op: 'line', x0: 58, y0: 172, x1: 166, y1: 172, width: 5, color: 'INK.soft' },
      {
        op: 'blob',
        cx: 82,
        cy: 132,
        rx: 13,
        ry: 12,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 3,
      },
      {
        op: 'blob',
        cx: 142,
        cy: 132,
        rx: 10,
        ry: 10,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 3,
      },
      { op: 'line', x0: 68, y0: 210, x1: 96, y1: 238, width: 7, color: 'shade' },
      { op: 'line', x0: 128, y0: 232, x1: 154, y1: 202, width: 7, color: 'shade' },
      {
        op: 'speckle',
        x: 60,
        y: 184,
        w: 104,
        h: 66,
        count: 22,
        color: 'INK.grain',
        alpha: 0.12,
        maxR: 1.4,
      },
    ],
  },

  // raft — six lashed floats and a simple upright guide rope.
  raft: {
    canvas: [320, 192],
    ops: [
      {
        op: 'blob',
        cx: 74,
        cy: 160,
        rx: 54,
        ry: 22,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 120,
        cy: 156,
        rx: 56,
        ry: 22,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 168,
        cy: 154,
        rx: 56,
        ry: 22,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 216,
        cy: 156,
        rx: 56,
        ry: 22,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 260,
        cy: 160,
        rx: 42,
        ry: 20,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'line', x0: 64, y0: 144, x1: 266, y1: 148, width: 7, color: 'shade' },
      { op: 'line', x0: 86, y0: 174, x1: 248, y1: 170, width: 7, color: 'shade' },
      { op: 'line', x0: 88, y0: 144, x1: 92, y1: 70, width: 7, color: 'shade' },
      {
        op: 'path',
        points: [
          [92, 72],
          [142, 96],
          [94, 116],
        ],
        close: true,
        fill: 'CLAY.pale',
        outline: 'INK.line',
        lineWidth: 4,
      },
      { op: 'line', x0: 24, y0: 184, x1: 296, y1: 184, width: 3, color: 'WATER.deep', jitter: 3 },
    ],
  },

  // --- W7 density: bespoke art for the high-traffic core forms --------------

  // bundle — a tied sheaf of stock, cord-wrapped.
  bundle: {
    canvas: [176, 128],
    ops: [
      { op: 'line', x0: 22, y0: 96, x1: 154, y1: 84, width: 7, color: 'fill' },
      { op: 'line', x0: 22, y0: 84, x1: 154, y1: 72, width: 7, color: 'fill' },
      { op: 'line', x0: 22, y0: 72, x1: 154, y1: 60, width: 7, color: 'shade' },
      { op: 'line', x0: 58, y0: 52, x1: 64, y1: 104, width: 5, color: 'SAGE.shade' },
      { op: 'line', x0: 114, y0: 50, x1: 120, y1: 102, width: 5, color: 'SAGE.shade' },
    ],
  },

  // pile — a neat heap of stones.
  pile: {
    canvas: [160, 128],
    ops: [
      {
        op: 'blob',
        cx: 56,
        cy: 96,
        rx: 30,
        ry: 20,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'blob',
        cx: 104,
        cy: 98,
        rx: 32,
        ry: 19,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'blob',
        cx: 80,
        cy: 64,
        rx: 26,
        ry: 17,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 4,
      },
    ],
  },

  // cord — a coil of twisted strands.
  cord: {
    canvas: [160, 128],
    ops: [
      {
        op: 'blob',
        cx: 80,
        cy: 78,
        rx: 54,
        ry: 40,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 80,
        cy: 78,
        rx: 26,
        ry: 18,
        fill: 'shade',
        outline: 'INK.soft',
        lineWidth: 3,
      },
      { op: 'line', x0: 36, y0: 64, x1: 124, y1: 92, width: 3, color: 'shade' },
    ],
  },

  // panel — a flat boarded sheet with seams.
  panel: {
    canvas: [224, 192],
    ops: [
      {
        op: 'rect',
        x: 36,
        y: 40,
        w: 152,
        h: 132,
        r: 8,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'line', x0: 86, y0: 46, x1: 86, y1: 166, width: 3, color: 'shade' },
      { op: 'line', x0: 138, y0: 46, x1: 138, y1: 166, width: 3, color: 'shade' },
    ],
  },

  // block — a quarried cube.
  block: {
    canvas: [192, 192],
    ops: [
      {
        op: 'rect',
        x: 40,
        y: 60,
        w: 112,
        h: 112,
        r: 6,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 6,
      },
      {
        op: 'path',
        points: [
          [40, 60],
          [72, 32],
          [184, 32],
          [152, 60],
        ],
        close: true,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'path',
        points: [
          [152, 60],
          [184, 32],
          [184, 144],
          [152, 172],
        ],
        close: true,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 5,
      },
    ],
  },

  // ring — a woven loop.
  ring: {
    canvas: [160, 160],
    ops: [
      {
        op: 'blob',
        cx: 80,
        cy: 88,
        rx: 56,
        ry: 56,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 6,
      },
      {
        op: 'blob',
        cx: 80,
        cy: 88,
        rx: 30,
        ry: 30,
        fill: 'PAPER.skyBottom',
        outline: 'INK.soft',
        lineWidth: 3,
      },
    ],
  },

  // vessel — an open-mouthed pot.
  vessel: {
    canvas: [192, 224],
    ops: [
      {
        op: 'path',
        points: [
          [56, 96],
          [44, 180],
          [148, 180],
          [136, 96],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 96,
        rx: 48,
        ry: 14,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'line', x0: 60, y0: 132, x1: 132, y1: 132, width: 2, color: 'shade' },
    ],
  },

  // fence — two posts and two rails.
  fence: {
    canvas: [256, 176],
    ops: [
      { op: 'line', x0: 56, y0: 164, x1: 58, y1: 36, width: 12, color: 'shade' },
      { op: 'line', x0: 200, y0: 164, x1: 202, y1: 36, width: 12, color: 'shade' },
      { op: 'line', x0: 36, y0: 70, x1: 220, y1: 66, width: 11, color: 'fill' },
      { op: 'line', x0: 36, y0: 116, x1: 220, y1: 112, width: 11, color: 'fill' },
    ],
  },

  // gate — a low fence with a hinged center leaf.
  gate: {
    canvas: [256, 192],
    ops: [
      { op: 'line', x0: 44, y0: 180, x1: 46, y1: 44, width: 13, color: 'shade' },
      { op: 'line', x0: 212, y0: 180, x1: 214, y1: 44, width: 13, color: 'shade' },
      {
        op: 'rect',
        x: 80,
        y: 70,
        w: 96,
        h: 104,
        r: 6,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'line', x0: 86, y0: 160, x1: 170, y1: 84, width: 4, color: 'shade' },
    ],
  },

  // table — a top on four legs.
  table: {
    canvas: [256, 224],
    ops: [
      { op: 'line', x0: 64, y0: 210, x1: 66, y1: 110, width: 11, color: 'shade' },
      { op: 'line', x0: 192, y0: 210, x1: 190, y1: 110, width: 11, color: 'shade' },
      {
        op: 'path',
        points: [
          [36, 96],
          [220, 92],
          [224, 120],
          [32, 124],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
    ],
  },

  // stool — a small round seat with carved seasonal leaves.
  stool: {
    canvas: [176, 192],
    ops: [
      { op: 'line', x0: 60, y0: 176, x1: 56, y1: 110, width: 9, color: 'shade' },
      { op: 'line', x0: 116, y0: 176, x1: 120, y1: 110, width: 9, color: 'shade' },
      {
        op: 'blob',
        cx: 88,
        cy: 102,
        rx: 52,
        ry: 18,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'grass', x: 76, baseY: 104, height: 18, lean: -8, width: 3, color: 'SAGE.deep' },
      { op: 'grass', x: 98, baseY: 104, height: 16, lean: 8, width: 3, color: 'SAGE.mid' },
      {
        op: 'blob',
        cx: 88,
        cy: 90,
        rx: 6,
        ry: 5,
        fill: 'CLAY.blossom',
        outline: 'INK.soft',
        lineWidth: 2,
      },
    ],
  },

  // lamp — a stem with a paper shade and a baked warm halo.
  lamp: {
    canvas: [192, 288],
    ops: [
      { op: 'halo', cx: 96, cy: 110, r: 84 },
      { op: 'line', x0: 96, y0: 272, x1: 96, y1: 140, width: 8, color: 'shade' },
      {
        op: 'blob',
        cx: 96,
        cy: 272,
        rx: 44,
        ry: 12,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'path',
        points: [
          [60, 138],
          [132, 138],
          [116, 84],
          [76, 84],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 104,
        rx: 6,
        ry: 6,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 2,
      },
    ],
  },

  // planter — a low trough of earth with a sprig.
  planter: {
    canvas: [224, 176],
    ops: [
      {
        op: 'path',
        points: [
          [40, 96],
          [52, 160],
          [172, 160],
          [184, 96],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 112,
        cy: 98,
        rx: 70,
        ry: 14,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 4,
      },
      { op: 'grass', x: 96, baseY: 96, height: 46, lean: -8, width: 5, color: 'SAGE.mid' },
      { op: 'grass', x: 122, baseY: 96, height: 40, lean: 10, width: 5, color: 'SAGE.deep' },
    ],
  },

  // trellis — a tall diagonal lattice.
  trellis: {
    canvas: [192, 288],
    ops: [
      { op: 'line', x0: 36, y0: 272, x1: 156, y1: 40, width: 4, color: 'fill' },
      { op: 'line', x0: 96, y0: 272, x1: 156, y1: 156, width: 4, color: 'fill' },
      { op: 'line', x0: 156, y0: 272, x1: 36, y1: 40, width: 4, color: 'fill' },
      { op: 'line', x0: 96, y0: 272, x1: 36, y1: 156, width: 4, color: 'fill' },
      {
        op: 'blob',
        cx: 96,
        cy: 120,
        rx: 12,
        ry: 10,
        fill: 'SAGE.light',
        outline: 'INK.soft',
        lineWidth: 2,
      },
    ],
  },

  // chime — a handmade crossbar with leaf plates and warm beads.
  chime: {
    canvas: [192, 288],
    ops: [
      { op: 'line', x0: 96, y0: 272, x1: 96, y1: 72, width: 8, color: 'shade' },
      { op: 'line', x0: 60, y0: 76, x1: 140, y1: 76, width: 5, color: 'shade' },
      { op: 'line', x0: 72, y0: 80, x1: 72, y1: 150, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 96, y0: 80, x1: 96, y1: 168, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 80, x1: 120, y1: 144, width: 3, color: 'INK.soft' },
      {
        op: 'blob',
        cx: 96,
        cy: 178,
        rx: 9,
        ry: 12,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 3,
      },
      {
        op: 'blob',
        cx: 120,
        cy: 152,
        rx: 8,
        ry: 6,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'path',
        points: [
          [62, 148],
          [72, 138],
          [82, 150],
          [72, 166],
        ],
        close: true,
        fill: 'SAGE.mid',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'path',
        points: [
          [84, 174],
          [96, 164],
          [108, 176],
          [96, 190],
        ],
        close: true,
        fill: 'SAGE.light',
        outline: 'INK.soft',
        lineWidth: 2,
      },
    ],
  },

  // mat — a flat woven rectangle (ground decal read).
  mat: {
    canvas: [256, 160],
    ops: [
      {
        op: 'blob',
        cx: 128,
        cy: 96,
        rx: 104,
        ry: 52,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
        irregularity: 0.05,
      },
      {
        op: 'blob',
        cx: 128,
        cy: 96,
        rx: 80,
        ry: 38,
        fill: 'shade',
        outline: 'INK.soft',
        lineWidth: 2,
        irregularity: 0.05,
      },
    ],
  },

  // sign — a board on a post with carved ticks.
  sign: {
    canvas: [224, 288],
    ops: [
      { op: 'line', x0: 112, y0: 276, x1: 112, y1: 120, width: 11, color: 'shade' },
      {
        op: 'rect',
        x: 44,
        y: 70,
        w: 136,
        h: 64,
        r: 8,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'line', x0: 64, y0: 96, x1: 160, y1: 94, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 64, y0: 114, x1: 132, y1: 112, width: 4, color: 'INK.soft' },
    ],
  },

  // basket — a woven open carrier.
  basket: {
    canvas: [192, 192],
    ops: [
      {
        op: 'path',
        points: [
          [44, 92],
          [56, 170],
          [136, 170],
          [148, 92],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 92,
        rx: 56,
        ry: 14,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 4,
      },
      { op: 'line', x0: 48, y0: 88, x1: 144, y1: 88, width: 4, color: 'shade' },
      { op: 'line', x0: 56, y0: 60, x1: 136, y1: 60, width: 5, color: 'fill', jitter: 5 },
    ],
  },

  // sprout pot — a round clay vessel with one hopeful living shoot.
  'sprout-pot': {
    canvas: [176, 192],
    ops: [
      {
        op: 'path',
        points: [
          [44, 92],
          [58, 176],
          [118, 176],
          [132, 92],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 88,
        cy: 92,
        rx: 48,
        ry: 14,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 4,
      },
      { op: 'line', x0: 88, y0: 88, x1: 88, y1: 42, width: 5, color: 'SAGE.deep' },
      {
        op: 'path',
        points: [
          [86, 62],
          [58, 46],
          [64, 74],
        ],
        close: true,
        fill: 'SAGE.light',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'path',
        points: [
          [90, 52],
          [118, 36],
          [112, 66],
        ],
        close: true,
        fill: 'SAGE.mid',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 88,
        cy: 36,
        rx: 6,
        ry: 6,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 2,
      },
    ],
  },

  // mailbox — a rounded wooden box, little door, leaf carving, and post.
  mailbox: {
    canvas: [208, 256],
    ops: [
      { op: 'line', x0: 104, y0: 244, x1: 104, y1: 142, width: 13, color: 'shade' },
      {
        op: 'blob',
        cx: 104,
        cy: 244,
        rx: 38,
        ry: 9,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 3,
      },
      {
        op: 'rect',
        x: 40,
        y: 76,
        w: 128,
        h: 82,
        r: 22,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'path',
        points: [
          [40, 98],
          [66, 58],
          [142, 58],
          [168, 98],
        ],
        close: true,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'rect',
        x: 64,
        y: 100,
        w: 78,
        h: 42,
        r: 8,
        fill: 'CLAY.pale',
        outline: 'INK.soft',
        lineWidth: 3,
      },
      {
        op: 'blob',
        cx: 130,
        cy: 121,
        rx: 5,
        ry: 5,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'path',
        points: [
          [78, 116],
          [92, 104],
          [100, 120],
          [88, 130],
        ],
        close: true,
        fill: 'SAGE.mid',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'speckle',
        x: 46,
        y: 66,
        w: 116,
        h: 84,
        count: 12,
        color: 'SAGE.deep',
        alpha: 0.16,
        maxR: 2,
      },
    ],
  },

  // mushroom lamp — broad cap, pale stem, and a quiet amber light beneath.
  'mushroom-lamp': {
    canvas: [208, 240],
    ops: [
      { op: 'halo', cx: 104, cy: 102, r: 76 },
      {
        op: 'path',
        points: [
          [78, 116],
          [72, 222],
          [136, 222],
          [130, 116],
        ],
        close: true,
        fill: 'CLAY.pale',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 104,
        cy: 222,
        rx: 48,
        ry: 11,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'path',
        points: [
          [30, 112],
          [50, 58],
          [104, 34],
          [158, 58],
          [178, 112],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 6,
      },
      {
        op: 'blob',
        cx: 104,
        cy: 110,
        rx: 70,
        ry: 15,
        fill: 'CLAY.pale',
        outline: 'INK.soft',
        lineWidth: 3,
      },
      {
        op: 'blob',
        cx: 104,
        cy: 102,
        rx: 7,
        ry: 7,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 76,
        cy: 70,
        rx: 7,
        ry: 5,
        fill: 'CLAY.pale',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 132,
        cy: 62,
        rx: 6,
        ry: 5,
        fill: 'CLAY.pale',
        outline: 'INK.soft',
        lineWidth: 2,
      },
    ],
  },

  // pet bed — a low woven nest with a folded soft blanket.
  'pet-bed': {
    canvas: [240, 160],
    ops: [
      {
        op: 'blob',
        cx: 120,
        cy: 112,
        rx: 102,
        ry: 42,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
        irregularity: 0.05,
      },
      {
        op: 'blob',
        cx: 120,
        cy: 106,
        rx: 76,
        ry: 28,
        fill: 'CLAY.pale',
        outline: 'INK.soft',
        lineWidth: 3,
        irregularity: 0.05,
      },
      {
        op: 'path',
        points: [
          [70, 98],
          [126, 86],
          [170, 116],
          [104, 132],
        ],
        close: true,
        fill: 'SAGE.light',
        outline: 'INK.soft',
        lineWidth: 3,
      },
      { op: 'line', x0: 88, y0: 104, x1: 148, y1: 116, width: 3, color: 'SAGE.deep' },
      { op: 'line', x0: 38, y0: 118, x1: 68, y1: 128, width: 3, color: 'shade' },
      { op: 'line', x0: 172, y0: 126, x1: 202, y1: 116, width: 3, color: 'shade' },
    ],
  },

  // food bowl — a shallow ceramic dish with a paw-like stamped mark.
  'food-bowl': {
    canvas: [192, 128],
    ops: [
      {
        op: 'path',
        points: [
          [34, 54],
          [50, 116],
          [142, 116],
          [158, 54],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 54,
        rx: 64,
        ry: 17,
        fill: 'CLAY.pale',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 58,
        rx: 42,
        ry: 10,
        fill: 'shade',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 88,
        rx: 8,
        ry: 7,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 84,
        cy: 78,
        rx: 4,
        ry: 4,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 1,
      },
      {
        op: 'blob',
        cx: 108,
        cy: 78,
        rx: 4,
        ry: 4,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 1,
      },
    ],
  },

  // garden lantern — a squat stone tower with a warm open window.
  'garden-lantern': {
    canvas: [192, 256],
    ops: [
      { op: 'halo', cx: 96, cy: 112, r: 68 },
      {
        op: 'blob',
        cx: 96,
        cy: 240,
        rx: 54,
        ry: 12,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'path',
        points: [
          [72, 226],
          [80, 142],
          [112, 142],
          [120, 226],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'rect',
        x: 54,
        y: 80,
        w: 84,
        h: 72,
        r: 10,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'rect',
        x: 72,
        y: 96,
        w: 48,
        h: 38,
        r: 8,
        fill: 'CLAY.pale',
        outline: 'INK.soft',
        lineWidth: 3,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 114,
        rx: 7,
        ry: 7,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'path',
        points: [
          [36, 82],
          [96, 46],
          [156, 82],
          [142, 94],
          [50, 94],
        ],
        close: true,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 5,
      },
    ],
  },

  // seed chest — an open compartment box with quiet glowing seed points.
  'seed-chest': {
    canvas: [224, 184],
    ops: [
      {
        op: 'rect',
        x: 34,
        y: 86,
        w: 156,
        h: 84,
        r: 10,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'path',
        points: [
          [40, 86],
          [58, 40],
          [178, 40],
          [188, 86],
        ],
        close: true,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'rect',
        x: 52,
        y: 100,
        w: 52,
        h: 42,
        r: 6,
        fill: 'CLAY.pale',
        outline: 'INK.soft',
        lineWidth: 3,
      },
      {
        op: 'rect',
        x: 120,
        y: 100,
        w: 52,
        h: 42,
        r: 6,
        fill: 'CLAY.pale',
        outline: 'INK.soft',
        lineWidth: 3,
      },
      {
        op: 'blob',
        cx: 76,
        cy: 120,
        rx: 6,
        ry: 6,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 144,
        cy: 118,
        rx: 5,
        ry: 5,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      { op: 'grass', x: 154, baseY: 132, height: 24, lean: 8, width: 3, color: 'SAGE.mid' },
    ],
  },

  // repair toy — a worn little robot with one visibly mended foot.
  'repair-toy': {
    canvas: [176, 208],
    ops: [
      {
        op: 'rect',
        x: 50,
        y: 74,
        w: 76,
        h: 74,
        r: 18,
        fill: 'ROBOT.shell',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'rect',
        x: 62,
        y: 88,
        w: 52,
        h: 26,
        r: 10,
        fill: 'ROBOT.visor',
        outline: 'INK.soft',
        lineWidth: 3,
      },
      {
        op: 'blob',
        cx: 78,
        cy: 100,
        rx: 4,
        ry: 4,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 1,
      },
      {
        op: 'blob',
        cx: 98,
        cy: 100,
        rx: 4,
        ry: 4,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 1,
      },
      { op: 'line', x0: 58, y0: 140, x1: 48, y1: 194, width: 10, color: 'ROBOT.dark' },
      { op: 'line', x0: 116, y0: 140, x1: 128, y1: 194, width: 10, color: 'ROBOT.dark' },
      {
        op: 'blob',
        cx: 44,
        cy: 196,
        rx: 24,
        ry: 8,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 3,
      },
      {
        op: 'blob',
        cx: 132,
        cy: 196,
        rx: 20,
        ry: 8,
        fill: 'ROBOT.shellShade',
        outline: 'INK.line',
        lineWidth: 3,
      },
      { op: 'line', x0: 42, y0: 184, x1: 54, y1: 198, width: 3, color: 'CLAY.blossom' },
      { op: 'line', x0: 54, y0: 184, x1: 42, y1: 198, width: 3, color: 'CLAY.blossom' },
    ],
  },

  // shelter — a gabled kennel in robot cream.
  shelter: {
    canvas: [288, 256],
    ops: [
      {
        op: 'rect',
        x: 56,
        y: 120,
        w: 176,
        h: 116,
        r: 12,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 6,
      },
      {
        op: 'path',
        points: [
          [36, 126],
          [144, 48],
          [252, 126],
        ],
        close: true,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 6,
      },
      {
        op: 'path',
        points: [
          [112, 236],
          [112, 168],
          [144, 140],
          [176, 168],
          [176, 236],
        ],
        close: true,
        fill: 'ROBOT.visor',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'blob',
        cx: 144,
        cy: 128,
        rx: 7,
        ry: 7,
        fill: 'ROBOT.accent',
        outline: 'INK.line',
        lineWidth: 2,
      },
    ],
  },

  // archway — two posts and a gentle arch with blossoms.
  archway: {
    canvas: [288, 320],
    ops: [
      { op: 'line', x0: 64, y0: 304, x1: 64, y1: 120, width: 13, color: 'fill' },
      { op: 'line', x0: 224, y0: 304, x1: 224, y1: 120, width: 13, color: 'fill' },
      {
        op: 'path',
        points: [
          [52, 124],
          [144, 36],
          [236, 124],
        ],
        outline: 'fill',
        lineWidth: 14,
      },
      {
        op: 'blob',
        cx: 100,
        cy: 80,
        rx: 11,
        ry: 9,
        fill: 'CLAY.blossom',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 188,
        cy: 80,
        rx: 11,
        ry: 9,
        fill: 'CLAY.blossom',
        outline: 'INK.soft',
        lineWidth: 2,
      },
    ],
  },

  // pergola — posts under a flat slatted roof.
  pergola: {
    canvas: [288, 256],
    ops: [
      { op: 'line', x0: 56, y0: 240, x1: 56, y1: 96, width: 12, color: 'fill' },
      { op: 'line', x0: 232, y0: 240, x1: 232, y1: 96, width: 12, color: 'fill' },
      {
        op: 'rect',
        x: 32,
        y: 70,
        w: 224,
        h: 18,
        r: 4,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 4,
      },
      { op: 'line', x0: 70, y0: 64, x1: 70, y1: 92, width: 5, color: 'fill' },
      { op: 'line', x0: 120, y0: 64, x1: 120, y1: 92, width: 5, color: 'fill' },
      { op: 'line', x0: 170, y0: 64, x1: 170, y1: 92, width: 5, color: 'fill' },
      { op: 'line', x0: 220, y0: 64, x1: 220, y1: 92, width: 5, color: 'fill' },
    ],
  },

  // well — a low stone ring with a little roof.
  well: {
    canvas: [224, 288],
    ops: [
      {
        op: 'path',
        points: [
          [56, 160],
          [64, 248],
          [160, 248],
          [168, 160],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 6,
      },
      {
        op: 'blob',
        cx: 112,
        cy: 160,
        rx: 60,
        ry: 18,
        fill: 'ROBOT.visor',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'line', x0: 64, y0: 156, x1: 60, y1: 64, width: 7, color: 'shade' },
      { op: 'line', x0: 160, y0: 156, x1: 164, y1: 64, width: 7, color: 'shade' },
      {
        op: 'path',
        points: [
          [44, 70],
          [112, 28],
          [180, 70],
        ],
        close: true,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 5,
      },
    ],
  },

  // shrine — a small stone niche with an upright marker.
  shrine: {
    canvas: [224, 288],
    ops: [
      {
        op: 'path',
        points: [
          [52, 130],
          [52, 256],
          [172, 256],
          [172, 130],
          [112, 88],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 6,
      },
      {
        op: 'rect',
        x: 92,
        y: 150,
        w: 40,
        h: 96,
        r: 8,
        fill: 'ROBOT.visor',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'blob',
        cx: 112,
        cy: 176,
        rx: 8,
        ry: 8,
        fill: 'ROBOT.accent',
        outline: 'INK.soft',
        lineWidth: 2,
      },
    ],
  },

  // cold-frame — a low glazed box over earth.
  'cold-frame': {
    canvas: [256, 192],
    ops: [
      {
        op: 'rect',
        x: 40,
        y: 120,
        w: 176,
        h: 56,
        r: 6,
        fill: 'shade',
        outline: 'INK.line',
        lineWidth: 5,
      },
      {
        op: 'path',
        points: [
          [40, 120],
          [76, 76],
          [216, 76],
          [216, 120],
        ],
        close: true,
        fill: 'WATER.edge',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'line', x0: 110, y0: 80, x1: 92, y1: 118, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 160, y0: 80, x1: 150, y1: 118, width: 3, color: 'INK.soft' },
    ],
  },

  // campfire — stone ring, crossed logs, a quiet flame + warm halo.
  campfire: {
    canvas: [224, 192],
    ops: [
      { op: 'halo', cx: 112, cy: 120, r: 88 },
      { op: 'line', x0: 56, y0: 168, x1: 168, y1: 144, width: 12, color: 'shade' },
      { op: 'line', x0: 60, y0: 144, x1: 164, y1: 170, width: 12, color: 'fill' },
      {
        op: 'path',
        points: [
          [112, 64],
          [138, 116],
          [112, 148],
          [86, 116],
        ],
        close: true,
        fill: 'ROBOT.accent',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'blob',
        cx: 56,
        cy: 174,
        rx: 14,
        ry: 10,
        fill: 'CLAY.pale',
        outline: 'INK.line',
        lineWidth: 3,
      },
      {
        op: 'blob',
        cx: 168,
        cy: 174,
        rx: 14,
        ry: 10,
        fill: 'CLAY.pale',
        outline: 'INK.line',
        lineWidth: 3,
      },
    ],
  },

  // garland — a sweeping arc of blossoms (worn read).
  garland: {
    canvas: [224, 128],
    ops: [
      {
        op: 'line',
        x0: 24,
        y0: 48,
        x1: 200,
        y1: 48,
        width: 6,
        color: 'SAGE.shade',
        jitter: 10,
        segments: 8,
      },
      {
        op: 'blob',
        cx: 56,
        cy: 78,
        rx: 12,
        ry: 10,
        fill: 'fill',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 96,
        cy: 92,
        rx: 12,
        ry: 10,
        fill: 'fill',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 136,
        cy: 92,
        rx: 12,
        ry: 10,
        fill: 'fill',
        outline: 'INK.soft',
        lineWidth: 2,
      },
      {
        op: 'blob',
        cx: 176,
        cy: 78,
        rx: 12,
        ry: 10,
        fill: 'fill',
        outline: 'INK.soft',
        lineWidth: 2,
      },
    ],
  },

  // stick — the fetch toy: a sturdy forked branch with a grip wrap.
  stick: {
    canvas: [192, 128],
    ops: [
      { op: 'line', x0: 24, y0: 84, x1: 168, y1: 56, width: 10, color: 'fill' },
      { op: 'line', x0: 120, y0: 64, x1: 156, y1: 30, width: 7, color: 'fill' },
      { op: 'line', x0: 44, y0: 74, x1: 52, y1: 92, width: 4, color: 'shade' },
      { op: 'line', x0: 52, y0: 72, x1: 60, y1: 90, width: 4, color: 'shade' },
    ],
  },

  // axe — a wedge head clamped on a handle (tool, rides in the gripper).
  axe: {
    canvas: [160, 224],
    ops: [
      { op: 'line', x0: 84, y0: 212, x1: 76, y1: 60, width: 11, color: 'shade' },
      {
        op: 'path',
        points: [
          [60, 56],
          [120, 40],
          [128, 92],
          [70, 96],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
      { op: 'line', x0: 80, y0: 70, x1: 76, y1: 180, width: 3, color: 'INK.soft' },
    ],
  },

  // pickaxe — a double-pointed head crossing a handle.
  pickaxe: {
    canvas: [192, 224],
    ops: [
      { op: 'line', x0: 96, y0: 212, x1: 96, y1: 70, width: 11, color: 'shade' },
      {
        op: 'path',
        points: [
          [28, 92],
          [96, 60],
          [164, 92],
          [96, 80],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
    ],
  },

  // shears — two hinged blades.
  shears: {
    canvas: [160, 224],
    ops: [
      { op: 'line', x0: 72, y0: 212, x1: 64, y1: 120, width: 6, color: 'shade' },
      { op: 'line', x0: 88, y0: 212, x1: 96, y1: 120, width: 6, color: 'shade' },
      {
        op: 'path',
        points: [
          [64, 120],
          [40, 48],
          [60, 44],
          [80, 112],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'path',
        points: [
          [96, 120],
          [120, 48],
          [100, 44],
          [80, 112],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 4,
      },
      {
        op: 'blob',
        cx: 80,
        cy: 116,
        rx: 6,
        ry: 6,
        fill: 'ROBOT.accent',
        outline: 'INK.line',
        lineWidth: 2,
      },
    ],
  },

  // scoop — a cupped head on a handle.
  scoop: {
    canvas: [160, 224],
    ops: [
      { op: 'line', x0: 80, y0: 212, x1: 80, y1: 96, width: 10, color: 'shade' },
      {
        op: 'path',
        points: [
          [44, 92],
          [116, 92],
          [104, 48],
          [56, 48],
        ],
        close: true,
        fill: 'fill',
        outline: 'INK.line',
        lineWidth: 5,
      },
    ],
  },
  'acorn-rhythm-tray': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[40, 110], [216, 110], [238, 138], [18, 138]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 82], [216, 82], [222, 112], [34, 112]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'line', x0: 52, y0: 94, x1: 204, y1: 94, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 50, y0: 102, x1: 206, y1: 102, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 92, cy: 98, rx: 9, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 120, cy: 98, rx: 9, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 148, cy: 98, rx: 9, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'airing-storage-bench': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[32, 72], [224, 72], [228, 88], [28, 88]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 56, y0: 56, x1: 56, y1: 72, width: 3, color: 'shade' },
      { op: 'line', x0: 104, y0: 56, x1: 104, y1: 72, width: 3, color: 'shade' },
      { op: 'line', x0: 152, y0: 56, x1: 152, y1: 72, width: 3, color: 'shade' },
      { op: 'line', x0: 200, y0: 56, x1: 200, y1: 72, width: 3, color: 'shade' },
      { op: 'rect', x: 40, y: 88, w: 176, h: 68, r: 5, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 56, y0: 104, x1: 56, y1: 140, width: 3, color: 'shade' },
      { op: 'line', x0: 80, y0: 104, x1: 80, y1: 140, width: 3, color: 'shade' },
      { op: 'line', x0: 176, y0: 104, x1: 176, y1: 140, width: 3, color: 'shade' },
      { op: 'line', x0: 120, y0: 120, x1: 136, y1: 120, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 40, y0: 156, x1: 216, y1: 156, width: 4, color: 'shade' },
    ],
  },
  'amber-step-light': {
    canvas: [208, 256],
    ops: [
      { op: 'halo', cx: 104, cy: 176, r: 48 },
      { op: 'rect', x: 40, y: 196, w: 128, h: 44, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[52, 196], [156, 196], [148, 150], [60, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 150], [148, 150], [128, 108], [80, 108]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[74, 184], [134, 184], [128, 156], [80, 156]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 104, cy: 172, rx: 12, ry: 7, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 64, y0: 218, x1: 144, y1: 218, width: 2, color: 'shade' },
      { op: 'blob', cx: 104, cy: 240, rx: 68, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'animal-path-camera-rest': {
    canvas: [224, 224],
    ops: [
      { op: 'blob', cx: 80, cy: 206, rx: 56, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 204, x1: 80, y1: 120, width: 12, color: 'shade' },
      { op: 'line', x0: 80, y0: 140, x1: 48, y1: 196, width: 7, color: 'shade' },
      { op: 'line', x0: 80, y0: 140, x1: 112, y1: 196, width: 7, color: 'shade' },
      { op: 'rect', x: 96, y: 96, w: 92, h: 56, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 150, cy: 124, rx: 20, ry: 20, fill: 'WATER.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 150, cy: 124, rx: 9, ry: 9, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 104, y: 100, w: 18, h: 12, r: 3, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 178, cy: 108, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'anniversary-shadow-box': {
    canvas: [200, 208],
    ops: [
      { op: 'rect', x: 34, y: 36, w: 132, h: 162, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 50, y: 52, w: 100, h: 130, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[50, 182], [50, 150], [150, 150], [150, 182]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[62, 150], [80, 110], [100, 150]], close: true, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[98, 150], [122, 98], [146, 150]], close: true, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 78, cy: 78, rx: 12, ry: 12, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 50, y0: 182, x1: 150, y1: 182, width: 2, color: 'INK.soft' },
    ],
  },
  'archive-card-cabinet': {
    canvas: [200, 208],
    ops: [
      { op: 'rect', x: 36, y: 36, w: 128, h: 156, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 48, y: 50, w: 104, h: 36, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 48, y: 96, w: 104, h: 36, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 48, y: 142, w: 104, h: 36, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 68, rx: 8, ry: 7, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[90, 114], [110, 114], [106, 124], [94, 124]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 160, rx: 7, ry: 7, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 44, y0: 192, x1: 156, y1: 192, width: 4, color: 'shade' },
    ],
  },
  'arrival-bell-stone': {
    canvas: [200, 200],
    ops: [
      { op: 'blob', cx: 100, cy: 168, rx: 74, ry: 26, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[28, 168], [34, 188], [166, 188], [172, 168]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[72, 148], [78, 80], [122, 80], [128, 148]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 100, cy: 148, rx: 30, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 100, y0: 80, x1: 100, y1: 62, width: 5, color: 'shade' },
      { op: 'blob', cx: 100, cy: 58, rx: 9, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[150, 180], [186, 176], [188, 190], [152, 194]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 112, rx: 6, ry: 6, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'autumn-leaf-catcher': {
    canvas: [208, 240],
    ops: [
      { op: 'blob', cx: 104, cy: 224, rx: 40, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 220, x1: 110, y1: 150, width: 7, color: 'shade' },
      { op: 'path', points: [[40, 72], [176, 96], [160, 176], [64, 160]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 72], [176, 96], [170, 116], [44, 92]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 60, y0: 92, x1: 78, y1: 158, width: 1.5, color: 'shade' },
      { op: 'line', x0: 96, y0: 96, x1: 110, y1: 166, width: 1.5, color: 'shade' },
      { op: 'line', x0: 132, y0: 100, x1: 144, y1: 172, width: 1.5, color: 'shade' },
      { op: 'blob', cx: 92, cy: 130, rx: 13, ry: 9, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 130, cy: 142, rx: 11, ry: 8, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'autumn-seed-mosaic': {
    canvas: [224, 288],
    ops: [
      { op: 'line', x0: 78, y0: 196, x1: 62, y1: 272, width: 8, color: 'shade' },
      { op: 'line', x0: 146, y0: 196, x1: 162, y1: 272, width: 8, color: 'shade' },
      { op: 'rect', x: 40, y: 40, w: 144, h: 160, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 58, y: 58, w: 108, h: 124, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 58, y0: 99, x1: 166, y1: 99, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 58, y0: 140, x1: 166, y1: 140, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 94, y0: 58, x1: 94, y1: 182, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 130, y0: 58, x1: 130, y1: 182, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 76, cy: 78, rx: 9, ry: 8, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 2 },
      { op: 'blob', cx: 148, cy: 120, rx: 9, ry: 8, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 160, rx: 9, ry: 8, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'backrest-roll': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[40, 128], [60, 160], [196, 160], [216, 128], [208, 116], [48, 116]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 48, y: 56, w: 160, h: 68, r: 34, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 56, cy: 90, rx: 13, ry: 34, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 200, cy: 90, rx: 13, ry: 34, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 108, y0: 62, x1: 104, y1: 118, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 152, y0: 62, x1: 156, y1: 118, width: 2, color: 'INK.soft' },
    ],
  },
  'balance-wobble-bridge': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[40, 84], [120, 72], [136, 72], [216, 84], [216, 100], [136, 90], [120, 90], [40, 100]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[110, 98], [120, 128], [136, 128], [146, 98]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 118, y0: 104, x1: 124, y1: 126, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 138, y0: 104, x1: 132, y1: 126, width: 2, color: 'INK.soft' },
      { op: 'path', points: [[28, 70], [40, 70], [44, 128], [24, 128]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[216, 70], [228, 70], [232, 128], [212, 128]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 90, x1: 120, y1: 80, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 136, y0: 80, x1: 216, y1: 90, width: 2, color: 'INK.soft' },
    ],
  },
  'basket-label-charms': {
    canvas: [192, 256],
    ops: [
      { op: 'path', points: [[44, 196], [148, 196], [160, 236], [32, 236]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 62, x1: 152, y1: 62, width: 7, color: 'shade' },
      { op: 'line', x0: 68, y0: 66, x1: 68, y1: 92, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 68, cy: 106, rx: 18, ry: 16, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 112, y0: 66, x1: 112, y1: 92, width: 3, color: 'INK.soft' },
      { op: 'path', points: [[98, 104], [126, 104], [120, 134], [104, 134]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 68, y0: 58, x1: 68, y1: 50, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 96, cy: 196, rx: 58, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'bat-water-trough': {
    canvas: [320, 144],
    ops: [
      { op: 'line', x0: 56, y0: 134, x1: 58, y1: 104, width: 10, color: 'shade' },
      { op: 'line', x0: 262, y0: 134, x1: 260, y1: 104, width: 10, color: 'shade' },
      { op: 'path', points: [[24, 70], [296, 66], [288, 110], [32, 114]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[36, 76], [284, 72], [280, 98], [40, 102]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[48, 80], [272, 76], [270, 92], [50, 96]], close: true, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 80, y0: 84, x1: 140, y1: 82, width: 2, color: 'WATER.edge' },
      { op: 'line', x0: 180, y0: 83, x1: 240, y1: 81, width: 2, color: 'WATER.edge' },
    ],
  },
  'battery-warmth-sleeve': {
    canvas: [200, 224],
    ops: [
      { op: 'path', points: [[52, 210], [148, 210], [156, 80], [44, 80]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 80], [156, 80], [148, 58], [52, 58]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 80, x1: 100, y1: 210, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 60, y0: 120, x1: 140, y1: 120, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 62, y0: 160, x1: 138, y1: 160, width: 2, color: 'INK.soft' },
      { op: 'rect', x: 78, y: 166, w: 44, h: 30, r: 7, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 181, rx: 7, ry: 7, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'bedside-glow-pebble': {
    canvas: [176, 256],
    ops: [
      { op: 'path', points: [[44, 248], [28, 180], [40, 128], [88, 108], [136, 128], [148, 180], [132, 248]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[56, 134], [88, 118], [120, 134], [112, 160], [64, 160]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 88, cy: 128, rx: 24, ry: 22, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 80, cy: 120, rx: 7, ry: 6, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 126, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 246, rx: 54, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 48, y0: 196, x1: 128, y1: 196, width: 2, color: 'shade' },
    ],
  },
  'bedside-slipper-cradle': {
    canvas: [256, 176],
    ops: [
      { op: 'line', x0: 70, y0: 162, x1: 70, y1: 120, width: 8, color: 'shade' },
      { op: 'line', x0: 186, y0: 162, x1: 186, y1: 120, width: 8, color: 'shade' },
      { op: 'blob', cx: 76, cy: 92, rx: 48, ry: 34, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 76, cy: 88, rx: 32, ry: 18, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 180, cy: 92, rx: 48, ry: 34, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 180, cy: 88, rx: 32, ry: 18, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 64, y0: 86, x1: 88, y1: 86, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 168, y0: 86, x1: 192, y1: 86, width: 2, color: 'INK.soft' },
    ],
  },
  'bee-hotel-cleaning-tray': {
    canvas: [288, 200],
    ops: [
      { op: 'rect', x: 52, y: 52, w: 184, h: 84, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 92, cy: 94, rx: 14, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 130, cy: 94, rx: 14, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 168, cy: 94, rx: 14, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 206, cy: 94, rx: 11, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'rect', x: 40, y: 138, w: 208, h: 48, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 132, y: 150, w: 24, h: 24, r: 4, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 56, y0: 162, x1: 118, y1: 162, width: 2, color: 'INK.soft' },
    ],
  },
  'bee-water-pebble-tray': {
    canvas: [224, 256],
    ops: [
      { op: 'path', points: [[88, 150], [80, 242], [144, 242], [136, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 150, rx: 92, ry: 30, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 146, rx: 74, ry: 18, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 84, cy: 140, rx: 16, ry: 11, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 126, cy: 150, rx: 18, ry: 12, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 150, cy: 138, rx: 12, ry: 9, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 62, y0: 146, x1: 78, y1: 143, width: 2, color: 'WATER.edge' },
    ],
  },
  'beetle-log-window': {
    canvas: [256, 200],
    ops: [
      { op: 'blob', cx: 128, cy: 140, rx: 110, ry: 48, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 48, cy: 140, rx: 22, ry: 40, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 48, cy: 140, rx: 11, ry: 22, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 110, y: 68, w: 76, h: 36, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 118, y0: 104, x1: 118, y1: 138, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 148, y0: 74, x1: 148, y1: 98, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 90, y0: 118, x1: 210, y1: 116, width: 2, color: 'shade' },
      { op: 'speckle', x: 80, y: 120, w: 140, h: 48, count: 18, maxR: 1.4, alpha: 0.12, color: 'INK.grain' },
    ],
  },
  'bell-muffling-sleeve': {
    canvas: [200, 288],
    ops: [
      { op: 'blob', cx: 100, cy: 272, rx: 40, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 266, x1: 100, y1: 52, width: 8, color: 'shade' },
      { op: 'path', points: [[58, 200], [142, 200], [122, 128], [78, 128]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 100, cy: 200, rx: 44, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 214, rx: 8, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[64, 184], [136, 184], [140, 210], [60, 210]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 72, y0: 196, x1: 128, y1: 196, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 100, cy: 52, rx: 12, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'bench-leveling-wedgebox': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 36, y: 74, w: 184, h: 78, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 48, y: 86, w: 160, h: 54, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[60, 134], [96, 90], [100, 134]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[108, 134], [140, 98], [144, 134]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[152, 134], [180, 106], [184, 134]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 36, y: 62, w: 184, h: 14, r: 5, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 120, y0: 62, x1: 120, y1: 54, width: 3, color: 'shade' },
    ],
  },
  'bird-footprint-caster': {
    canvas: [200, 256],
    ops: [
      { op: 'path', points: [[44, 240], [44, 150], [156, 150], [156, 240]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 56, y: 162, w: 88, h: 66, r: 6, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 100, y0: 196, x1: 100, y1: 216, width: 3, color: 'shade' },
      { op: 'line', x0: 100, y0: 196, x1: 84, y1: 182, width: 3, color: 'shade' },
      { op: 'line', x0: 100, y0: 196, x1: 116, y1: 182, width: 3, color: 'shade' },
      { op: 'line', x0: 52, y0: 150, x1: 52, y1: 60, width: 7, color: 'shade' },
      { op: 'line', x0: 148, y0: 150, x1: 148, y1: 60, width: 7, color: 'shade' },
      { op: 'path', points: [[40, 60], [160, 60], [156, 40], [44, 40]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 40, x1: 100, y1: 56, width: 6, color: 'shade' },
      { op: 'path', points: [[88, 28], [112, 28], [108, 42], [92, 42]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'birdbath-ramp-insert': {
    canvas: [224, 256],
    ops: [
      { op: 'path', points: [[96, 130], [88, 210], [78, 236], [146, 236], [136, 210], [128, 130]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 124, rx: 86, ry: 28, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 118, rx: 64, ry: 16, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[170, 70], [196, 118], [150, 124], [130, 108]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 150, y0: 86, x1: 172, y1: 92, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 146, y0: 98, x1: 168, y1: 104, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 142, y0: 110, x1: 164, y1: 116, width: 2, color: 'INK.soft' },
    ],
  },
  'birdcall-whistle-tree': {
    canvas: [224, 288],
    ops: [
      { op: 'blob', cx: 112, cy: 268, rx: 44, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 262, x1: 110, y1: 80, width: 13, color: 'shade' },
      { op: 'line', x0: 110, y0: 150, x1: 54, y1: 118, width: 7, color: 'shade' },
      { op: 'line', x0: 110, y0: 128, x1: 168, y1: 98, width: 7, color: 'shade' },
      { op: 'blob', cx: 48, cy: 112, rx: 15, ry: 20, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 174, cy: 92, rx: 13, ry: 17, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 110, cy: 70, rx: 17, ry: 23, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 48, y0: 128, x1: 48, y1: 124, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 110, y0: 90, x1: 110, y1: 86, width: 3, color: 'INK.soft' },
    ],
  },
  'blanket-ladder': {
    canvas: [200, 288],
    ops: [
      { op: 'line', x0: 60, y0: 278, x1: 88, y1: 36, width: 10, color: 'shade' },
      { op: 'line', x0: 150, y0: 278, x1: 136, y1: 36, width: 10, color: 'shade' },
      { op: 'line', x0: 82, y0: 80, x1: 132, y1: 80, width: 7, color: 'shade' },
      { op: 'line', x0: 76, y0: 150, x1: 138, y1: 150, width: 7, color: 'shade' },
      { op: 'line', x0: 70, y0: 220, x1: 144, y1: 220, width: 7, color: 'shade' },
      { op: 'path', points: [[80, 76], [134, 76], [150, 200], [128, 210], [120, 150], [100, 150], [94, 212], [70, 200]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 107, y0: 86, x1: 110, y1: 196, width: 2, color: 'INK.soft' },
    ],
  },
  'blossom-counting-garland': {
    canvas: [200, 288],
    ops: [
      { op: 'line', x0: 46, y0: 56, x1: 46, y1: 272, width: 7, color: 'shade' },
      { op: 'line', x0: 154, y0: 56, x1: 154, y1: 272, width: 7, color: 'shade' },
      { op: 'path', points: [[46, 60], [100, 96], [154, 60]], close: false, outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[46, 108], [100, 148], [154, 108]], close: false, outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 70, cy: 78, rx: 12, ry: 11, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 92, rx: 12, ry: 11, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 130, cy: 78, rx: 12, ry: 11, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 80, cy: 130, rx: 12, ry: 11, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 120, cy: 130, rx: 12, ry: 11, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 34, y0: 272, x1: 58, y1: 272, width: 4, color: 'shade' },
      { op: 'line', x0: 142, y0: 272, x1: 166, y1: 272, width: 4, color: 'shade' },
    ],
  },
  'boardwalk-grip-brush': {
    canvas: [256, 112],
    ops: [
      { op: 'line', x0: 30, y0: 34, x1: 182, y1: 58, width: 11, color: 'shade' },
      { op: 'path', points: [[176, 44], [232, 52], [232, 76], [178, 72]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 182, y0: 74, x1: 182, y1: 104, width: 3, color: 'shade' },
      { op: 'line', x0: 194, y0: 75, x1: 194, y1: 105, width: 3, color: 'shade' },
      { op: 'line', x0: 206, y0: 75, x1: 206, y1: 106, width: 3, color: 'shade' },
      { op: 'line', x0: 218, y0: 74, x1: 218, y1: 105, width: 3, color: 'shade' },
      { op: 'line', x0: 230, y0: 72, x1: 230, y1: 102, width: 3, color: 'shade' },
      { op: 'line', x0: 36, y0: 38, x1: 120, y1: 50, width: 2, color: 'INK.soft' },
    ],
  },
  'boat-towing-reel': {
    canvas: [200, 256],
    ops: [
      { op: 'line', x0: 80, y0: 120, x1: 76, y1: 244, width: 11, color: 'shade' },
      { op: 'blob', cx: 78, cy: 244, rx: 34, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 96, cy: 98, rx: 58, ry: 56, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 96, cy: 98, rx: 30, ry: 29, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 96, y0: 98, x1: 60, y1: 62, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 96, y0: 98, x1: 132, y1: 134, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 96, y0: 98, x1: 134, y1: 70, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 96, cy: 98, rx: 9, ry: 9, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 150, y0: 120, x1: 182, y1: 150, width: 2, color: 'WATER.edge' },
    ],
  },
  'book-repair-press': {
    canvas: [224, 208],
    ops: [
      { op: 'path', points: [[44, 192], [180, 192], [172, 202], [52, 202]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 40, y: 136, w: 144, h: 58, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 108, w: 120, h: 24, r: 4, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 40, y: 76, w: 144, h: 34, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 58, y0: 76, x1: 58, y1: 40, width: 8, color: 'shade' },
      { op: 'blob', cx: 58, cy: 38, rx: 13, ry: 11, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 166, y0: 76, x1: 166, y1: 40, width: 8, color: 'shade' },
      { op: 'blob', cx: 166, cy: 38, rx: 13, ry: 11, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'bookmark-weaving-frame': {
    canvas: [160, 256],
    ops: [
      { op: 'path', points: [[44, 244], [116, 244], [110, 254], [50, 254]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 46, y: 40, w: 18, h: 210, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 96, y: 40, w: 18, h: 210, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 64, y0: 70, x1: 96, y1: 70, width: 3, color: 'SAGE.mid' },
      { op: 'line', x0: 64, y0: 110, x1: 96, y1: 110, width: 3, color: 'CLAY.deep' },
      { op: 'line', x0: 64, y0: 150, x1: 96, y1: 150, width: 3, color: 'SAGE.mid' },
      { op: 'line', x0: 64, y0: 190, x1: 96, y1: 190, width: 3, color: 'CLAY.deep' },
      { op: 'line', x0: 74, y0: 52, x1: 74, y1: 230, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 86, y0: 52, x1: 86, y1: 230, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 55, cy: 40, rx: 11, ry: 9, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 105, cy: 40, rx: 11, ry: 9, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'boot-warming-mat': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 52, y: 118, w: 152, h: 36, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 108], [216, 108], [224, 76], [32, 76]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 92, y0: 80, x1: 86, y1: 108, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 140, y0: 78, x1: 138, y1: 108, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 188, y0: 80, x1: 192, y1: 108, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 48, y0: 92, x1: 214, y1: 92, width: 2, color: 'INK.soft' },
      { op: 'speckle', x: 64, y: 124, w: 128, h: 24, count: 7, maxR: 2, alpha: 0.28, color: 'INK.grain' },
    ],
  },
  'bread-proofing-basket': {
    canvas: [208, 240],
    ops: [
      { op: 'path', points: [[44, 128], [164, 128], [148, 224], [60, 224]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[50, 148], [158, 148], [64, 168], [148, 168], [68, 188], [144, 188], [72, 208], [136, 208]], close: false, outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 58, y0: 138, x1: 150, y1: 138, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 104, cy: 120, rx: 68, ry: 24, n: 11, irregularity: 0.08, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 116], [104, 98], [168, 116], [150, 128], [58, 128]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 104, y0: 102, x1: 104, y1: 124, width: 2, color: 'INK.soft' },
    ],
  },
  'breadcrumb-stone-setter': {
    canvas: [200, 200],
    ops: [
      { op: 'rect', x: 40, y: 108, w: 98, h: 72, r: 16, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[40, 150], [46, 180], [132, 180], [138, 150]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 88, y0: 110, x1: 156, y1: 52, width: 9, color: 'shade' },
      { op: 'blob', cx: 160, cy: 48, rx: 13, ry: 13, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 110, y0: 180, x1: 110, y1: 192, width: 6, color: 'shade' },
      { op: 'blob', cx: 110, cy: 188, rx: 18, ry: 13, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 56, y0: 124, x1: 120, y1: 124, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 160, cy: 48, rx: 4, ry: 4, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'breeze-bass-harp': {
    canvas: [224, 256],
    ops: [
      { op: 'blob', cx: 112, cy: 240, rx: 52, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 58, y0: 234, x1: 52, y1: 120, width: 12, color: 'shade' },
      { op: 'line', x0: 166, y0: 234, x1: 172, y1: 120, width: 12, color: 'shade' },
      { op: 'path', points: [[40, 118], [184, 118], [160, 72], [64, 72]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 112, cy: 66, rx: 58, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 120, x1: 62, y1: 230, width: 5, color: 'INK.soft' },
      { op: 'line', x0: 90, y0: 120, x1: 90, y1: 230, width: 5, color: 'INK.soft' },
      { op: 'line', x0: 134, y0: 120, x1: 134, y1: 230, width: 5, color: 'INK.soft' },
      { op: 'line', x0: 162, y0: 120, x1: 162, y1: 230, width: 5, color: 'INK.soft' },
    ],
  },
  'breeze-cooling-screen': {
    canvas: [256, 144],
    ops: [
      { op: 'line', x0: 56, y0: 130, x1: 56, y1: 40, width: 6, color: 'shade' },
      { op: 'line', x0: 200, y0: 130, x1: 200, y1: 40, width: 6, color: 'shade' },
      { op: 'blob', cx: 56, cy: 130, rx: 30, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 200, cy: 130, rx: 30, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 56, y: 36, w: 144, h: 88, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 72, y: 52, w: 112, h: 56, r: 4, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 72, y0: 70, x1: 184, y1: 70, width: 2, color: 'WATER.mid' },
      { op: 'line', x0: 72, y0: 90, x1: 184, y1: 90, width: 2, color: 'WATER.mid' },
    ],
  },
  'bridge-board-tapper': {
    canvas: [256, 112],
    ops: [
      { op: 'line', x0: 30, y0: 42, x1: 182, y1: 66, width: 10, color: 'shade' },
      { op: 'rect', x: 170, y: 42, w: 58, h: 42, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 199, cy: 63, rx: 16, ry: 16, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 40, y0: 92, x1: 52, y1: 92, width: 3, color: 'INK.line' },
      { op: 'line', x0: 62, y0: 94, x1: 78, y1: 94, width: 3, color: 'INK.line' },
      { op: 'line', x0: 88, y0: 96, x1: 110, y1: 96, width: 3, color: 'INK.line' },
      { op: 'line', x0: 36, y0: 46, x1: 120, y1: 58, width: 2, color: 'INK.soft' },
    ],
  },
  'bridge-depth-gauge': {
    canvas: [200, 224],
    ops: [
      { op: 'line', x0: 78, y0: 208, x1: 120, y1: 40, width: 11, color: 'fill' },
      { op: 'line', x0: 78, y0: 208, x1: 120, y1: 40, width: 3, color: 'INK.line' },
      { op: 'line', x0: 92, y0: 150, x1: 108, y1: 150, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 86, y0: 118, x1: 102, y1: 118, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 80, y0: 86, x1: 96, y1: 86, width: 4, color: 'INK.soft' },
      { op: 'path', points: [[108, 128], [124, 118], [140, 128], [124, 138]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 78, cy: 206, rx: 34, ry: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 78, cy: 206, rx: 12, ry: 11, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'broth-cooling-rack': {
    canvas: [256, 224],
    ops: [
      { op: 'line', x0: 56, y0: 212, x1: 62, y1: 120, width: 11, color: 'shade' },
      { op: 'line', x0: 200, y0: 212, x1: 194, y1: 120, width: 11, color: 'shade' },
      { op: 'path', points: [[36, 110], [220, 106], [222, 128], [38, 132]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 118, x1: 62, y1: 170, width: 3, color: 'shade' },
      { op: 'line', x0: 100, y0: 118, x1: 100, y1: 170, width: 3, color: 'shade' },
      { op: 'line', x0: 138, y0: 118, x1: 138, y1: 170, width: 3, color: 'shade' },
      { op: 'line', x0: 176, y0: 118, x1: 176, y1: 170, width: 3, color: 'shade' },
      { op: 'blob', cx: 128, cy: 98, rx: 42, ry: 18, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 128, cy: 92, rx: 28, ry: 9, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'brush-pressure-gauge': {
    canvas: [256, 112],
    ops: [
      { op: 'path', points: [[22, 58], [150, 50], [152, 70], [24, 78]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 60, y0: 62, x1: 120, y1: 58, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 62, y0: 74, x1: 120, y1: 70, width: 4, color: 'INK.soft' },
      { op: 'path', points: [[150, 42], [178, 40], [180, 80], [152, 78]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 210, cy: 58, rx: 30, ry: 28, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 210, y0: 58, x1: 228, y1: 42, width: 3, color: 'INK.line' },
      { op: 'blob', cx: 210, cy: 58, rx: 4, ry: 4, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'brush-shaping-comb': {
    canvas: [256, 112],
    ops: [
      { op: 'path', points: [[20, 52], [150, 48], [150, 82], [20, 86]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 150, y: 54, w: 86, h: 24, r: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 86, x1: 40, y1: 102, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 58, y0: 86, x1: 58, y1: 104, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 76, y0: 86, x1: 76, y1: 104, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 94, y0: 86, x1: 94, y1: 104, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 86, x1: 112, y1: 104, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 130, y0: 86, x1: 130, y1: 102, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 166, y0: 66, x1: 220, y1: 66, width: 2, color: 'INK.soft' },
    ],
  },
  'bubble-chasing-bellows': {
    canvas: [224, 200],
    ops: [
      { op: 'path', points: [[40, 188], [44, 150], [120, 134], [124, 176]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[44, 150], [56, 116], [110, 102], [120, 134]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[120, 150], [168, 142], [170, 160], [124, 170]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 52, y0: 134, x1: 112, y1: 120, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 182, cy: 108, rx: 16, ry: 16, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 204, cy: 62, rx: 11, ry: 11, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 172, cy: 58, rx: 7, ry: 7, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'bulb-storage-hammock': {
    canvas: [256, 224],
    ops: [
      { op: 'line', x0: 48, y0: 40, x1: 60, y1: 120, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 208, y0: 40, x1: 196, y1: 120, width: 3, color: 'INK.soft' },
      { op: 'path', points: [[60, 72], [196, 72], [178, 108], [78, 108]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[74, 118], [182, 118], [166, 156], [90, 156]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[88, 166], [168, 166], [150, 206], [106, 206]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 78, y0: 90, x1: 178, y1: 90, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 92, y0: 138, x1: 164, y1: 138, width: 2, color: 'INK.soft' },
      { op: 'rect', x: 196, y: 86, w: 26, h: 18, r: 4, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 36, y: 132, w: 26, h: 18, r: 4, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
    ],
  },
  'burrow-rain-diverter': {
    canvas: [320, 144],
    ops: [
      { op: 'path', points: [[20, 80], [120, 60], [230, 76], [300, 112], [300, 130], [228, 96], [122, 80], [24, 98]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[28, 86], [120, 68], [226, 82], [290, 114], [286, 122], [224, 92], [122, 78], [30, 94]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 56, y0: 132, x1: 58, y1: 102, width: 9, color: 'shade' },
      { op: 'line', x0: 190, y0: 134, x1: 188, y1: 92, width: 9, color: 'shade' },
      { op: 'line', x0: 60, y0: 80, x1: 140, y1: 68, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 180, y0: 78, x1: 260, y1: 96, width: 2, color: 'INK.soft' },
    ],
  },
  'butterfly-sun-shelf': {
    canvas: [256, 168],
    ops: [
      { op: 'line', x0: 58, y0: 156, x1: 62, y1: 96, width: 11, color: 'shade' },
      { op: 'line', x0: 198, y0: 156, x1: 194, y1: 96, width: 11, color: 'shade' },
      { op: 'path', points: [[30, 72], [226, 68], [230, 96], [34, 100]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[30, 72], [226, 68], [224, 80], [32, 84]], close: true, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 60, y0: 88, x1: 196, y1: 85, width: 2, color: 'shade' },
      { op: 'blob', cx: 150, cy: 62, rx: 13, ry: 9, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'speckle', x: 44, y: 74, w: 168, h: 20, count: 12, maxR: 1.2, alpha: 0.12, color: 'INK.grain' },
    ],
  },
  'calm-breath-light': {
    canvas: [176, 256],
    ops: [
      { op: 'halo', cx: 88, cy: 96, r: 86 },
      { op: 'path', points: [[54, 242], [122, 242], [112, 196], [64, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 88, y0: 196, x1: 88, y1: 156, width: 12, color: 'shade' },
      { op: 'blob', cx: 88, cy: 104, rx: 58, ry: 56, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 104, rx: 30, ry: 29, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 104, rx: 6, ry: 6, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'candle-reflector-fan': {
    canvas: [208, 288],
    ops: [
      { op: 'halo', cx: 68, cy: 160, r: 52 },
      { op: 'path', points: [[96, 196], [120, 84], [156, 76], [140, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[110, 190], [132, 90], [150, 86], [132, 190]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 120, y0: 110, x1: 150, y1: 104, width: 2, color: 'shade' },
      { op: 'line', x0: 116, y0: 150, x1: 146, y1: 146, width: 2, color: 'shade' },
      { op: 'line', x0: 68, y0: 196, x1: 68, y1: 150, width: 6, color: 'shade' },
      { op: 'blob', cx: 68, cy: 146, rx: 7, ry: 11, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 60, y: 196, w: 96, h: 20, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 230, rx: 58, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'canopy-tieback-tree': {
    canvas: [200, 288],
    ops: [
      { op: 'blob', cx: 100, cy: 268, rx: 56, ry: 17, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[88, 260], [94, 80], [106, 80], [112, 260]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[96, 96], [60, 56], [48, 62], [90, 108]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[104, 90], [148, 52], [158, 60], [110, 104]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 50, cy: 60, rx: 10, ry: 10, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 154, cy: 56, rx: 10, ry: 10, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 100, y0: 110, x1: 100, y1: 250, width: 2, color: 'INK.soft' },
    ],
  },
  'care-tool-roll': {
    canvas: [200, 256],
    ops: [
      { op: 'blob', cx: 100, cy: 240, rx: 52, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[54, 238], [146, 238], [140, 72], [60, 72]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 72, rx: 42, ry: 12, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 98, x1: 80, y1: 228, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 100, y0: 96, x1: 100, y1: 230, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 98, x1: 120, y1: 228, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 80, cy: 110, rx: 6, ry: 9, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 120, cy: 110, rx: 6, ry: 9, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'caterpillar-plant-guard': {
    canvas: [224, 256],
    ops: [
      { op: 'line', x0: 46, y0: 236, x1: 46, y1: 150, width: 9, color: 'shade' },
      { op: 'line', x0: 112, y0: 244, x1: 112, y1: 150, width: 9, color: 'shade' },
      { op: 'line', x0: 178, y0: 236, x1: 178, y1: 150, width: 9, color: 'shade' },
      { op: 'blob', cx: 112, cy: 160, rx: 78, ry: 20, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 158, rx: 58, ry: 13, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[104, 158], [88, 108], [108, 72], [120, 72], [136, 108], [120, 158]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[112, 120], [80, 96], [88, 116]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[112, 104], [142, 84], [136, 104]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'ceramic-repair-cradle': {
    canvas: [224, 208],
    ops: [
      { op: 'blob', cx: 112, cy: 196, rx: 74, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[36, 128], [188, 128], [166, 194], [58, 194]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 128, rx: 76, ry: 20, fill: 'WATER.sand', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[78, 124], [118, 52], [140, 58], [112, 122]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[112, 122], [140, 58], [160, 74], [138, 126]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 120, y0: 70, x1: 126, y1: 118, width: 2, color: 'INK.soft' },
      { op: 'speckle', x: 60, y: 120, w: 104, h: 14, count: 8, maxR: 2, alpha: 0.3, color: 'INK.grain' },
    ],
  },
  'charcoal-sketch-rest': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[24, 72], [232, 64], [240, 118], [18, 126]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[28, 124], [64, 126], [62, 138], [34, 136]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[196, 124], [232, 120], [234, 134], [206, 138]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 40, y0: 86, x1: 216, y1: 80, width: 4, color: 'shade' },
      { op: 'line', x0: 40, y0: 100, x1: 216, y1: 94, width: 4, color: 'shade' },
      { op: 'rect', x: 150, y: 40, w: 76, h: 34, r: 7, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 92, x1: 120, y1: 90, width: 5, color: 'INK.line' },
      { op: 'speckle', x: 56, y: 104, w: 80, h: 12, count: 6, maxR: 2, alpha: 0.3, color: 'INK.grain' },
    ],
  },
  'charcoal-tea-brazier': {
    canvas: [200, 256],
    ops: [
      { op: 'path', points: [[44, 236], [56, 140], [144, 140], [156, 236]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[56, 140], [144, 140], [150, 236], [156, 236]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 100, cy: 134, rx: 62, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 128, rx: 42, ry: 11, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 62, y0: 176, x1: 86, y1: 176, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 176, x1: 138, y1: 176, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 66, y0: 204, x1: 92, y1: 204, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 110, y0: 204, x1: 134, y1: 204, width: 4, color: 'INK.soft' },
    ],
  },
  'charging-cable-swingarm': {
    canvas: [224, 288],
    ops: [
      { op: 'blob', cx: 78, cy: 266, rx: 56, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 78, y0: 262, x1: 78, y1: 60, width: 14, color: 'fill' },
      { op: 'path', points: [[72, 58], [200, 46], [202, 66], [74, 78]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 198, cy: 58, rx: 12, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 198, y0: 70, x1: 186, y1: 150, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 184, cy: 160, rx: 16, ry: 12, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 184, cy: 156, rx: 6, ry: 4, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'cloud-shadow-dial': {
    canvas: [216, 168],
    ops: [
      { op: 'blob', cx: 108, cy: 130, rx: 92, ry: 30, n: 16, irregularity: 0.05, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 128, rx: 70, ry: 22, n: 16, irregularity: 0.05, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 108, y0: 128, x1: 108, y1: 70, width: 6, color: 'shade' },
      { op: 'path', points: [[108, 128], [150, 142], [120, 150]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 38, y0: 128, x1: 56, y1: 124, width: 2.5, color: 'INK.soft' },
      { op: 'line', x0: 178, y0: 128, x1: 160, y1: 124, width: 2.5, color: 'INK.soft' },
      { op: 'line', x0: 108, y0: 100, x1: 108, y1: 90, width: 2.5, color: 'INK.soft' },
      { op: 'blob', cx: 108, cy: 70, rx: 6, ry: 6, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'cloud-shape-frame': {
    canvas: [224, 256],
    ops: [
      { op: 'path', points: [[54, 240], [40, 170], [184, 170], [170, 240]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[48, 170], [48, 40], [60, 40], [60, 170]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[164, 170], [164, 40], [176, 40], [176, 170]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 54, y0: 52, x1: 170, y1: 52, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 86, cy: 52, rx: 11, ry: 7, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[84, 68], [78, 104], [98, 118], [124, 110], [120, 76], [100, 66]], close: true, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[126, 90], [132, 120], [156, 128], [170, 112], [158, 86], [140, 82]], close: true, fill: 'PAPER.skyTop', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 138, cy: 52, rx: 10, ry: 7, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'comfort-scent-cloth': {
    canvas: [208, 224],
    ops: [
      { op: 'rect', x: 48, y: 110, w: 112, h: 100, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 58, y: 150, w: 124, h: 50, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 170, cy: 175, rx: 9, ry: 12, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[70, 150], [150, 150], [140, 86], [112, 72], [80, 86]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 90, y0: 110, x1: 130, y1: 110, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 96, y0: 94, x1: 124, y1: 94, width: 2, color: 'INK.soft' },
    ],
  },
  'community-bell-rope': {
    canvas: [224, 224],
    ops: [
      { op: 'path', points: [[30, 40], [30, 52], [194, 52], [194, 40]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[92, 52], [86, 84], [138, 84], [132, 52]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 92, rx: 12, ry: 12, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 72, y0: 84, x1: 58, y1: 188, width: 4, color: 'CLAY.mid' },
      { op: 'line', x0: 112, y0: 92, x1: 112, y1: 196, width: 4, color: 'CLAY.mid' },
      { op: 'line', x0: 152, y0: 84, x1: 166, y1: 188, width: 4, color: 'CLAY.mid' },
      { op: 'blob', cx: 58, cy: 196, rx: 13, ry: 11, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 204, rx: 13, ry: 11, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 166, cy: 196, rx: 13, ry: 11, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'community-mending-circle': {
    canvas: [256, 144],
    ops: [
      { op: 'line', x0: 72, y0: 134, x1: 78, y1: 92, width: 11, color: 'shade' },
      { op: 'line', x0: 184, y0: 134, x1: 178, y1: 92, width: 11, color: 'shade' },
      { op: 'blob', cx: 128, cy: 80, rx: 104, ry: 34, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 74, rx: 84, ry: 24, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 86, cy: 70, rx: 18, ry: 9, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 170, cy: 70, rx: 18, ry: 9, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 128, cy: 64, rx: 14, ry: 8, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 128, cy: 62, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'community-route-quilt': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[28, 40], [228, 36], [234, 128], [34, 134]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 96, y0: 38, x1: 98, y1: 131, width: 3, color: 'shade' },
      { op: 'line', x0: 164, y0: 37, x1: 166, y1: 130, width: 3, color: 'shade' },
      { op: 'line', x0: 31, y0: 86, x1: 231, y1: 83, width: 3, color: 'shade' },
      { op: 'rect', x: 42, y: 50, w: 42, h: 28, r: 4, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 108, y: 94, w: 42, h: 28, r: 4, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 176, y: 48, w: 42, h: 28, r: 4, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[42, 98], [84, 94], [150, 128], [42, 130]], close: true, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 50, y0: 62, x1: 210, y1: 110, width: 3, color: 'CLAY.deep' },
    ],
  },
  'community-seed-dispenser': {
    canvas: [256, 208],
    ops: [
      { op: 'rect', x: 36, y: 48, w: 184, h: 150, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[32, 48], [224, 48], [212, 28], [44, 28]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 68, w: 68, h: 48, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 136, y: 68, w: 68, h: 48, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 80, y0: 92, x1: 92, y1: 92, width: 5, color: 'INK.line' },
      { op: 'line', x0: 164, y0: 92, x1: 176, y1: 92, width: 5, color: 'INK.line' },
      { op: 'rect', x: 92, y: 140, w: 72, h: 40, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 110, y0: 160, x1: 146, y1: 160, width: 5, color: 'INK.soft' },
      { op: 'blob', cx: 128, cy: 42, rx: 6, ry: 6, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'community-tea-wheel': {
    canvas: [208, 200],
    ops: [
      { op: 'line', x0: 104, y0: 192, x1: 104, y1: 120, width: 13, color: 'shade' },
      { op: 'blob', cx: 104, cy: 192, rx: 36, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 104, cy: 98, rx: 84, ry: 34, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 88, rx: 66, ry: 24, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 104, y0: 64, x1: 104, y1: 112, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 50, y0: 88, x1: 158, y1: 88, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 70, cy: 80, rx: 11, ry: 7, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 138, cy: 80, rx: 11, ry: 7, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 104, cy: 88, rx: 6, ry: 6, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'community-tool-locker': {
    canvas: [256, 200],
    ops: [
      { op: 'line', x0: 58, y0: 190, x1: 58, y1: 160, width: 9, color: 'shade' },
      { op: 'line', x0: 198, y0: 190, x1: 198, y1: 160, width: 9, color: 'shade' },
      { op: 'rect', x: 40, y: 44, w: 176, h: 120, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[34, 46], [128, 28], [222, 46]], close: false, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 54, y: 60, w: 64, h: 92, r: 4, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'rect', x: 138, y: 60, w: 64, h: 92, r: 4, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 62, y0: 92, x1: 110, y1: 92, width: 3, color: 'INK.line' },
      { op: 'line', x0: 146, y0: 92, x1: 194, y1: 92, width: 3, color: 'INK.line' },
      { op: 'blob', cx: 124, cy: 106, rx: 5, ry: 5, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'compost-heat-spire': {
    canvas: [160, 256],
    ops: [
      { op: 'path', points: [[56, 240], [40, 200], [120, 200], [104, 240]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[70, 204], [64, 40], [96, 40], [90, 204]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 80, cy: 40, rx: 20, ry: 18, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[72, 200], [88, 200], [86, 170], [74, 170]], close: true, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[74, 170], [86, 170], [85, 130], [75, 130]], close: true, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[75, 130], [85, 130], [84, 90], [76, 90]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 96, y0: 120, x1: 108, y1: 120, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 96, y0: 160, x1: 108, y1: 160, width: 3, color: 'INK.soft' },
    ],
  },
  'compost-scrap-caddy': {
    canvas: [224, 176],
    ops: [
      { op: 'path', points: [[48, 80], [176, 80], [164, 164], [60, 164]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 84], [164, 84], [168, 72], [56, 72]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[58, 42], [166, 42], [176, 80], [48, 80]], close: false, outline: 'INK.line', lineWidth: 6 },
      { op: 'line', x0: 78, y0: 104, x1: 78, y1: 140, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 104, x1: 104, y1: 140, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 130, y0: 104, x1: 130, y1: 140, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 156, y0: 104, x1: 156, y1: 140, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 112, cy: 68, rx: 12, ry: 7, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'compost-turning-drum': {
    canvas: [256, 224],
    ops: [
      { op: 'line', x0: 64, y0: 210, x1: 80, y1: 120, width: 11, color: 'shade' },
      { op: 'line', x0: 192, y0: 210, x1: 176, y1: 120, width: 11, color: 'shade' },
      { op: 'rect', x: 52, y: 74, w: 152, h: 92, r: 40, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 90, y0: 84, x1: 90, y1: 156, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 118, y0: 80, x1: 118, y1: 160, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 146, y0: 80, x1: 146, y1: 160, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 174, y0: 84, x1: 174, y1: 156, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 204, y0: 120, x1: 232, y1: 120, width: 6, color: 'shade' },
      { op: 'line', x0: 232, y0: 120, x1: 232, y1: 150, width: 6, color: 'shade' },
      { op: 'blob', cx: 232, cy: 156, rx: 9, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'constellation-pin-lantern': {
    canvas: [176, 288],
    ops: [
      { op: 'halo', cx: 88, cy: 150, r: 80 },
      { op: 'line', x0: 88, y0: 272, x1: 88, y1: 232, width: 10, color: 'shade' },
      { op: 'path', points: [[52, 232], [124, 232], [112, 84], [64, 84]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 80, rx: 30, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 74, cy: 120, rx: 4, ry: 4, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 98, cy: 140, rx: 4, ry: 4, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 82, cy: 172, rx: 4, ry: 4, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 104, cy: 196, rx: 4, ry: 4, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 268, rx: 42, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'cool-down-fan-nest': {
    canvas: [256, 144],
    ops: [
      { op: 'blob', cx: 128, cy: 108, rx: 110, ry: 30, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 100, rx: 78, ry: 22, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 96, rx: 52, ry: 15, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 40, y0: 100, x1: 76, y1: 98, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 180, y0: 98, x1: 216, y1: 100, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 74, x1: 128, y1: 82, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 56, cy: 104, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 200, cy: 104, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'coolant-check-window': {
    canvas: [160, 200],
    ops: [
      { op: 'path', points: [[44, 186], [116, 186], [108, 150], [52, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 58, y: 40, w: 44, h: 116, r: 20, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 68, y: 52, w: 24, h: 92, r: 12, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 80, cy: 80, rx: 12, ry: 12, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 64, y0: 100, x1: 96, y1: 100, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 64, y0: 118, x1: 96, y1: 118, width: 2, color: 'INK.soft' },
    ],
  },
  'cooling-reed-mat': {
    canvas: [256, 144],
    ops: [
      { op: 'line', x0: 56, y0: 134, x1: 56, y1: 110, width: 8, color: 'shade' },
      { op: 'line', x0: 128, y0: 136, x1: 128, y1: 110, width: 8, color: 'shade' },
      { op: 'line', x0: 200, y0: 134, x1: 200, y1: 110, width: 8, color: 'shade' },
      { op: 'path', points: [[32, 80], [224, 76], [230, 104], [26, 108]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 86, x1: 216, y1: 82, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 42, y0: 98, x1: 218, y1: 94, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 80, y0: 78, x1: 80, y1: 104, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 77, x1: 128, y1: 105, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 176, y0: 78, x1: 176, y1: 104, width: 2, color: 'INK.soft' },
    ],
  },
  'cooling-window-shelf': {
    canvas: [224, 240],
    ops: [
      { op: 'path', points: [[40, 196], [184, 196], [184, 224], [40, 224]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 44, y: 80, w: 136, h: 116, r: 4, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 72, y0: 84, x1: 72, y1: 192, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 84, x1: 112, y1: 192, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 152, y0: 84, x1: 152, y1: 192, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 48, y0: 120, x1: 176, y1: 120, width: 2, color: 'INK.soft' },
      { op: 'path', points: [[44, 80], [180, 80], [180, 64], [44, 64]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 112, cy: 156, rx: 40, ry: 22, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'cord-splicing-jig': {
    canvas: [224, 200],
    ops: [
      { op: 'rect', x: 28, y: 108, w: 168, h: 80, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 56, y0: 108, x1: 56, y1: 74, width: 11, color: 'shade' },
      { op: 'line', x0: 112, y0: 108, x1: 112, y1: 74, width: 11, color: 'shade' },
      { op: 'line', x0: 168, y0: 108, x1: 168, y1: 74, width: 11, color: 'shade' },
      { op: 'path', points: [[56, 80], [112, 96], [168, 80]], close: false, outline: 'CLAY.deep', lineWidth: 6 },
      { op: 'path', points: [[56, 86], [112, 102], [168, 86]], close: false, outline: 'CLAY.mid', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 150, x1: 176, y1: 150, width: 2, color: 'INK.soft' },
    ],
  },
  'corner-swing-shelf': {
    canvas: [192, 256],
    ops: [
      { op: 'line', x0: 96, y0: 236, x1: 96, y1: 44, width: 8, color: 'shade' },
      { op: 'path', points: [[96, 60], [168, 84], [100, 100]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[96, 120], [36, 144], [92, 160]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[96, 180], [164, 204], [100, 220]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 130, cy: 86, rx: 12, ry: 9, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 62, cy: 148, rx: 11, ry: 9, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 96, cy: 44, rx: 7, ry: 5, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 96, cy: 236, rx: 16, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'crumb-catching-board': {
    canvas: [240, 208],
    ops: [
      { op: 'line', x0: 72, y0: 196, x1: 76, y1: 150, width: 11, color: 'shade' },
      { op: 'line', x0: 168, y0: 196, x1: 164, y1: 150, width: 11, color: 'shade' },
      { op: 'rect', x: 40, y: 118, w: 160, h: 36, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 56, y0: 126, x1: 92, y1: 126, width: 3, color: 'shade' },
      { op: 'line', x0: 108, y0: 126, x1: 150, y1: 126, width: 3, color: 'shade' },
      { op: 'rect', x: 56, y: 154, w: 128, h: 30, r: 5, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 120, cy: 169, rx: 10, ry: 7, fill: 'fill', outline: 'INK.soft', lineWidth: 3 },
    ],
  },
  'culvert-viewing-hatch': {
    canvas: [176, 256],
    ops: [
      { op: 'rect', x: 30, y: 40, w: 116, h: 196, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 44, y: 56, w: 88, h: 164, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 44, y0: 92, x1: 132, y1: 92, width: 3, color: 'INK.line' },
      { op: 'line', x0: 44, y0: 128, x1: 132, y1: 128, width: 3, color: 'INK.line' },
      { op: 'line', x0: 44, y0: 164, x1: 132, y1: 164, width: 3, color: 'INK.line' },
      { op: 'line', x0: 44, y0: 200, x1: 132, y1: 200, width: 3, color: 'INK.line' },
      { op: 'line', x0: 30, y0: 40, x1: 146, y1: 40, width: 6, color: 'INK.line' },
      { op: 'blob', cx: 118, cy: 74, rx: 18, ry: 18, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 146, y0: 48, x1: 118, y1: 74, width: 3, color: 'shade' },
    ],
  },
  'cup-rinsing-fountain': {
    canvas: [224, 240],
    ops: [
      { op: 'path', points: [[88, 150], [80, 224], [144, 224], [136, 150]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 140, rx: 74, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 134, rx: 54, ry: 13, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[150, 128], [196, 96], [206, 108], [166, 144]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 200, cy: 102, rx: 9, ry: 7, fill: 'WATER.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 224, rx: 44, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'cup-warming-tile': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 40, y: 70, w: 176, h: 58, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 70], [216, 70], [228, 52], [52, 52]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 78, y: 92, w: 100, h: 34, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[96, 126], [160, 126], [160, 150], [96, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 128, cy: 138, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 62, y0: 110, x1: 74, y1: 110, width: 3, color: 'INK.soft' },
    ],
  },
  'cupboard-tea-drawer': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 36, y: 48, w: 184, h: 104, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 48, y: 60, w: 78, h: 36, r: 5, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 130, y: 60, w: 78, h: 36, r: 5, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 48, y: 104, w: 78, h: 36, r: 5, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 130, y: 104, w: 78, h: 36, r: 5, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 87, cy: 78, rx: 5, ry: 5, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 169, cy: 122, rx: 5, ry: 5, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'curved-part-vise': {
    canvas: [200, 224],
    ops: [
      { op: 'rect', x: 48, y: 188, w: 104, h: 26, r: 7, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 188, x1: 100, y1: 150, width: 14, color: 'shade' },
      { op: 'path', points: [[40, 86], [92, 90], [92, 154], [40, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[160, 86], [108, 90], [108, 154], [160, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[100, 38], [126, 52], [128, 90], [100, 80], [72, 90], [74, 52]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 92, y0: 104, x1: 92, y1: 140, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 108, y0: 104, x1: 108, y1: 140, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 160, y0: 118, x1: 188, y1: 118, width: 8, color: 'shade' },
    ],
  },
  'cushion-mending-hoop': {
    canvas: [224, 288],
    ops: [
      { op: 'line', x0: 80, y0: 278, x1: 108, y1: 160, width: 11, color: 'shade' },
      { op: 'line', x0: 144, y0: 278, x1: 116, y1: 160, width: 11, color: 'shade' },
      { op: 'line', x0: 68, y0: 270, x1: 156, y1: 270, width: 9, color: 'shade' },
      { op: 'blob', cx: 112, cy: 98, rx: 76, ry: 76, n: 16, irregularity: 0.04, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 98, rx: 56, ry: 56, n: 16, irregularity: 0.05, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 88, y0: 98, x1: 136, y1: 98, width: 2, color: 'shade' },
      { op: 'line', x0: 112, y0: 74, x1: 112, y1: 122, width: 2, color: 'shade' },
      { op: 'blob', cx: 112, cy: 98, rx: 8, ry: 8, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'cushion-stack-cart': {
    canvas: [256, 224],
    ops: [
      { op: 'rect', x: 48, y: 56, w: 160, h: 108, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 62, y: 72, w: 132, h: 34, r: 16, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 62, y: 116, w: 132, h: 34, r: 16, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 44, y0: 94, x1: 212, y1: 94, width: 4, color: 'shade' },
      { op: 'line', x0: 44, y0: 138, x1: 212, y1: 138, width: 4, color: 'shade' },
      { op: 'blob', cx: 84, cy: 190, rx: 24, ry: 24, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 84, cy: 190, rx: 7, ry: 7, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 172, cy: 190, rx: 24, ry: 24, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 172, cy: 190, rx: 7, ry: 7, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'daily-use-carousel': {
    canvas: [224, 176],
    ops: [
      { op: 'blob', cx: 112, cy: 160, rx: 56, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 112, y0: 156, x1: 112, y1: 48, width: 7, color: 'shade' },
      { op: 'blob', cx: 112, cy: 108, rx: 80, ry: 22, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 56, rx: 64, ry: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 32, y0: 108, x1: 192, y1: 108, width: 3, color: 'shade' },
      { op: 'blob', cx: 68, cy: 98, rx: 13, ry: 9, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 150, cy: 100, rx: 13, ry: 9, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 50, rx: 13, ry: 8, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'dew-collector-cup': {
    canvas: [200, 240],
    ops: [
      { op: 'path', points: [[80, 232], [74, 180], [126, 180], [120, 232]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 180, rx: 28, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 176], [34, 96], [60, 52], [100, 84], [140, 52], [166, 96]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 176, x1: 100, y1: 78, width: 3, color: 'SAGE.shade' },
      { op: 'line', x0: 100, y0: 120, x1: 66, y1: 92, width: 2, color: 'SAGE.shade' },
      { op: 'line', x0: 100, y0: 120, x1: 134, y1: 92, width: 2, color: 'SAGE.shade' },
    ],
  },
  'dew-magnifier-stand': {
    canvas: [200, 224],
    ops: [
      { op: 'blob', cx: 92, cy: 206, rx: 48, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 92, y0: 204, x1: 92, y1: 80, width: 9, color: 'shade' },
      { op: 'line', x0: 92, y0: 140, x1: 150, y1: 120, width: 6, color: 'INK.soft' },
      { op: 'path', points: [[40, 156], [92, 150], [90, 168], [40, 172]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 92, y0: 90, x1: 140, y1: 74, width: 6, color: 'INK.soft' },
      { op: 'blob', cx: 150, cy: 66, rx: 34, ry: 34, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 150, cy: 66, rx: 22, ry: 22, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 140, y0: 58, x1: 150, y1: 50, width: 2, color: 'WATER.edge' },
    ],
  },
  'distance-counting-beads': {
    canvas: [224, 200],
    ops: [
      { op: 'line', x0: 40, y0: 182, x1: 48, y1: 80, width: 11, color: 'shade' },
      { op: 'line', x0: 184, y0: 182, x1: 176, y1: 80, width: 11, color: 'shade' },
      { op: 'line', x0: 44, y0: 92, x1: 180, y1: 92, width: 7, color: 'fill' },
      { op: 'line', x0: 44, y0: 92, x1: 180, y1: 92, width: 2, color: 'INK.line' },
      { op: 'blob', cx: 70, cy: 92, rx: 20, ry: 20, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 92, rx: 20, ry: 20, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 154, cy: 92, rx: 20, ry: 20, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 70, cy: 92, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 154, cy: 88, rx: 6, ry: 6, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'door-draft-snake-cradle': {
    canvas: [208, 224],
    ops: [
      { op: 'line', x0: 48, y0: 210, x1: 48, y1: 120, width: 10, color: 'shade' },
      { op: 'line', x0: 160, y0: 210, x1: 160, y1: 120, width: 10, color: 'shade' },
      { op: 'path', points: [[30, 210], [178, 210], [170, 196], [38, 196]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[40, 130], [60, 130], [60, 124], [40, 124]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[148, 130], [168, 130], [168, 124], [148, 124]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 150], [88, 138], [140, 150], [160, 144], [160, 128], [140, 134], [88, 122], [44, 134]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 52, y0: 136, x1: 150, y1: 138, width: 2, color: 'INK.soft' },
      { op: 'speckle', x: 50, y: 128, w: 104, h: 18, count: 6, maxR: 2, alpha: 0.25, color: 'INK.grain' },
    ],
  },
  'door-latch-practice-board': {
    canvas: [208, 224],
    ops: [
      { op: 'rect', x: 32, y: 36, w: 144, h: 172, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 76, cy: 80, rx: 24, ry: 24, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 76, y0: 80, x1: 98, y1: 68, width: 5, color: 'CLAY.deep' },
      { op: 'rect', x: 108, y: 60, w: 52, h: 20, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 120, y0: 70, x1: 156, y1: 70, width: 5, color: 'CLAY.deep' },
      { op: 'path', points: [[52, 140], [100, 140], [100, 160], [52, 160]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 100, y0: 150, x1: 140, y1: 150, width: 6, color: 'CLAY.deep' },
      { op: 'blob', cx: 144, cy: 150, rx: 9, ry: 9, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[40, 208], [168, 208], [178, 222], [30, 222]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'door-paw-bell': {
    canvas: [176, 256],
    ops: [
      { op: 'path', points: [[34, 238], [142, 238], [136, 196], [40, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 198, rx: 54, ry: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 192, rx: 40, ry: 11, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 88, y0: 192, x1: 88, y1: 40, width: 7, color: 'shade' },
      { op: 'path', points: [[60, 40], [116, 40], [124, 86], [112, 118], [64, 118], [52, 86]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 120, rx: 36, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 88, y0: 120, x1: 88, y1: 150, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 88, cy: 156, rx: 8, ry: 8, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'doorstep-rinse-pump': {
    canvas: [208, 208],
    ops: [
      { op: 'blob', cx: 104, cy: 188, rx: 76, ry: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 180, rx: 58, ry: 12, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[60, 178], [100, 178], [96, 108], [64, 108]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[64, 108], [96, 108], [150, 96], [154, 76], [96, 86]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[150, 96], [154, 76], [166, 82], [160, 100]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 80, y0: 108, x1: 80, y1: 48, width: 7, color: 'shade' },
      { op: 'path', points: [[58, 48], [102, 48], [100, 36], [60, 36]], close: true, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 156, y0: 100, x1: 156, y1: 120, width: 2, color: 'WATER.deep' },
    ],
  },
  'doorway-height-ribbons': {
    canvas: [224, 256],
    ops: [
      { op: 'path', points: [[36, 52], [188, 52], [182, 36], [42, 36]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[56, 52], [80, 52], [74, 232], [60, 238], [48, 232]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[100, 52], [124, 52], [118, 200], [104, 206], [94, 200]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[144, 52], [168, 52], [162, 244], [148, 250], [138, 244]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 56, y0: 110, x1: 80, y1: 110, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 100, y0: 110, x1: 124, y1: 110, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 144, y0: 110, x1: 168, y1: 110, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 100, y0: 160, x1: 124, y1: 160, width: 2, color: 'INK.soft' },
    ],
  },
  'dragonfly-perch-ring': {
    canvas: [256, 224],
    ops: [
      { op: 'blob', cx: 128, cy: 196, rx: 104, ry: 22, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 128, y0: 196, x1: 128, y1: 110, width: 12, color: 'shade' },
      { op: 'blob', cx: 128, cy: 96, rx: 52, ry: 50, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 96, rx: 30, ry: 29, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 128, y0: 46, x1: 128, y1: 18, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 178, y0: 82, x1: 214, y1: 70, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 78, y0: 82, x1: 42, y1: 70, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 166, y0: 126, x1: 196, y1: 146, width: 4, color: 'INK.soft' },
    ],
  },
  'dream-note-lantern': {
    canvas: [200, 200],
    ops: [
      { op: 'halo', cx: 100, cy: 96, r: 50 },
      { op: 'rect', x: 48, y: 52, w: 104, h: 120, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 64, y: 68, w: 72, h: 88, r: 6, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[78, 140], [92, 100], [110, 120], [122, 84]], close: false, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 64, y0: 92, x1: 136, y1: 92, width: 2, color: 'shade' },
      { op: 'blob', cx: 100, cy: 48, rx: 4, ry: 4, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 100, cy: 172, rx: 58, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'driftwood-sorting-cradle': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[28, 70], [228, 70], [214, 160], [42, 160]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 76, y0: 72, x1: 80, y1: 156, width: 7, color: 'shade' },
      { op: 'line', x0: 128, y0: 72, x1: 128, y1: 156, width: 7, color: 'shade' },
      { op: 'line', x0: 180, y0: 72, x1: 176, y1: 156, width: 7, color: 'shade' },
      { op: 'blob', cx: 52, cy: 60, rx: 18, ry: 8, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 102, cy: 56, rx: 13, ry: 7, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 154, cy: 58, rx: 10, ry: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 36, y0: 160, x1: 220, y1: 160, width: 3, color: 'shade' },
    ],
  },
  'dry-fit-assembly-table': {
    canvas: [256, 144],
    ops: [
      { op: 'line', x0: 52, y0: 134, x1: 56, y1: 80, width: 11, color: 'shade' },
      { op: 'line', x0: 204, y0: 134, x1: 200, y1: 80, width: 11, color: 'shade' },
      { op: 'path', points: [[28, 60], [228, 56], [230, 80], [30, 84]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 64, cy: 70, rx: 5, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 100, cy: 69, rx: 5, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 156, cy: 68, rx: 5, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 192, cy: 67, rx: 5, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 128, y0: 62, x1: 128, y1: 34, width: 7, color: 'CLAY.deep' },
      { op: 'line', x0: 80, y0: 63, x1: 80, y1: 40, width: 7, color: 'CLAY.mid' },
    ],
  },
  'duet-pipe-pair': {
    canvas: [200, 256],
    ops: [
      { op: 'blob', cx: 100, cy: 240, rx: 46, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[58, 234], [76, 234], [80, 72], [60, 72]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[120, 234], [138, 234], [140, 96], [122, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[68, 72], [132, 96], [132, 114], [68, 90]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 68, cy: 74, rx: 11, ry: 7, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 130, cy: 98, rx: 11, ry: 7, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 67, y0: 120, x1: 71, y1: 120, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 129, y0: 140, x1: 133, y1: 140, width: 4, color: 'INK.soft' },
    ],
  },
  'dumpling-folding-tray': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 70, y0: 212, x1: 76, y1: 160, width: 11, color: 'shade' },
      { op: 'line', x0: 154, y0: 212, x1: 148, y1: 160, width: 11, color: 'shade' },
      { op: 'blob', cx: 112, cy: 134, rx: 92, ry: 40, n: 14, irregularity: 0.04, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 130, rx: 30, ry: 16, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 58, cy: 124, rx: 15, ry: 11, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 166, cy: 124, rx: 15, ry: 11, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 78, cy: 154, rx: 15, ry: 11, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 146, cy: 154, rx: 15, ry: 11, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'dusk-window-prism': {
    canvas: [176, 256],
    ops: [
      { op: 'path', points: [[44, 248], [44, 72], [132, 72], [132, 248]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 58, y: 92, w: 60, h: 120, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[72, 98], [112, 116], [104, 200], [64, 182]], close: true, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 76, y0: 108, x1: 100, y1: 190, width: 2, color: 'WATER.mid' },
      { op: 'blob', cx: 88, cy: 88, rx: 5, ry: 5, fill: 'INK.soft', outline: 'INK.line', lineWidth: 2 },
      { op: 'blob', cx: 126, cy: 150, rx: 4, ry: 4, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 248, rx: 54, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'dust-blower-bellows': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[30, 128], [120, 128], [150, 90], [120, 52], [30, 80]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 86, x1: 120, y1: 78, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 48, y0: 104, x1: 120, y1: 100, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 48, y0: 120, x1: 118, y1: 118, width: 2, color: 'INK.soft' },
      { op: 'path', points: [[150, 82], [220, 86], [222, 98], [150, 98]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 230, cy: 92, rx: 16, ry: 12, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 70, cy: 132, rx: 40, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'ear-sensor-brush': {
    canvas: [256, 112],
    ops: [
      { op: 'path', points: [[20, 64], [150, 52], [152, 68], [22, 80]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[150, 42], [190, 40], [192, 80], [152, 78]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 214, cy: 42, rx: 24, ry: 20, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 222, cy: 78, rx: 24, ry: 20, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 66, x1: 120, y1: 58, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 160, y0: 54, x1: 160, y1: 74, width: 3, color: 'INK.soft' },
    ],
  },
  'echo-distance-tubes': {
    canvas: [256, 128],
    ops: [
      { op: 'path', points: [[24, 116], [24, 72], [238, 72], [238, 116]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 74], [40, 30], [100, 30], [100, 74]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 70, cy: 30, rx: 30, ry: 9, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[120, 74], [120, 46], [164, 46], [164, 74]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 142, cy: 46, rx: 22, ry: 7, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[186, 74], [186, 56], [218, 56], [218, 74]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 202, cy: 56, rx: 16, ry: 6, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 40, y0: 120, x1: 40, y1: 112, width: 10, color: 'shade' },
      { op: 'line', x0: 220, y0: 120, x1: 220, y1: 112, width: 10, color: 'shade' },
    ],
  },
  'echo-listening-cone': {
    canvas: [256, 224],
    ops: [
      { op: 'blob', cx: 120, cy: 206, rx: 50, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 120, y0: 200, x1: 120, y1: 140, width: 9, color: 'shade' },
      { op: 'path', points: [[112, 138], [208, 58], [222, 86], [140, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 214, cy: 72, rx: 18, ry: 34, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 211, cy: 72, rx: 11, ry: 24, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 120, cy: 138, rx: 14, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 130, y0: 130, x1: 170, y1: 98, width: 2, color: 'INK.soft' },
    ],
  },
  'echo-marker-clapper': {
    canvas: [160, 288],
    ops: [
      { op: 'blob', cx: 80, cy: 272, rx: 40, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 266, x1: 82, y1: 110, width: 18, color: 'shade' },
      { op: 'path', points: [[58, 108], [106, 108], [100, 52], [64, 52]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[110, 118], [140, 96], [148, 112], [120, 134]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 120, x1: 120, y1: 112, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 70, y0: 64, x1: 94, y1: 62, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 68, y0: 84, x1: 96, y1: 82, width: 2, color: 'INK.soft' },
    ],
  },
  'entry-gear-turntable': {
    canvas: [256, 176],
    ops: [
      { op: 'line', x0: 128, y0: 162, x1: 128, y1: 50, width: 12, color: 'shade' },
      { op: 'blob', cx: 128, cy: 162, rx: 58, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 116], [212, 116], [200, 96], [56, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[64, 68], [192, 68], [182, 50], [74, 50]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 60, y0: 118, x1: 52, y1: 140, width: 5, color: 'shade' },
      { op: 'line', x0: 128, y0: 118, x1: 128, y1: 142, width: 5, color: 'shade' },
      { op: 'line', x0: 196, y0: 118, x1: 204, y1: 140, width: 5, color: 'shade' },
      { op: 'blob', cx: 52, cy: 144, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 204, cy: 144, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'entry-mirror-hood': {
    canvas: [192, 256],
    ops: [
      { op: 'path', points: [[52, 242], [140, 242], [132, 206], [60, 206]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 96, y0: 206, x1: 96, y1: 150, width: 9, color: 'shade' },
      { op: 'rect', x: 40, y: 56, w: 112, h: 100, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 54, y: 70, w: 84, h: 74, r: 8, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[40, 58], [152, 58], [160, 30], [120, 22], [96, 40], [72, 22], [32, 30]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 80, x1: 110, y1: 120, width: 3, color: 'WATER.mid' },
      { op: 'line', x0: 78, y0: 78, x1: 118, y1: 110, width: 2, color: 'WATER.mid' },
    ],
  },
  'entry-scent-cloth-hook': {
    canvas: [176, 256],
    ops: [
      { op: 'path', points: [[36, 242], [140, 242], [132, 206], [44, 206]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 88, y0: 206, x1: 88, y1: 34, width: 8, color: 'shade' },
      { op: 'path', points: [[88, 34], [120, 34], [124, 52], [100, 52]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 118, y0: 50, x1: 118, y1: 68, width: 6, color: 'shade' },
      { op: 'path', points: [[96, 66], [140, 66], [150, 150], [128, 170], [100, 150]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[100, 150], [128, 170], [122, 150]], close: true, fill: 'SAGE.shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 112, y0: 80, x1: 112, y1: 140, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 130, y0: 82, x1: 134, y1: 138, width: 2, color: 'INK.soft' },
    ],
  },
  'erosion-check-stair': {
    canvas: [208, 200],
    ops: [
      { op: 'path', points: [[36, 184], [100, 184], [100, 140], [150, 140], [150, 100], [180, 100], [180, 184], [36, 184]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 68, y0: 184, x1: 68, y1: 120, width: 4, color: 'shade' },
      { op: 'line', x0: 124, y0: 140, x1: 124, y1: 76, width: 4, color: 'shade' },
      { op: 'line', x0: 166, y0: 100, x1: 166, y1: 40, width: 4, color: 'shade' },
      { op: 'line', x0: 62, y0: 136, x1: 74, y1: 136, width: 3, color: 'INK.line' },
      { op: 'line', x0: 118, y0: 96, x1: 130, y1: 96, width: 3, color: 'INK.line' },
      { op: 'line', x0: 160, y0: 60, x1: 172, y1: 60, width: 3, color: 'INK.line' },
      { op: 'line', x0: 44, y0: 160, x1: 92, y1: 160, width: 2, color: 'INK.soft' },
    ],
  },
  'fallen-branch-lifter': {
    canvas: [256, 112],
    ops: [
      { op: 'line', x0: 40, y0: 92, x1: 172, y1: 56, width: 9, color: 'shade' },
      { op: 'blob', cx: 42, cy: 92, rx: 15, ry: 12, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 120, y0: 68, x1: 150, y1: 36, width: 7, color: 'shade' },
      { op: 'path', points: [[166, 58], [214, 28], [226, 40], [182, 72]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[200, 30], [236, 18], [244, 30], [210, 46]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 120, cy: 68, rx: 7, ry: 7, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 180, y0: 52, x1: 216, y1: 34, width: 2, color: 'INK.soft' },
    ],
  },
  'farewell-keepsake-drawer': {
    canvas: [200, 200],
    ops: [
      { op: 'rect', x: 36, y: 40, w: 128, h: 150, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 50, y: 54, w: 100, h: 66, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 87, rx: 34, ry: 24, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 100, cy: 90, rx: 14, ry: 14, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[44, 178], [156, 178], [148, 134], [52, 134]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 70, y0: 156, x1: 130, y1: 156, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 100, cy: 156, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'fastener-length-gauge': {
    canvas: [224, 176],
    ops: [
      { op: 'rect', x: 28, y: 96, w: 168, h: 64, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 96], [40, 72], [176, 72], [176, 96]], close: false, outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 40, y0: 78, x1: 40, y1: 96, width: 3, color: 'shade' },
      { op: 'line', x0: 76, y0: 68, x1: 76, y1: 96, width: 3, color: 'shade' },
      { op: 'line', x0: 112, y0: 60, x1: 112, y1: 96, width: 3, color: 'shade' },
      { op: 'line', x0: 150, y0: 52, x1: 150, y1: 96, width: 3, color: 'shade' },
      { op: 'blob', cx: 58, cy: 126, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 96, cy: 126, rx: 9, ry: 9, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 140, cy: 126, rx: 12, ry: 12, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'feather-balance-stand': {
    canvas: [224, 200],
    ops: [
      { op: 'path', points: [[100, 190], [78, 176], [146, 176], [124, 190]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 176, x1: 112, y1: 70, width: 9, color: 'shade' },
      { op: 'line', x0: 40, y0: 52, x1: 184, y1: 86, width: 6, color: 'INK.line' },
      { op: 'path', points: [[107, 72], [117, 72], [114, 58], [110, 58]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 40, y0: 52, x1: 40, y1: 80, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 184, y0: 86, x1: 184, y1: 114, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 40, cy: 86, rx: 13, ry: 7, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 184, cy: 120, rx: 13, ry: 7, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'feather-drop-basket': {
    canvas: [256, 224],
    ops: [
      { op: 'path', points: [[48, 120], [208, 120], [190, 206], [66, 206]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 120, rx: 84, ry: 22, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 118, rx: 68, ry: 15, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 56, y0: 150, x1: 200, y1: 150, width: 2, color: 'shade' },
      { op: 'line', x0: 60, y0: 178, x1: 196, y1: 178, width: 2, color: 'shade' },
      { op: 'line', x0: 90, y0: 126, x1: 98, y1: 200, width: 2, color: 'shade' },
      { op: 'line', x0: 166, y0: 126, x1: 158, y1: 200, width: 2, color: 'shade' },
      { op: 'path', points: [[112, 90], [120, 118], [128, 90], [120, 72]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'fence-gap-gauge': {
    canvas: [256, 112],
    ops: [
      { op: 'line', x0: 30, y0: 88, x1: 150, y1: 52, width: 11, color: 'shade' },
      { op: 'line', x0: 146, y0: 50, x1: 236, y1: 40, width: 11, color: 'fill' },
      { op: 'blob', cx: 148, cy: 52, rx: 9, ry: 9, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 36, y0: 86, x1: 40, y1: 104, width: 4, color: 'shade' },
      { op: 'line', x0: 232, y0: 42, x1: 234, y1: 60, width: 4, color: 'fill' },
      { op: 'line', x0: 60, y0: 80, x1: 62, y1: 94, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 90, y0: 72, x1: 92, y1: 86, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 64, x1: 122, y1: 78, width: 2, color: 'INK.soft' },
    ],
  },
  'festival-tea-urn': {
    canvas: [224, 288],
    ops: [
      { op: 'path', points: [[58, 272], [52, 228], [172, 228], [166, 272]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 90], [52, 224], [172, 224], [164, 90]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 84, rx: 62, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[70, 224], [64, 158], [44, 158], [44, 180]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[154, 224], [160, 158], [180, 158], [180, 180]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 44, y0: 196, x1: 180, y1: 196, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 112, cy: 68, rx: 16, ry: 12, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'rect', x: 36, y: 196, w: 152, h: 12, r: 4, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
    ],
  },
  'fetch-sort-basket': {
    canvas: [224, 200],
    ops: [
      { op: 'path', points: [[40, 108], [184, 108], [170, 190], [54, 190]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 112, cy: 108, rx: 74, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 68, cy: 104, rx: 16, ry: 9, fill: 'PAPER.floor', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[112, 94], [126, 112], [98, 112]], close: true, fill: 'PAPER.floor', outline: 'INK.line', lineWidth: 3 },
      { op: 'rect', x: 146, y: 98, w: 22, h: 18, r: 4, fill: 'PAPER.floor', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 62, y0: 150, x1: 166, y1: 150, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 94, y0: 120, x1: 86, y1: 188, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 134, y0: 120, x1: 140, y1: 188, width: 2, color: 'INK.soft' },
    ],
  },
  'field-journal-cradle': {
    canvas: [200, 256],
    ops: [
      { op: 'line', x0: 100, y0: 244, x1: 150, y1: 150, width: 12, color: 'shade' },
      { op: 'path', points: [[44, 238], [156, 238], [150, 250], [50, 250]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 150], [160, 118], [170, 150], [58, 182]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[52, 148], [150, 122], [156, 142], [62, 168]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 100, y0: 136, x1: 104, y1: 172, width: 2, color: 'INK.soft' },
      { op: 'path', points: [[44, 128], [58, 124], [60, 150], [46, 154]], close: true, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[148, 108], [162, 104], [164, 130], [150, 134]], close: true, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'finish-drying-cage': {
    canvas: [200, 256],
    ops: [
      { op: 'rect', x: 36, y: 48, w: 128, h: 168, r: 14, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 36, y: 210, w: 128, h: 18, r: 5, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 52, y: 92, w: 96, h: 16, r: 4, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 52, y: 146, w: 96, h: 16, r: 4, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 68, y0: 52, x1: 68, y1: 210, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 100, y0: 52, x1: 100, y1: 210, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 132, y0: 52, x1: 132, y1: 210, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 40, y0: 130, x1: 160, y1: 130, width: 2, color: 'INK.soft' },
    ],
  },
  'firefly-viewing-box': {
    canvas: [176, 256],
    ops: [
      { op: 'path', points: [[40, 248], [40, 72], [88, 44], [136, 72], [136, 248]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 72], [88, 44], [136, 72], [88, 92]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[96, 108], [124, 108], [124, 196], [96, 196]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 56, y0: 112, x1: 80, y1: 112, width: 3, color: 'shade' },
      { op: 'line', x0: 56, y0: 136, x1: 80, y1: 136, width: 3, color: 'shade' },
      { op: 'line', x0: 56, y0: 160, x1: 80, y1: 160, width: 3, color: 'shade' },
      { op: 'blob', cx: 110, cy: 150, rx: 4, ry: 4, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 248, rx: 56, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'first-rain-cup-tree': {
    canvas: [224, 288],
    ops: [
      { op: 'path', points: [[104, 272], [100, 120], [124, 120], [120, 272]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 130, x1: 52, y1: 92, width: 6, color: 'shade' },
      { op: 'line', x0: 112, y0: 150, x1: 172, y1: 104, width: 6, color: 'shade' },
      { op: 'line', x0: 112, y0: 118, x1: 112, y1: 62, width: 6, color: 'shade' },
      { op: 'path', points: [[40, 92], [46, 116], [66, 116], [64, 92]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 62], [106, 86], [126, 86], [124, 62]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[160, 104], [166, 128], [186, 128], [184, 104]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 53, cy: 102, rx: 9, ry: 4, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 1.5 },
      { op: 'blob', cx: 113, cy: 72, rx: 9, ry: 4, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 1.5 },
      { op: 'line', x0: 80, y0: 272, x1: 144, y1: 272, width: 4, color: 'shade' },
    ],
  },
  'floating-leaf-race': {
    canvas: [256, 112],
    ops: [
      { op: 'path', points: [[20, 32], [236, 28], [238, 82], [22, 86]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[34, 40], [224, 38], [224, 52], [34, 54]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[34, 62], [224, 60], [224, 74], [34, 76]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 128, y0: 32, x1: 128, y1: 84, width: 5, color: 'shade' },
      { op: 'blob', cx: 70, cy: 47, rx: 13, ry: 7, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 172, cy: 68, rx: 13, ry: 7, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 20, y0: 86, x1: 236, y1: 82, width: 3, color: 'WATER.edge' },
    ],
  },
  'floor-reading-nest': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[28, 140], [40, 72], [96, 52], [160, 52], [216, 72], [228, 140], [182, 130], [150, 108], [106, 108], [74, 130]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[108, 150], [108, 112], [148, 112], [148, 150]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 114, y: 118, w: 28, h: 26, r: 3, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 128, y0: 120, x1: 128, y1: 142, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 58, y0: 96, x1: 72, y1: 86, width: 2, color: 'shade' },
      { op: 'line', x0: 198, y0: 96, x1: 184, y1: 86, width: 2, color: 'shade' },
    ],
  },
  'flower-opening-clock': {
    canvas: [224, 256],
    ops: [
      { op: 'path', points: [[100, 240], [92, 170], [132, 170], [124, 240]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 244, rx: 40, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 112, cy: 108, rx: 74, ry: 74, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 108, rx: 58, ry: 58, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 112, y0: 108, x1: 112, y1: 56, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 108, x1: 160, y1: 108, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 108, x1: 80, y1: 148, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 112, cy: 62, rx: 10, ry: 10, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 156, cy: 108, rx: 10, ry: 10, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 80, cy: 150, rx: 10, ry: 10, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'fog-path-beacon': {
    canvas: [160, 272],
    ops: [
      { op: 'halo', cx: 80, cy: 120, r: 70 },
      { op: 'blob', cx: 80, cy: 256, rx: 48, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 252, x1: 80, y1: 150, width: 7, color: 'shade' },
      { op: 'path', points: [[34, 150], [126, 150], [118, 120], [42, 120]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[30, 118], [130, 118], [110, 86], [50, 86]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 80, cy: 122, rx: 30, ry: 8, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 80, cy: 112, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'foldaway-display-ladder': {
    canvas: [176, 256],
    ops: [
      { op: 'line', x0: 48, y0: 236, x1: 52, y1: 36, width: 9, color: 'fill' },
      { op: 'line', x0: 128, y0: 236, x1: 124, y1: 36, width: 9, color: 'fill' },
      { op: 'path', points: [[50, 72], [126, 72], [126, 90], [50, 90]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[50, 128], [126, 128], [126, 146], [50, 146]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[50, 184], [126, 184], [126, 202], [50, 202]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 52, cy: 72, rx: 6, ry: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 52, cy: 128, rx: 6, ry: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 68, rx: 16, ry: 12, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 52, cy: 236, rx: 12, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 124, cy: 236, rx: 12, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'folding-letter-screen': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[24, 52], [88, 40], [90, 160], [26, 162]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[94, 40], [162, 40], [164, 160], [96, 160]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[168, 40], [232, 52], [230, 162], [170, 160]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[104, 96], [154, 96], [154, 134], [104, 134]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[104, 96], [129, 116], [154, 96]], close: false, outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[36, 100], [78, 100], [78, 138], [38, 138]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[36, 100], [57, 118], [78, 100]], close: false, outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 92, y0: 40, x1: 92, y1: 160, width: 3, color: 'shade' },
    ],
  },
  'folding-nap-canopy': {
    canvas: [256, 224],
    ops: [
      { op: 'line', x0: 128, y0: 210, x1: 128, y1: 76, width: 9, color: 'shade' },
      { op: 'line', x0: 128, y0: 210, x1: 86, y1: 210, width: 7, color: 'shade' },
      { op: 'line', x0: 128, y0: 210, x1: 170, y1: 210, width: 7, color: 'shade' },
      { op: 'path', points: [[24, 84], [128, 46], [232, 84], [218, 112], [128, 80], [38, 112]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 128, y0: 52, x1: 40, y1: 104, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 50, x1: 84, y1: 92, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 50, x1: 172, y1: 92, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 52, x1: 216, y1: 104, width: 2, color: 'INK.soft' },
    ],
  },
  'follow-light-puck': {
    canvas: [224, 160],
    ops: [
      { op: 'blob', cx: 86, cy: 150, rx: 54, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 138], [48, 108], [124, 108], [128, 138]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 86, cy: 108, rx: 42, ry: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 86, cy: 106, rx: 22, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 86, cy: 106, rx: 7, ry: 4, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 118, y0: 104, x1: 182, y1: 126, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 110, x1: 188, y1: 150, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 186, cy: 146, rx: 18, ry: 7, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'footstep-drum-mat': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[24, 96], [232, 96], [224, 134], [32, 134]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[24, 84], [232, 84], [232, 98], [24, 98]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 64, cy: 86, rx: 26, ry: 11, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 128, cy: 86, rx: 26, ry: 11, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 192, cy: 86, rx: 26, ry: 11, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 96, y0: 84, x1: 96, y1: 98, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 160, y0: 84, x1: 160, y1: 98, width: 2, color: 'INK.soft' },
    ],
  },
  'found-parts-library': {
    canvas: [256, 192],
    ops: [
      { op: 'rect', x: 36, y: 32, w: 184, h: 148, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 50, y: 46, w: 156, h: 34, r: 4, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 50, y: 90, w: 156, h: 34, r: 4, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 50, y: 134, w: 156, h: 34, r: 4, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 90, cy: 63, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 150, cy: 107, rx: 7, ry: 5, fill: 'SAGE.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 110, cy: 151, rx: 6, ry: 6, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 118, y0: 63, x1: 138, y1: 63, width: 4, color: 'shade' },
      { op: 'line', x0: 118, y0: 107, x1: 138, y1: 107, width: 4, color: 'shade' },
      { op: 'line', x0: 44, y0: 180, x1: 212, y1: 180, width: 4, color: 'shade' },
    ],
  },
  'fragile-item-nest': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 32, y: 56, w: 192, h: 92, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 72, y0: 68, x1: 104, y1: 100, width: 7, color: 'CLAY.light' },
      { op: 'line', x0: 120, y0: 68, x1: 104, y1: 100, width: 7, color: 'CLAY.light' },
      { op: 'line', x0: 152, y0: 68, x1: 184, y1: 104, width: 7, color: 'CLAY.light' },
      { op: 'line', x0: 200, y0: 68, x1: 184, y1: 104, width: 7, color: 'CLAY.light' },
      { op: 'blob', cx: 104, cy: 104, rx: 20, ry: 18, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 184, cy: 108, rx: 15, ry: 14, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 40, y0: 148, x1: 216, y1: 148, width: 4, color: 'shade' },
    ],
  },
  'friendship-plant-exchange': {
    canvas: [224, 272],
    ops: [
      { op: 'path', points: [[34, 160], [46, 256], [104, 256], [110, 160]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[118, 140], [124, 256], [182, 256], [190, 140]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 72, cy: 160, rx: 40, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 154, cy: 140, rx: 38, ry: 11, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 72, y0: 160, x1: 72, y1: 104, width: 4, color: 'SAGE.deep' },
      { op: 'path', points: [[72, 108], [50, 84], [74, 80], [86, 104]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 154, y0: 140, x1: 156, y1: 92, width: 4, color: 'SAGE.deep' },
      { op: 'path', points: [[156, 96], [178, 72], [182, 98], [160, 108]], close: true, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 108, y0: 206, x1: 120, y1: 206, width: 4, color: 'shade' },
    ],
  },
  'frog-shade-tile': {
    canvas: [256, 168],
    ops: [
      { op: 'blob', cx: 78, cy: 140, rx: 26, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 178, cy: 142, rx: 28, ry: 17, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[28, 98], [228, 90], [238, 112], [34, 124]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[26, 80], [226, 72], [230, 94], [30, 102]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[26, 80], [226, 72], [228, 82], [28, 90]], close: true, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 70, y0: 88, x1: 110, y1: 85, width: 2, color: 'WATER.mid' },
      { op: 'line', x0: 150, y0: 83, x1: 190, y1: 80, width: 2, color: 'WATER.mid' },
    ],
  },
  'frost-warning-flower': {
    canvas: [200, 208],
    ops: [
      { op: 'blob', cx: 100, cy: 192, rx: 50, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 188, x1: 100, y1: 110, width: 6, color: 'shade' },
      { op: 'path', points: [[100, 110], [60, 72], [78, 40], [100, 58]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[100, 110], [140, 72], [122, 40], [100, 58]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[100, 110], [64, 108], [58, 74], [88, 80]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 110], [136, 108], [142, 74], [112, 80]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 100, rx: 13, ry: 13, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 100, y0: 88, x1: 100, y1: 64, width: 2, color: 'INK.soft' },
    ],
  },
  'fruit-catching-apron': {
    canvas: [208, 256],
    ops: [
      { op: 'line', x0: 48, y0: 240, x1: 52, y1: 96, width: 8, color: 'shade' },
      { op: 'line', x0: 160, y0: 240, x1: 156, y1: 96, width: 8, color: 'shade' },
      { op: 'line', x0: 48, y0: 96, x1: 160, y1: 96, width: 5, color: 'shade' },
      { op: 'path', points: [[52, 100], [156, 100], [120, 196], [88, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 74, y0: 130, x1: 134, y1: 130, width: 2, color: 'shade' },
      { op: 'line', x0: 86, y0: 160, x1: 122, y1: 160, width: 2, color: 'shade' },
      { op: 'blob', cx: 104, cy: 188, rx: 16, ry: 15, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[80, 60], [140, 52], [120, 84], [88, 86]], close: true, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
    ],
  },
  'fruit-slicing-guide': {
    canvas: [208, 208],
    ops: [
      { op: 'path', points: [[36, 148], [172, 148], [164, 196], [44, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 120, rx: 62, ry: 58, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 62, x1: 104, y1: 178, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 46, y0: 120, x1: 162, y1: 120, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 62, y0: 78, x1: 146, y1: 162, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 146, y0: 78, x1: 62, y1: 162, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 104, cy: 62, rx: 12, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 104, cy: 120, rx: 8, ry: 8, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'garden-kneeling-sled': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[40, 150], [36, 120], [208, 116], [216, 146]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 72, y: 92, w: 108, h: 30, r: 12, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 40, y0: 150, x1: 40, y1: 166, width: 6, color: 'shade' },
      { op: 'line', x0: 210, y0: 146, x1: 210, y1: 166, width: 6, color: 'shade' },
      { op: 'rect', x: 44, y: 124, w: 24, h: 24, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 184, y: 120, w: 24, h: 24, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 216, y0: 130, x1: 244, y1: 108, width: 4, color: 'shade' },
      { op: 'blob', cx: 244, cy: 104, rx: 12, ry: 10, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'garden-row-marker': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 104, y0: 146, x1: 188, y1: 52, width: 7, color: 'shade' },
      { op: 'blob', cx: 92, cy: 150, rx: 58, ry: 58, n: 14, irregularity: 0.03, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 92, y0: 150, x1: 92, y1: 94, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 92, y0: 150, x1: 140, y1: 172, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 92, y0: 150, x1: 44, y1: 172, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 92, y0: 150, x1: 126, y1: 110, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 92, y0: 150, x1: 58, y1: 110, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 92, cy: 150, rx: 14, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[36, 210], [48, 200], [60, 210], [48, 220]], close: true, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'garden-tool-wash': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 56, y0: 210, x1: 62, y1: 150, width: 9, color: 'shade' },
      { op: 'line', x0: 168, y0: 210, x1: 162, y1: 150, width: 9, color: 'shade' },
      { op: 'path', points: [[36, 108], [44, 150], [180, 150], [188, 108]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 110, rx: 70, ry: 13, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 62, y0: 102, x1: 62, y1: 82, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 82, y0: 102, x1: 82, y1: 80, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 102, y0: 102, x1: 102, y1: 82, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 122, y0: 102, x1: 122, y1: 80, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 142, y0: 102, x1: 142, y1: 82, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 96, y0: 150, x1: 128, y1: 150, width: 5, color: 'INK.line' },
    ],
  },
  'gentle-jump-hoop': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[44, 150], [40, 112], [60, 112], [64, 150]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[192, 150], [196, 112], [216, 112], [212, 150]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 52, cy: 150, rx: 24, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 204, cy: 150, rx: 24, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 128, cy: 108, rx: 70, ry: 60, fill: 'fill', outline: 'INK.line', lineWidth: 7 },
      { op: 'blob', cx: 128, cy: 110, rx: 52, ry: 44, fill: 'PAPER.floor', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 128, cy: 50, rx: 9, ry: 8, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'glow-paint-mixing-stone': {
    canvas: [208, 224],
    ops: [
      { op: 'path', points: [[40, 208], [32, 150], [60, 120], [148, 120], [176, 150], [168, 208]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 122, rx: 68, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 104, cy: 120, rx: 48, ry: 12, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[44, 116], [164, 116], [150, 72], [110, 84], [58, 72]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 116, x1: 110, y1: 84, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 104, cy: 92, rx: 8, ry: 6, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 56, y0: 160, x1: 150, y1: 160, width: 2, color: 'shade' },
      { op: 'blob', cx: 104, cy: 206, rx: 62, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'glow-thread-spool': {
    canvas: [224, 200],
    ops: [
      { op: 'line', x0: 60, y0: 188, x1: 64, y1: 108, width: 9, color: 'shade' },
      { op: 'line', x0: 156, y0: 188, x1: 152, y1: 108, width: 9, color: 'shade' },
      { op: 'rect', x: 72, y: 56, w: 76, h: 76, r: 34, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 110, cy: 94, rx: 24, ry: 24, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 110, cy: 94, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 148, y0: 94, x1: 190, y1: 76, width: 5, color: 'shade' },
      { op: 'blob', cx: 192, cy: 72, rx: 8, ry: 8, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 110, cy: 188, rx: 58, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'glue-warming-cup': {
    canvas: [200, 224],
    ops: [
      { op: 'blob', cx: 100, cy: 206, rx: 62, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 108], [156, 108], [148, 200], [52, 200]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 108, rx: 56, ry: 16, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[70, 116], [130, 116], [124, 180], [76, 180]], close: true, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[44, 108], [36, 86], [60, 84], [60, 108]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 48, y0: 92, x1: 52, y1: 54, width: 5, color: 'CLAY.deep' },
      { op: 'line', x0: 70, y0: 140, x1: 124, y1: 140, width: 2, color: 'INK.soft' },
    ],
  },
  'goodbye-lantern-path': {
    canvas: [256, 256],
    ops: [
      { op: 'path', points: [[40, 248], [96, 176], [200, 176], [232, 248]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 74, y0: 222, x1: 74, y1: 172, width: 6, color: 'shade' },
      { op: 'path', points: [[58, 170], [64, 134], [88, 134], [92, 170]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 75, cy: 146, rx: 11, ry: 11, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 150, y0: 204, x1: 150, y1: 158, width: 6, color: 'shade' },
      { op: 'path', points: [[136, 156], [141, 124], [161, 124], [166, 156]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 151, cy: 135, rx: 10, ry: 10, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 210, y0: 192, x1: 210, y1: 150, width: 6, color: 'shade' },
      { op: 'path', points: [[198, 148], [202, 118], [220, 118], [224, 148]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 211, cy: 129, rx: 9, ry: 9, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'goodnight-chord-lamp': {
    canvas: [200, 288],
    ops: [
      { op: 'halo', cx: 100, cy: 108, r: 86 },
      { op: 'blob', cx: 100, cy: 270, rx: 46, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 264, x1: 100, y1: 150, width: 8, color: 'shade' },
      { op: 'path', points: [[58, 150], [142, 150], [124, 88], [76, 88]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 100, cy: 132, rx: 30, ry: 9, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 78, y0: 150, x1: 78, y1: 210, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 100, y0: 150, x1: 100, y1: 222, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 122, y0: 150, x1: 122, y1: 210, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 78, cy: 214, rx: 6, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 226, rx: 6, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 122, cy: 214, rx: 6, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 102, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'goodnight-cover-cradle': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[28, 130], [180, 130], [170, 96], [38, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 96, rx: 74, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 196, y0: 130, x1: 196, y1: 48, width: 11, color: 'shade' },
      { op: 'path', points: [[196, 52], [238, 56], [236, 116], [196, 112]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 204, y0: 72, x1: 230, y1: 74, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 204, y0: 92, x1: 230, y1: 94, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 196, y0: 50, x1: 238, y1: 54, width: 4, color: 'INK.soft' },
    ],
  },
  'goodnight-porch-switch': {
    canvas: [200, 208],
    ops: [
      { op: 'blob', cx: 100, cy: 190, rx: 68, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 48, y: 72, w: 104, h: 116, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 72], [140, 72], [136, 52], [64, 52]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 68, y: 96, w: 64, h: 76, r: 8, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 100, y0: 110, x1: 100, y1: 160, width: 5, color: 'INK.soft' },
      { op: 'path', points: [[84, 108], [116, 108], [112, 128], [88, 128]], close: true, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 154, rx: 10, ry: 7, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'grafting-tool-rest': {
    canvas: [200, 224],
    ops: [
      { op: 'rect', x: 36, y: 120, w: 128, h: 92, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 36, y: 120, w: 128, h: 22, r: 10, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 60, y0: 126, x1: 60, y1: 138, width: 7, color: 'INK.line' },
      { op: 'line', x0: 90, y0: 126, x1: 90, y1: 138, width: 7, color: 'INK.line' },
      { op: 'line', x0: 120, y0: 126, x1: 120, y1: 138, width: 7, color: 'INK.line' },
      { op: 'line', x0: 60, y0: 132, x1: 52, y1: 76, width: 4, color: 'shade' },
      { op: 'line', x0: 90, y0: 132, x1: 96, y1: 70, width: 4, color: 'shade' },
      { op: 'blob', cx: 52, cy: 70, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 142, cy: 162, rx: 16, ry: 12, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 142, cy: 159, rx: 10, ry: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'guest-slipper-drawer': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 28, y: 40, w: 200, h: 108, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 128, y0: 44, x1: 128, y1: 144, width: 4, color: 'shade' },
      { op: 'rect', x: 44, y: 58, w: 72, h: 74, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 140, y: 58, w: 72, h: 74, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 56, y0: 76, x1: 104, y1: 76, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 56, y0: 94, x1: 104, y1: 94, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 56, y0: 112, x1: 104, y1: 112, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 152, y0: 76, x1: 200, y1: 76, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 152, y0: 94, x1: 200, y1: 94, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 152, y0: 112, x1: 200, y1: 112, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 80, cy: 118, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'habitat-care-cart': {
    canvas: [288, 224],
    ops: [
      { op: 'rect', x: 40, y: 84, w: 208, h: 92, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 144, y0: 92, x1: 144, y1: 168, width: 4, color: 'shade' },
      { op: 'line', x0: 48, y0: 130, x1: 140, y1: 130, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 92, cy: 108, rx: 20, ry: 12, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 196, y0: 94, x1: 196, y1: 120, width: 7, color: 'CLAY.mid' },
      { op: 'path', points: [[40, 176], [8, 148], [18, 142], [48, 168]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 86, cy: 190, rx: 24, ry: 24, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 86, cy: 190, rx: 8, ry: 8, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 210, cy: 190, rx: 24, ry: 24, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 210, cy: 190, rx: 8, ry: 8, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'hail-protection-fan': {
    canvas: [256, 160],
    ops: [
      { op: 'blob', cx: 128, cy: 146, rx: 110, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 142, x1: 40, y1: 96, width: 7, color: 'shade' },
      { op: 'path', points: [[36, 96], [230, 52], [238, 72], [44, 118]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[120, 80], [230, 52], [238, 72], [126, 100]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 70, y0: 90, x1: 76, y1: 112, width: 2.5, color: 'shade' },
      { op: 'line', x0: 118, y0: 80, x1: 124, y1: 102, width: 2.5, color: 'shade' },
      { op: 'line', x0: 166, y0: 70, x1: 172, y1: 92, width: 2.5, color: 'shade' },
      { op: 'line', x0: 214, y0: 60, x1: 220, y1: 82, width: 2.5, color: 'shade' },
    ],
  },
  'hand-drill-guide': {
    canvas: [200, 224],
    ops: [
      { op: 'rect', x: 36, y: 168, w: 128, h: 40, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 62, y: 150, w: 76, h: 24, r: 5, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 76, y0: 150, x1: 76, y1: 172, width: 7, color: 'CLAY.deep' },
      { op: 'line', x0: 124, y0: 150, x1: 124, y1: 172, width: 7, color: 'CLAY.deep' },
      { op: 'rect', x: 86, y: 40, w: 30, h: 118, r: 12, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 101, y0: 48, x1: 101, y1: 150, width: 5, color: 'shade' },
      { op: 'line', x0: 92, y0: 58, x1: 92, y1: 140, width: 2, color: 'INK.soft' },
    ],
  },
  'hand-mill-table': {
    canvas: [208, 208],
    ops: [
      { op: 'line', x0: 52, y0: 196, x1: 56, y1: 150, width: 12, color: 'shade' },
      { op: 'line', x0: 156, y0: 196, x1: 152, y1: 150, width: 12, color: 'shade' },
      { op: 'rect', x: 40, y: 118, w: 128, h: 40, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[56, 118], [152, 118], [156, 138], [52, 138]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 58, y0: 146, x1: 150, y1: 146, width: 3, color: 'shade' },
      { op: 'blob', cx: 104, cy: 94, rx: 48, ry: 30, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 94, rx: 12, ry: 9, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 104, y0: 94, x1: 140, y1: 58, width: 7, color: 'shade' },
      { op: 'blob', cx: 146, cy: 52, rx: 10, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'hanging-pocket-tree': {
    canvas: [176, 256],
    ops: [
      { op: 'blob', cx: 88, cy: 238, rx: 50, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 88, y0: 236, x1: 88, y1: 48, width: 10, color: 'fill' },
      { op: 'line', x0: 88, y0: 96, x1: 36, y1: 68, width: 7, color: 'fill' },
      { op: 'line', x0: 88, y0: 110, x1: 140, y1: 78, width: 7, color: 'fill' },
      { op: 'path', points: [[20, 70], [52, 70], [48, 118], [24, 118]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[124, 80], [156, 80], [152, 128], [128, 128]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[68, 124], [108, 124], [104, 178], [72, 178]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 36, y0: 70, x1: 36, y1: 62, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 88, y0: 124, x1: 88, y1: 116, width: 3, color: 'INK.soft' },
    ],
  },
  'harvest-sharing-wheel': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 112, y0: 116, x1: 112, y1: 214, width: 12, color: 'shade' },
      { op: 'blob', cx: 112, cy: 108, rx: 94, ry: 92, n: 14, irregularity: 0.04, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 18, y0: 108, x1: 206, y1: 108, width: 3, color: 'shade' },
      { op: 'line', x0: 112, y0: 14, x1: 112, y1: 202, width: 3, color: 'shade' },
      { op: 'line', x0: 45, y0: 41, x1: 179, y1: 175, width: 3, color: 'shade' },
      { op: 'line', x0: 179, y0: 41, x1: 45, y1: 175, width: 3, color: 'shade' },
      { op: 'blob', cx: 112, cy: 60, rx: 15, ry: 13, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 160, cy: 108, rx: 13, ry: 13, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 156, rx: 14, ry: 13, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 64, cy: 108, rx: 13, ry: 13, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 108, rx: 12, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'harvest-weigh-balance': {
    canvas: [256, 224],
    ops: [
      { op: 'line', x0: 128, y0: 210, x1: 128, y1: 56, width: 9, color: 'shade' },
      { op: 'line', x0: 48, y0: 58, x1: 208, y1: 58, width: 6, color: 'fill' },
      { op: 'blob', cx: 128, cy: 56, rx: 10, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 48, y0: 60, x1: 48, y1: 120, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 208, y0: 60, x1: 208, y1: 104, width: 3, color: 'INK.soft' },
      { op: 'path', points: [[20, 120], [28, 164], [68, 164], [76, 120]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[180, 104], [188, 148], [228, 148], [236, 104]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 48, cy: 120, rx: 28, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 208, cy: 104, rx: 28, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 200, cy: 96, rx: 12, ry: 10, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'headrest-drawer': {
    canvas: [224, 176],
    ops: [
      { op: 'rect', x: 40, y: 70, w: 144, h: 92, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 82], [60, 58], [164, 58], [184, 82]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 56, y: 112, w: 136, h: 40, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 124, cy: 132, rx: 9, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 52, y0: 78, x1: 52, y1: 108, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 172, y0: 78, x1: 172, y1: 108, width: 2, color: 'INK.soft' },
    ],
  },
  'heatwave-water-crock': {
    canvas: [200, 240],
    ops: [
      { op: 'blob', cx: 100, cy: 224, rx: 56, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 90], [156, 90], [170, 200], [30, 200]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 90], [60, 108], [54, 196], [30, 196]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 88, rx: 60, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 84, rx: 42, ry: 10, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[166, 120], [196, 124], [196, 140], [168, 138]], close: false, outline: 'INK.line', lineWidth: 5 },
      { op: 'speckle', x: 50, y: 120, w: 100, h: 70, count: 14, maxR: 2, alpha: 0.28, color: 'INK.grain' },
    ],
  },
  'hedgehog-passage-gate': {
    canvas: [256, 224],
    ops: [
      { op: 'path', points: [[24, 210], [24, 72], [232, 72], [232, 210], [176, 210], [176, 150], [160, 120], [120, 108], [98, 120], [80, 150], [80, 210]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[24, 72], [232, 72], [232, 90], [24, 90]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 24, y0: 138, x1: 80, y1: 138, width: 2, color: 'shade' },
      { op: 'line', x0: 176, y0: 138, x1: 232, y1: 138, width: 2, color: 'shade' },
      { op: 'line', x0: 24, y0: 172, x1: 80, y1: 172, width: 2, color: 'shade' },
      { op: 'line', x0: 176, y0: 172, x1: 232, y1: 172, width: 2, color: 'shade' },
      { op: 'line', x0: 98, y0: 124, x1: 158, y1: 124, width: 3, color: 'INK.soft' },
    ],
  },
  'help-request-flapboard': {
    canvas: [224, 272],
    ops: [
      { op: 'line', x0: 54, y0: 256, x1: 58, y1: 150, width: 11, color: 'shade' },
      { op: 'line', x0: 170, y0: 256, x1: 166, y1: 150, width: 11, color: 'shade' },
      { op: 'rect', x: 36, y: 40, w: 152, h: 118, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 56, w: 54, h: 40, r: 5, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 118, y: 56, w: 54, h: 40, r: 5, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 52, y: 104, w: 54, h: 40, r: 5, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 118, y: 104, w: 54, h: 40, r: 5, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 52, y0: 76, x1: 106, y1: 76, width: 3, color: 'shade' },
      { op: 'line', x0: 118, y0: 124, x1: 172, y1: 124, width: 3, color: 'shade' },
      { op: 'blob', cx: 145, cy: 76, rx: 5, ry: 5, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'herb-chopping-trough': {
    canvas: [240, 224],
    ops: [
      { op: 'line', x0: 72, y0: 212, x1: 76, y1: 160, width: 11, color: 'shade' },
      { op: 'line', x0: 168, y0: 212, x1: 164, y1: 160, width: 11, color: 'shade' },
      { op: 'path', points: [[36, 108], [120, 140], [204, 108], [200, 164], [120, 180], [40, 164]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 128], [180, 128], [156, 108], [84, 108]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 120, y0: 64, x1: 120, y1: 110, width: 7, color: 'shade' },
      { op: 'line', x0: 94, y0: 62, x1: 146, y1: 62, width: 9, color: 'shade' },
      { op: 'line', x0: 120, y0: 146, x1: 120, y1: 172, width: 2, color: 'INK.soft' },
    ],
  },
  'herb-drying-dome': {
    canvas: [200, 256],
    ops: [
      { op: 'blob', cx: 100, cy: 224, rx: 70, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[34, 224], [34, 140], [60, 86], [100, 68], [140, 86], [166, 140], [166, 224]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 42, y0: 150, x1: 158, y1: 150, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 38, y0: 190, x1: 162, y1: 190, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 74, y0: 78, x1: 74, y1: 216, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 126, y0: 78, x1: 126, y1: 216, width: 2, color: 'INK.soft' },
      { op: 'path', points: [[100, 68], [100, 44], [118, 40]], close: false, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 118, cy: 40, rx: 6, ry: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'herb-snipping-carousel': {
    canvas: [224, 208],
    ops: [
      { op: 'blob', cx: 112, cy: 160, rx: 92, ry: 34, n: 14, irregularity: 0.04, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 150, rx: 34, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[28, 140], [34, 182], [68, 182], [74, 140]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[150, 140], [156, 182], [190, 182], [196, 140]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[88, 130], [94, 168], [130, 168], [136, 130]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'grass', x: 50, baseY: 140, height: 34, lean: -6, width: 4, color: 'SAGE.mid' },
      { op: 'grass', x: 172, baseY: 140, height: 34, lean: 6, width: 4, color: 'SAGE.deep' },
      { op: 'grass', x: 112, baseY: 130, height: 30, lean: 0, width: 4, color: 'SAGE.light' },
    ],
  },
  'herb-steeping-basket': {
    canvas: [200, 240],
    ops: [
      { op: 'path', points: [[52, 118], [148, 118], [136, 222], [64, 222]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 108], [160, 108], [150, 96], [50, 96]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[36, 118], [28, 86], [44, 86], [52, 118]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[164, 118], [172, 86], [156, 86], [148, 118]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 58, y0: 150, x1: 142, y1: 150, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 62, y0: 182, x1: 138, y1: 182, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 100, y0: 120, x1: 100, y1: 218, width: 2, color: 'shade' },
    ],
  },
  'hide-cloth-frame': {
    canvas: [224, 256],
    ops: [
      { op: 'path', points: [[44, 238], [52, 64], [64, 64], [60, 238]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[164, 238], [160, 64], [172, 64], [180, 238]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 58, w: 120, h: 14, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[68, 88], [112, 84], [110, 128], [66, 124]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[118, 118], [158, 122], [156, 170], [116, 162]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[64, 158], [108, 162], [106, 212], [62, 206]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 70, y0: 96, x1: 106, y1: 94, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 130, x1: 152, y1: 132, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 66, y0: 170, x1: 102, y1: 172, width: 2, color: 'INK.soft' },
    ],
  },
  'hinge-alignment-frame': {
    canvas: [224, 208],
    ops: [
      { op: 'rect', x: 36, y: 176, w: 152, h: 24, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[52, 176], [52, 48], [80, 48], [80, 148], [176, 148], [176, 176]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 92, y: 60, w: 80, h: 18, r: 4, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 100, y0: 68, x1: 160, y1: 68, width: 2, color: 'shade' },
      { op: 'blob', cx: 100, cy: 69, rx: 4, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 66, y0: 90, x1: 66, y1: 150, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 160, x1: 160, y1: 160, width: 2, color: 'INK.soft' },
    ],
  },
  'homecoming-token-rail': {
    canvas: [256, 176],
    ops: [
      { op: 'line', x0: 46, y0: 162, x1: 50, y1: 92, width: 10, color: 'shade' },
      { op: 'line', x0: 210, y0: 162, x1: 206, y1: 92, width: 10, color: 'shade' },
      { op: 'path', points: [[36, 158], [220, 158], [212, 140], [44, 140]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 40, y0: 92, x1: 216, y1: 92, width: 7, color: 'fill' },
      { op: 'blob', cx: 72, cy: 92, rx: 15, ry: 15, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 104, cy: 92, rx: 15, ry: 15, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 136, cy: 92, rx: 15, ry: 15, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 184, cy: 92, rx: 15, ry: 15, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 156, y0: 92, x1: 168, y1: 92, width: 2, color: 'INK.soft' },
    ],
  },
  'honey-drip-stand': {
    canvas: [200, 256],
    ops: [
      { op: 'line', x0: 56, y0: 236, x1: 58, y1: 48, width: 10, color: 'shade' },
      { op: 'path', points: [[58, 52], [140, 48], [142, 66], [60, 70]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 132, y0: 66, x1: 132, y1: 110, width: 4, color: 'shade' },
      { op: 'path', points: [[116, 110], [148, 110], [140, 140], [124, 140]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 132, cy: 158, rx: 7, ry: 11, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 132, cy: 210, rx: 52, ry: 16, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 132, cy: 206, rx: 34, ry: 9, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'hum-recording-horn': {
    canvas: [256, 224],
    ops: [
      { op: 'blob', cx: 96, cy: 206, rx: 54, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 138, w: 92, h: 62, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 98, cy: 138, rx: 40, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 76, y0: 138, x1: 76, y1: 196, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 98, y0: 138, x1: 98, y1: 196, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 138, x1: 120, y1: 196, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 140, y0: 120, x1: 210, y1: 120, width: 7, color: 'shade' },
      { op: 'path', points: [[208, 86], [228, 72], [236, 168], [208, 154]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 224, cy: 120, rx: 11, ry: 36, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
    ],
  },
  'ice-edge-warning-float': {
    canvas: [200, 256],
    ops: [
      { op: 'blob', cx: 100, cy: 218, rx: 56, ry: 30, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 190, x1: 100, y1: 120, width: 9, color: 'shade' },
      { op: 'path', points: [[100, 120], [156, 96], [150, 56], [100, 80]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[100, 120], [100, 80], [50, 56], [44, 96]], close: true, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 80, x1: 100, y1: 120, width: 3, color: 'INK.line' },
      { op: 'blob', cx: 100, cy: 118, rx: 7, ry: 7, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 48, y0: 236, x1: 152, y1: 236, width: 3, color: 'WATER.edge' },
    ],
  },
  'ice-lantern-mold': {
    canvas: [160, 272],
    ops: [
      { op: 'blob', cx: 80, cy: 256, rx: 52, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[36, 120], [124, 120], [136, 248], [24, 248]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[52, 128], [108, 128], [116, 240], [44, 240]], close: true, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 80, cy: 118, rx: 48, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[72, 118], [72, 72], [88, 72], [88, 118]], close: true, fill: 'PAPER.skyBottom', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 80, y0: 72, x1: 80, y1: 48, width: 4, color: 'shade' },
      { op: 'blob', cx: 80, cy: 44, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 60, y0: 180, x1: 100, y1: 180, width: 2, color: 'WATER.mid' },
    ],
  },
  'ink-drying-carousel': {
    canvas: [208, 224],
    ops: [
      { op: 'line', x0: 104, y0: 214, x1: 104, y1: 110, width: 10, color: 'shade' },
      { op: 'path', points: [[64, 210], [144, 210], [136, 220], [72, 220]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 96, rx: 66, ry: 62, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 96, rx: 16, ry: 15, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 104, y0: 80, x1: 104, y1: 36, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 118, y0: 90, x1: 160, y1: 74, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 90, y0: 108, x1: 52, y1: 140, width: 3, color: 'INK.soft' },
      { op: 'path', points: [[92, 24], [116, 24], [116, 46], [92, 46]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[160, 62], [182, 68], [176, 90], [154, 84]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[30, 128], [52, 134], [46, 156], [24, 150]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'insect-night-shield': {
    canvas: [176, 288],
    ops: [
      { op: 'line', x0: 88, y0: 274, x1: 88, y1: 150, width: 12, color: 'shade' },
      { op: 'blob', cx: 88, cy: 274, rx: 42, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[36, 116], [140, 116], [120, 156], [56, 156]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[36, 116], [140, 116], [100, 80], [76, 80]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'halo', cx: 88, cy: 162, r: 40 },
      { op: 'blob', cx: 88, cy: 156, rx: 18, ry: 8, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 60, y0: 98, x1: 116, y1: 98, width: 2, color: 'INK.soft' },
    ],
  },
  'insect-viewing-stage': {
    canvas: [200, 256],
    ops: [
      { op: 'path', points: [[64, 238], [58, 180], [142, 180], [136, 238]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 248, x1: 62, y1: 236, width: 10, color: 'shade' },
      { op: 'line', x0: 138, y0: 248, x1: 138, y1: 236, width: 10, color: 'shade' },
      { op: 'path', points: [[44, 182], [156, 182], [150, 166], [50, 166]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 52, y0: 166, x1: 52, y1: 58, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 148, y0: 166, x1: 148, y1: 58, width: 4, color: 'INK.soft' },
      { op: 'path', points: [[40, 56], [160, 56], [152, 38], [48, 38]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 120, rx: 40, ry: 48, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 100, cy: 140, rx: 12, ry: 8, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'jar-carry-frame': {
    canvas: [192, 256],
    ops: [
      { op: 'path', points: [[40, 52], [152, 52], [148, 72], [44, 72]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 52, y0: 72, x1: 56, y1: 234, width: 8, color: 'fill' },
      { op: 'line', x0: 140, y0: 72, x1: 136, y1: 234, width: 8, color: 'fill' },
      { op: 'path', points: [[44, 40], [148, 40], [148, 52], [44, 52]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 96, y0: 40, x1: 96, y1: 24, width: 4, color: 'INK.soft' },
      { op: 'path', points: [[58, 118], [90, 118], [88, 176], [62, 176]], close: true, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[104, 150], [136, 150], [134, 210], [108, 210]], close: true, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 62, y0: 150, x1: 86, y1: 150, width: 3, color: 'WATER.mid' },
      { op: 'line', x0: 108, y0: 182, x1: 132, y1: 182, width: 3, color: 'WATER.mid' },
    ],
  },
  'joint-warming-wrap': {
    canvas: [200, 224],
    ops: [
      { op: 'path', points: [[40, 210], [160, 210], [152, 150], [48, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 150, rx: 58, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[58, 142], [142, 142], [150, 96], [120, 80], [80, 80], [50, 96]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[80, 80], [120, 80], [114, 60], [86, 60]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 70, y0: 120, x1: 130, y1: 120, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 72, y0: 104, x1: 128, y1: 104, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 100, cy: 196, rx: 7, ry: 7, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'kettle-nest': {
    canvas: [224, 160],
    ops: [
      { op: 'blob', cx: 112, cy: 120, rx: 94, ry: 34, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 106, rx: 94, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 104, rx: 70, ry: 22, fill: 'fill', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 102, rx: 46, ry: 14, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 100, rx: 24, ry: 7, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 40, y0: 92, x1: 184, y1: 92, width: 2, color: 'INK.soft' },
    ],
  },
  'kindness-repair-basket': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[44, 78], [58, 166], [198, 166], [212, 78]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 78, rx: 86, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 62, y0: 104, x1: 196, y1: 104, width: 3, color: 'shade' },
      { op: 'line', x0: 68, y0: 132, x1: 190, y1: 132, width: 3, color: 'shade' },
      { op: 'path', points: [[60, 78], [80, 38], [100, 78]], close: true, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 118, y: 40, w: 40, h: 34, r: 6, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 184, cy: 58, rx: 16, ry: 14, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 138, cy: 56, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'kindness-token-fountain': {
    canvas: [224, 288],
    ops: [
      { op: 'path', points: [[60, 272], [68, 236], [156, 236], [164, 272]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 224, rx: 92, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 156, rx: 66, ry: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 100, rx: 40, ry: 14, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 156, x1: 112, y1: 224, width: 6, color: 'shade' },
      { op: 'line', x0: 112, y0: 100, x1: 112, y1: 156, width: 6, color: 'shade' },
      { op: 'blob', cx: 84, cy: 218, rx: 10, ry: 6, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 2 },
      { op: 'blob', cx: 138, cy: 220, rx: 10, ry: 6, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 152, rx: 10, ry: 6, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'lake-breeze-dryer': {
    canvas: [200, 256],
    ops: [
      { op: 'line', x0: 68, y0: 150, x1: 62, y1: 244, width: 10, color: 'shade' },
      { op: 'line', x0: 132, y0: 150, x1: 138, y1: 244, width: 10, color: 'shade' },
      { op: 'rect', x: 40, y: 44, w: 120, h: 110, r: 16, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 56, y0: 44, x1: 56, y1: 154, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 80, y0: 44, x1: 80, y1: 154, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 44, x1: 104, y1: 154, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 44, x1: 128, y1: 154, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 44, y0: 78, x1: 156, y1: 78, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 44, y0: 118, x1: 156, y1: 118, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 100, y0: 99, x1: 150, y1: 99, width: 5, color: 'shade' },
      { op: 'blob', cx: 156, cy: 99, rx: 11, ry: 10, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'lamp-cleaning-perch': {
    canvas: [176, 256],
    ops: [
      { op: 'line', x0: 96, y0: 244, x1: 96, y1: 40, width: 13, color: 'shade' },
      { op: 'blob', cx: 96, cy: 34, rx: 22, ry: 16, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 150], [148, 150], [148, 168], [44, 168]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 54, y0: 168, x1: 58, y1: 242, width: 8, color: 'shade' },
      { op: 'line', x0: 138, y0: 168, x1: 134, y1: 242, width: 8, color: 'shade' },
      { op: 'line', x0: 110, y0: 96, x1: 138, y1: 96, width: 5, color: 'shade' },
      { op: 'line', x0: 138, y0: 96, x1: 138, y1: 112, width: 4, color: 'shade' },
      { op: 'line', x0: 48, y0: 158, x1: 144, y1: 158, width: 2, color: 'INK.soft' },
    ],
  },
  'lampwick-trimming-dock': {
    canvas: [208, 256],
    ops: [
      { op: 'rect', x: 40, y: 196, w: 128, h: 44, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 68, y0: 196, x1: 68, y1: 92, width: 7, color: 'shade' },
      { op: 'path', points: [[58, 92], [88, 80], [88, 104], [58, 116]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 104, y0: 196, x1: 120, y1: 108, width: 3, color: 'shade' },
      { op: 'line', x0: 120, y0: 196, x1: 104, y1: 108, width: 3, color: 'shade' },
      { op: 'blob', cx: 104, cy: 104, rx: 7, ry: 7, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 120, cy: 104, rx: 7, ry: 7, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 132, y: 160, w: 40, h: 36, r: 10, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 152, cy: 166, rx: 15, ry: 5, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'lantern-carry-yoke': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[40, 52], [128, 28], [216, 52], [214, 68], [128, 46], [42, 68]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 60, x1: 48, y1: 92, width: 3, color: 'shade' },
      { op: 'line', x0: 208, y0: 60, x1: 208, y1: 92, width: 3, color: 'shade' },
      { op: 'path', points: [[34, 92], [62, 92], [56, 128], [40, 128]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[194, 92], [222, 92], [216, 128], [200, 128]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 48, cy: 112, rx: 4, ry: 4, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 208, cy: 112, rx: 4, ry: 4, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 100, y0: 40, x1: 156, y1: 40, width: 2, color: 'shade' },
      { op: 'blob', cx: 128, cy: 130, rx: 94, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'lantern-floating-basin': {
    canvas: [200, 256],
    ops: [
      { op: 'path', points: [[60, 176], [56, 232], [144, 232], [140, 176]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 150, rx: 86, ry: 30, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 144, rx: 70, ry: 18, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 58, y0: 140, x1: 62, y1: 110, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 100, y0: 138, x1: 100, y1: 104, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 142, y0: 140, x1: 138, y1: 110, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 62, cy: 104, rx: 11, ry: 12, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 98, rx: 12, ry: 13, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 138, cy: 104, rx: 11, ry: 12, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'lap-blanket-basket': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[44, 76], [212, 76], [196, 162], [60, 162]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 74, rx: 86, ry: 17, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[78, 72], [150, 52], [176, 60], [148, 70]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 62, y0: 104, x1: 196, y1: 104, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 60, y0: 132, x1: 196, y1: 132, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 80, x1: 100, y1: 158, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 152, y0: 80, x1: 156, y1: 158, width: 2, color: 'INK.soft' },
    ],
  },
  'leaf-mold-cage': {
    canvas: [208, 256],
    ops: [
      { op: 'blob', cx: 104, cy: 228, rx: 76, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[36, 76], [42, 228], [166, 228], [172, 76]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 76, rx: 68, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 62, y0: 80, x1: 64, y1: 224, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 78, x1: 104, y1: 226, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 146, y0: 80, x1: 144, y1: 224, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 40, y0: 118, x1: 168, y1: 118, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 42, y0: 166, x1: 166, y1: 166, width: 3, color: 'INK.soft' },
      { op: 'path', points: [[146, 96], [182, 88], [180, 150], [144, 160]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[78, 140], [90, 128], [102, 140], [88, 156]], close: true, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'leaf-press-book': {
    canvas: [200, 208],
    ops: [
      { op: 'path', points: [[36, 150], [164, 142], [170, 190], [40, 198]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[34, 116], [166, 108], [168, 150], [36, 158]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[34, 82], [166, 74], [168, 116], [34, 124]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[32, 60], [168, 52], [170, 82], [34, 90]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 98, x1: 120, y1: 94, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 56, y0: 48, x1: 58, y1: 196, width: 7, color: 'shade' },
      { op: 'blob', cx: 56, cy: 44, rx: 13, ry: 11, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 144, y0: 52, x1: 146, y1: 196, width: 7, color: 'shade' },
      { op: 'blob', cx: 146, cy: 48, rx: 13, ry: 11, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'leaf-rustle-frame': {
    canvas: [224, 256],
    ops: [
      { op: 'line', x0: 48, y0: 244, x1: 50, y1: 56, width: 12, color: 'shade' },
      { op: 'line', x0: 176, y0: 244, x1: 174, y1: 56, width: 12, color: 'shade' },
      { op: 'path', points: [[40, 52], [184, 52], [184, 76], [40, 76]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 54, y: 76, w: 116, h: 150, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 86, cy: 120, rx: 16, ry: 11, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 132, cy: 150, rx: 15, ry: 10, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 92, cy: 182, rx: 14, ry: 10, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 62, y0: 100, x1: 62, y1: 216, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 162, y0: 100, x1: 162, y1: 216, width: 2, color: 'INK.soft' },
    ],
  },
  'leaf-vein-lightbox': {
    canvas: [200, 288],
    ops: [
      { op: 'path', points: [[44, 272], [40, 120], [160, 120], [156, 272]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 274, rx: 60, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 124], [156, 124], [150, 40], [50, 40]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[56, 118], [148, 118], [144, 50], [60, 50]], close: true, fill: 'PAPER.skyTop', outline: 'INK.soft', lineWidth: 2 },
      { op: 'halo', cx: 100, cy: 84, r: 44 },
      { op: 'path', points: [[72, 108], [88, 60], [128, 58], [136, 100], [100, 112]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 104, y0: 110, x1: 110, y1: 60, width: 3, color: 'SAGE.deep' },
      { op: 'line', x0: 106, y0: 88, x1: 80, y1: 86, width: 2, color: 'SAGE.deep' },
      { op: 'line', x0: 108, y0: 76, x1: 130, y1: 70, width: 2, color: 'SAGE.deep' },
    ],
  },
  'listening-pillow': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[28, 120], [228, 120], [238, 146], [18, 146]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 54, y0: 132, x1: 202, y1: 132, width: 2, color: 'INK.soft' },
      { op: 'path', points: [[44, 118], [212, 118], [206, 72], [50, 72]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 128, cy: 88, rx: 72, ry: 24, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 86, rx: 50, ry: 15, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 62, y0: 98, x1: 58, y1: 116, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 194, y0: 98, x1: 198, y1: 116, width: 2, color: 'INK.soft' },
    ],
  },
  'litter-sort-sled': {
    canvas: [208, 224],
    ops: [
      { op: 'line', x0: 104, y0: 118, x1: 104, y1: 40, width: 9, color: 'shade' },
      { op: 'line', x0: 80, y0: 40, x1: 128, y1: 40, width: 7, color: 'shade' },
      { op: 'path', points: [[34, 118], [174, 118], [160, 180], [48, 180]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 118, x1: 104, y1: 180, width: 4, color: 'INK.line' },
      { op: 'line', x0: 42, y0: 180, x1: 48, y1: 210, width: 8, color: 'shade' },
      { op: 'line', x0: 166, y0: 180, x1: 160, y1: 210, width: 8, color: 'shade' },
      { op: 'line', x0: 40, y0: 210, x1: 170, y1: 210, width: 7, color: 'shade' },
      { op: 'line', x0: 40, y0: 128, x1: 168, y1: 128, width: 2, color: 'INK.soft' },
    ],
  },
  'lost-found-story-shelf': {
    canvas: [256, 184],
    ops: [
      { op: 'rect', x: 30, y: 36, w: 196, h: 138, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 30, y0: 106, x1: 226, y1: 106, width: 6, color: 'shade' },
      { op: 'line', x0: 95, y0: 40, x1: 95, y1: 170, width: 5, color: 'shade' },
      { op: 'line', x0: 161, y0: 40, x1: 161, y1: 170, width: 5, color: 'shade' },
      { op: 'blob', cx: 62, cy: 78, rx: 18, ry: 18, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[128, 96], [128, 58], [156, 58], [156, 96]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 172, y: 120, w: 42, h: 30, r: 4, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 180, y0: 132, x1: 206, y1: 132, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 180, y0: 140, x1: 206, y1: 140, width: 2, color: 'INK.soft' },
    ],
  },
  'lunch-wrapping-press': {
    canvas: [240, 224],
    ops: [
      { op: 'line', x0: 72, y0: 212, x1: 78, y1: 160, width: 11, color: 'shade' },
      { op: 'line', x0: 168, y0: 212, x1: 162, y1: 160, width: 11, color: 'shade' },
      { op: 'rect', x: 40, y: 138, w: 160, h: 26, r: 5, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[46, 138], [58, 88], [182, 88], [194, 138]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 48, cy: 136, rx: 7, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 78, y0: 110, x1: 162, y1: 110, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 92, x1: 120, y1: 134, width: 3, color: 'SAGE.mid' },
    ],
  },
  'magnet-find-tray': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 34, y: 70, w: 188, h: 74, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 150, x1: 48, y1: 140, width: 10, color: 'shade' },
      { op: 'line', x0: 208, y0: 150, x1: 208, y1: 140, width: 10, color: 'shade' },
      { op: 'rect', x: 44, y: 80, w: 168, h: 30, r: 6, fill: 'WATER.sand', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 92, y: 54, w: 56, h: 34, r: 8, fill: 'SAGE.deep', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 54, x1: 104, y1: 78, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 136, y0: 54, x1: 136, y1: 78, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 148, y0: 40, x1: 196, y1: 40, width: 5, color: 'shade' },
      { op: 'line', x0: 196, y0: 40, x1: 196, y1: 54, width: 5, color: 'shade' },
    ],
  },
  'maintenance-treat-drawer': {
    canvas: [208, 224],
    ops: [
      { op: 'rect', x: 48, y: 96, w: 112, h: 114, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 150, x1: 160, y1: 150, width: 3, color: 'INK.soft' },
      { op: 'rect', x: 60, y: 156, w: 128, h: 46, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 176, cy: 179, rx: 9, ry: 12, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[64, 96], [144, 96], [136, 68], [72, 68]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 120, x1: 128, y1: 120, width: 2, color: 'INK.soft' },
    ],
  },
  'maker-cleanup-cart': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 36, y: 56, w: 184, h: 92, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 76, w: 56, h: 56, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 120, y: 76, w: 56, h: 56, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 70, cy: 152, rx: 15, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 186, cy: 152, rx: 15, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 188, y0: 56, x1: 196, y1: 30, width: 6, color: 'CLAY.deep' },
      { op: 'path', points: [[180, 30], [212, 30], [208, 46], [184, 46]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 70, y0: 92, x1: 90, y1: 92, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 138, y0: 92, x1: 158, y1: 92, width: 2, color: 'INK.soft' },
    ],
  },
  'map-folding-board': {
    canvas: [256, 168],
    ops: [
      { op: 'path', points: [[20, 40], [124, 36], [122, 150], [18, 154]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[132, 36], [236, 40], [238, 154], [134, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 128, y0: 34, x1: 128, y1: 154, width: 7, color: 'shade' },
      { op: 'line', x0: 24, y0: 76, x1: 120, y1: 74, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 26, y0: 112, x1: 120, y1: 112, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 136, y0: 74, x1: 232, y1: 76, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 68, y0: 40, x1: 68, y1: 150, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 182, y0: 40, x1: 182, y1: 150, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 92, cy: 96, rx: 18, ry: 11, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'map-weather-cover': {
    canvas: [224, 288],
    ops: [
      { op: 'line', x0: 64, y0: 280, x1: 72, y1: 196, width: 12, color: 'shade' },
      { op: 'line', x0: 160, y0: 280, x1: 152, y1: 196, width: 12, color: 'shade' },
      { op: 'rect', x: 40, y: 48, w: 144, h: 150, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'rect', x: 56, y: 66, w: 112, h: 116, r: 8, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 112, cy: 50, rx: 74, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 72, y0: 104, x1: 150, y1: 128, width: 3, color: 'SAGE.deep' },
      { op: 'line', x0: 150, y0: 128, x1: 84, y1: 156, width: 3, color: 'SAGE.deep' },
      { op: 'blob', cx: 112, cy: 50, rx: 8, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'marker-post-driver': {
    canvas: [160, 256],
    ops: [
      { op: 'line', x0: 80, y0: 244, x1: 80, y1: 40, width: 10, color: 'shade' },
      { op: 'rect', x: 44, y: 70, w: 72, h: 58, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 86, x1: 40, y1: 120, width: 7, color: 'shade' },
      { op: 'line', x0: 120, y0: 86, x1: 120, y1: 120, width: 7, color: 'shade' },
      { op: 'line', x0: 80, y0: 40, x1: 80, y1: 18, width: 7, color: 'shade' },
      { op: 'blob', cx: 80, cy: 244, rx: 26, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 54, y0: 98, x1: 106, y1: 98, width: 2, color: 'INK.soft' },
    ],
  },
  'meal-cover-cloche': {
    canvas: [224, 240],
    ops: [
      { op: 'blob', cx: 112, cy: 216, rx: 86, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[34, 212], [44, 120], [112, 72], [180, 120], [190, 212]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 56, y0: 140, x1: 168, y1: 140, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 46, y0: 176, x1: 178, y1: 176, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 72, y0: 96, x1: 80, y1: 204, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 152, y0: 96, x1: 144, y1: 204, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 72, x1: 112, y1: 48, width: 6, color: 'shade' },
      { op: 'blob', cx: 112, cy: 44, rx: 12, ry: 10, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'memory-caption-box': {
    canvas: [208, 200],
    ops: [
      { op: 'rect', x: 36, y: 60, w: 136, h: 126, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[36, 182], [172, 182], [164, 192], [44, 192]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 48, y: 120, w: 140, h: 52, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[80, 72], [96, 72], [96, 108], [80, 108]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[112, 72], [128, 72], [128, 108], [112, 108]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 90, rx: 5, ry: 5, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 120, cy: 90, rx: 5, ry: 5, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 118, cy: 146, rx: 9, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'memory-object-plinth': {
    canvas: [192, 224],
    ops: [
      { op: 'blob', cx: 96, cy: 204, rx: 54, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[60, 96], [132, 96], [140, 196], [52, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 96, cy: 94, rx: 52, ry: 14, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 96, cy: 88, rx: 40, ry: 11, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[84, 86], [108, 86], [104, 44], [90, 44]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 72, y: 150, w: 48, h: 18, r: 3, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 80, y0: 159, x1: 112, y1: 159, width: 3, color: 'INK.soft' },
    ],
  },
  'memory-parade-cart': {
    canvas: [256, 224],
    ops: [
      { op: 'rect', x: 40, y: 110, w: 176, h: 62, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 110, x1: 48, y1: 52, width: 5, color: 'shade' },
      { op: 'line', x0: 208, y0: 110, x1: 208, y1: 52, width: 5, color: 'shade' },
      { op: 'line', x0: 48, y0: 56, x1: 208, y1: 56, width: 4, color: 'shade' },
      { op: 'blob', cx: 92, cy: 86, rx: 15, ry: 15, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[140, 98], [156, 66], [172, 98]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 78, cy: 190, rx: 22, ry: 22, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 178, cy: 190, rx: 22, ry: 22, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 78, cy: 190, rx: 7, ry: 7, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 178, cy: 190, rx: 7, ry: 7, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'memory-scent-box': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 28, y: 40, w: 200, h: 126, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 42, y: 54, w: 80, h: 46, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 134, y: 54, w: 80, h: 46, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 42, y: 108, w: 172, h: 46, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 58, y0: 68, x1: 106, y1: 68, width: 3, color: 'INK.grain' },
      { op: 'line', x0: 58, y0: 80, x1: 106, y1: 80, width: 3, color: 'INK.grain' },
      { op: 'line', x0: 150, y0: 68, x1: 198, y1: 68, width: 3, color: 'INK.grain' },
      { op: 'line', x0: 150, y0: 80, x1: 198, y1: 80, width: 3, color: 'INK.grain' },
      { op: 'blob', cx: 128, cy: 131, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'memory-thread-loom': {
    canvas: [200, 208],
    ops: [
      { op: 'path', points: [[40, 196], [40, 40], [60, 40], [60, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[140, 196], [140, 40], [160, 40], [160, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 54, x1: 160, y1: 54, width: 6, color: 'shade' },
      { op: 'line', x0: 40, y0: 182, x1: 160, y1: 182, width: 6, color: 'shade' },
      { op: 'line', x0: 72, y0: 54, x1: 72, y1: 182, width: 4, color: 'SAGE.mid' },
      { op: 'line', x0: 90, y0: 54, x1: 90, y1: 182, width: 4, color: 'CLAY.deep' },
      { op: 'line', x0: 108, y0: 54, x1: 108, y1: 182, width: 4, color: 'SAGE.deep' },
      { op: 'line', x0: 126, y0: 54, x1: 126, y1: 182, width: 4, color: 'CLAY.light' },
      { op: 'line', x0: 58, y0: 118, x1: 142, y1: 118, width: 5, color: 'WATER.edge' },
    ],
  },
  'memory-weather-vane': {
    canvas: [200, 208],
    ops: [
      { op: 'rect', x: 76, y: 150, w: 48, h: 48, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 150, x1: 100, y1: 56, width: 9, color: 'shade' },
      { op: 'blob', cx: 100, cy: 92, rx: 58, ry: 56, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 92, rx: 42, ry: 40, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[100, 92], [138, 72], [120, 98]], close: true, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 48, rx: 7, ry: 7, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 146, cy: 92, rx: 7, ry: 7, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 54, cy: 92, rx: 7, ry: 7, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 100, cy: 92, rx: 6, ry: 6, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'minnow-shadow-screen': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[28, 52], [228, 48], [230, 96], [30, 100]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 104, y: 58, w: 48, h: 36, r: 6, fill: 'WATER.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 58, y0: 52, x1: 58, y1: 98, width: 2, color: 'shade' },
      { op: 'line', x0: 84, y0: 52, x1: 84, y1: 98, width: 2, color: 'shade' },
      { op: 'line', x0: 172, y0: 52, x1: 172, y1: 98, width: 2, color: 'shade' },
      { op: 'line', x0: 198, y0: 52, x1: 198, y1: 98, width: 2, color: 'shade' },
      { op: 'blob', cx: 52, cy: 118, rx: 18, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 204, cy: 118, rx: 18, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 30, y0: 138, x1: 228, y1: 134, width: 3, color: 'WATER.edge' },
    ],
  },
  'mirror-light-shelf': {
    canvas: [208, 288],
    ops: [
      { op: 'halo', cx: 104, cy: 216, r: 40 },
      { op: 'line', x0: 104, y0: 248, x1: 104, y1: 196, width: 10, color: 'shade' },
      { op: 'rect', x: 56, y: 196, w: 96, h: 18, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 196], [148, 196], [170, 72], [82, 72]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 92, y0: 92, x1: 150, y1: 92, width: 2, color: 'shade' },
      { op: 'line', x0: 88, y0: 140, x1: 150, y1: 140, width: 2, color: 'shade' },
      { op: 'line', x0: 104, y0: 196, x1: 104, y1: 176, width: 5, color: 'shade' },
      { op: 'blob', cx: 104, cy: 172, rx: 6, ry: 10, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 104, cy: 248, rx: 48, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'moon-angle-quadrant': {
    canvas: [208, 224],
    ops: [
      { op: 'line', x0: 68, y0: 210, x1: 66, y1: 70, width: 11, color: 'shade' },
      { op: 'path', points: [[34, 212], [100, 212], [88, 196], [46, 196]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 60], [156, 62], [150, 84], [136, 112], [112, 138], [84, 152], [60, 156], [60, 60]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[56, 56], [160, 58], [160, 68], [56, 66]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 150, y0: 78, x1: 140, y1: 74, width: 2.5, color: 'INK.soft' },
      { op: 'line', x0: 134, y0: 108, x1: 126, y1: 100, width: 2.5, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 134, x1: 98, y1: 124, width: 2.5, color: 'INK.soft' },
      { op: 'line', x0: 62, y0: 64, x1: 98, y1: 150, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 100, cy: 156, rx: 9, ry: 11, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'moon-phase-nesting-boxes': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 28, y: 68, w: 56, h: 80, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 92, y: 88, w: 44, h: 60, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 144, y: 104, w: 36, h: 44, r: 9, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 188, y: 116, w: 28, h: 32, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 56, cy: 78, rx: 18, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 108], [60, 98], [68, 118], [52, 128]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[104, 118], [116, 110], [120, 128], [108, 134]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 128, cy: 150, rx: 104, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'moon-viewing-tray': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 86, y0: 160, x1: 80, y1: 210, width: 7, color: 'shade' },
      { op: 'line', x0: 138, y0: 160, x1: 144, y1: 210, width: 7, color: 'shade' },
      { op: 'blob', cx: 112, cy: 210, rx: 56, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 112, cy: 118, rx: 92, ry: 40, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 112, rx: 48, ry: 18, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 50, cy: 112, rx: 13, ry: 10, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 174, cy: 112, rx: 13, ry: 10, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 80, cy: 134, rx: 12, ry: 9, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 146, cy: 134, rx: 12, ry: 9, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'morning-status-bell': {
    canvas: [224, 224],
    ops: [
      { op: 'path', points: [[40, 210], [184, 210], [172, 170], [52, 170]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 70, cy: 150, rx: 30, ry: 26, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 124, cy: 150, rx: 30, ry: 26, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 178, cy: 150, rx: 30, ry: 26, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 70, cy: 150, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 124, cy: 150, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 178, cy: 150, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'moss-moisture-box': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 24, y: 72, w: 208, h: 72, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 150, x1: 40, y1: 140, width: 10, color: 'shade' },
      { op: 'line', x0: 216, y0: 150, x1: 216, y1: 140, width: 10, color: 'shade' },
      { op: 'line', x0: 128, y0: 74, x1: 128, y1: 142, width: 4, color: 'INK.line' },
      { op: 'blob', cx: 76, cy: 98, rx: 40, ry: 18, fill: 'SAGE.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 180, cy: 98, rx: 40, ry: 18, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 50, y0: 128, x1: 62, y1: 128, width: 5, color: 'shade' },
      { op: 'line', x0: 80, y0: 128, x1: 92, y1: 128, width: 5, color: 'shade' },
      { op: 'line', x0: 164, y0: 128, x1: 176, y1: 128, width: 5, color: 'shade' },
      { op: 'line', x0: 194, y0: 128, x1: 206, y1: 128, width: 5, color: 'shade' },
    ],
  },
  'moth-rest-bark-board': {
    canvas: [176, 256],
    ops: [
      { op: 'line', x0: 62, y0: 244, x1: 62, y1: 210, width: 12, color: 'shade' },
      { op: 'line', x0: 114, y0: 244, x1: 114, y1: 210, width: 12, color: 'shade' },
      { op: 'path', points: [[46, 40], [130, 36], [134, 214], [42, 218]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 66, y0: 52, x1: 68, y1: 206, width: 3, color: 'shade' },
      { op: 'line', x0: 90, y0: 48, x1: 91, y1: 204, width: 3, color: 'shade' },
      { op: 'line', x0: 112, y0: 50, x1: 113, y1: 206, width: 3, color: 'shade' },
      { op: 'line', x0: 54, y0: 56, x1: 55, y1: 200, width: 2, color: 'INK.soft' },
      { op: 'speckle', x: 50, y: 60, w: 78, h: 140, count: 16, maxR: 1.3, alpha: 0.12, color: 'INK.grain' },
    ],
  },
  'mud-boot-scraper': {
    canvas: [200, 224],
    ops: [
      { op: 'rect', x: 40, y: 158, w: 120, h: 60, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 56, y: 176, w: 90, h: 30, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 100, y0: 190, x1: 132, y1: 190, width: 4, color: 'INK.line' },
      { op: 'path', points: [[46, 158], [54, 70], [100, 46], [146, 70], [154, 158]], close: false, outline: 'INK.line', lineWidth: 6 },
      { op: 'line', x0: 62, y0: 150, x1: 60, y1: 96, width: 3, color: 'shade' },
      { op: 'line', x0: 84, y0: 150, x1: 82, y1: 78, width: 3, color: 'shade' },
      { op: 'line', x0: 116, y0: 150, x1: 118, y1: 78, width: 3, color: 'shade' },
      { op: 'line', x0: 138, y0: 150, x1: 140, y1: 96, width: 3, color: 'shade' },
    ],
  },
  'mud-depth-sandal': {
    canvas: [256, 112],
    ops: [
      { op: 'line', x0: 36, y0: 36, x1: 176, y1: 80, width: 9, color: 'shade' },
      { op: 'blob', cx: 40, cy: 34, rx: 13, ry: 11, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[156, 72], [244, 86], [238, 104], [150, 90]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'line', x0: 160, y0: 82, x1: 236, y1: 94, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 176, y0: 80, x1: 172, y1: 74, width: 5, color: 'shade' },
      { op: 'line', x0: 196, y0: 83, x1: 192, y1: 77, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 216, y0: 86, x1: 212, y1: 80, width: 4, color: 'INK.soft' },
    ],
  },
  'mudroom-drop-rail': {
    canvas: [192, 256],
    ops: [
      { op: 'path', points: [[36, 44], [156, 44], [156, 64], [36, 64]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[56, 64], [52, 90], [68, 90], [62, 64]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[96, 64], [92, 90], [108, 90], [102, 64]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[136, 64], [132, 90], [148, 90], [142, 64]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 60, cy: 108, rx: 18, ry: 20, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[36, 150], [156, 150], [150, 196], [42, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 196], [132, 196], [126, 236], [66, 236]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 50, y0: 172, x1: 140, y1: 172, width: 3, color: 'INK.soft' },
    ],
  },
  'mulch-chopping-box': {
    canvas: [224, 224],
    ops: [
      { op: 'rect', x: 40, y: 96, w: 120, h: 116, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[36, 96], [164, 96], [152, 72], [48, 72]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 150, y0: 80, x1: 210, y1: 40, width: 7, color: 'shade' },
      { op: 'blob', cx: 210, cy: 38, rx: 9, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 56, y: 166, w: 92, h: 40, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 92, y0: 186, x1: 112, y1: 186, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 60, y0: 120, x1: 60, y1: 156, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 140, y0: 120, x1: 140, y1: 156, width: 2, color: 'INK.soft' },
    ],
  },
  'mushroom-shade-log': {
    canvas: [256, 176],
    ops: [
      { op: 'blob', cx: 128, cy: 140, rx: 104, ry: 24, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 40, y: 72, w: 176, h: 64, r: 32, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 48, cy: 104, rx: 18, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 48, cy: 104, rx: 9, ry: 15, fill: 'INK.soft', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 110, y0: 76, x1: 118, y1: 132, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 160, y0: 74, x1: 166, y1: 134, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 150, cy: 74, rx: 14, ry: 9, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 150, y0: 78, x1: 150, y1: 92, width: 4, color: 'shade' },
    ],
  },
  'nature-rubbing-board': {
    canvas: [208, 248],
    ops: [
      { op: 'path', points: [[40, 236], [168, 236], [160, 246], [48, 246]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 70], [164, 70], [170, 236], [38, 236]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 86], [148, 86], [150, 210], [58, 210]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[104, 100], [120, 140], [112, 180], [96, 178], [88, 140]], close: true, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 104, y0: 104, x1: 104, y1: 176, width: 2, color: 'SAGE.deep' },
      { op: 'rect', x: 40, y: 52, w: 128, h: 22, r: 7, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 68, y0: 52, x1: 68, y1: 36, width: 6, color: 'CLAY.deep' },
      { op: 'line', x0: 140, y0: 52, x1: 140, y1: 36, width: 6, color: 'CLAY.deep' },
    ],
  },
  'neighbor-gift-hatch': {
    canvas: [256, 168],
    ops: [
      { op: 'rect', x: 36, y: 36, w: 184, h: 124, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 128, y0: 40, x1: 128, y1: 156, width: 4, color: 'shade' },
      { op: 'path', points: [[50, 150], [50, 86], [120, 86], [120, 124], [88, 150]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 120, y0: 86, x1: 88, y1: 150, width: 3, color: 'INK.soft' },
      { op: 'rect', x: 138, y: 62, w: 68, h: 40, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 172, cy: 124, rx: 18, ry: 7, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 110, cy: 102, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'neighbor-message-wheel': {
    canvas: [208, 208],
    ops: [
      { op: 'line', x0: 104, y0: 200, x1: 104, y1: 118, width: 12, color: 'shade' },
      { op: 'blob', cx: 104, cy: 200, rx: 34, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 104, cy: 92, rx: 76, ry: 74, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[104, 28], [126, 46], [104, 64], [82, 46]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[168, 92], [150, 110], [132, 92], [150, 74]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[104, 156], [82, 138], [104, 120], [126, 138]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[40, 92], [58, 74], [76, 92], [58, 110]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 104, cy: 92, rx: 14, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 104, cy: 92, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'neighbor-recipe-pot': {
    canvas: [224, 288],
    ops: [
      { op: 'path', points: [[50, 156], [58, 256], [166, 256], [174, 156]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 152, rx: 70, ry: 20, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 148, rx: 52, ry: 12, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 60, cy: 110, rx: 15, ry: 14, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 94, rx: 15, ry: 14, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 164, cy: 110, rx: 15, ry: 14, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 62, y0: 118, x1: 78, y1: 140, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 106, x1: 112, y1: 138, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 162, y0: 118, x1: 146, y1: 140, width: 2, color: 'INK.soft' },
    ],
  },
  'nest-distance-marker': {
    canvas: [224, 256],
    ops: [
      { op: 'line', x0: 56, y0: 244, x1: 56, y1: 110, width: 11, color: 'shade' },
      { op: 'blob', cx: 56, cy: 244, rx: 30, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 168, y0: 244, x1: 168, y1: 110, width: 11, color: 'shade' },
      { op: 'blob', cx: 168, cy: 244, rx: 30, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 68], [180, 64], [182, 116], [46, 120]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 56, y0: 150, x1: 168, y1: 150, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 68, y0: 92, x1: 156, y1: 90, width: 3, color: 'shade' },
      { op: 'blob', cx: 150, cy: 80, rx: 8, ry: 7, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'new-walk-bell': {
    canvas: [192, 224],
    ops: [
      { op: 'path', points: [[58, 200], [134, 200], [140, 214], [52, 214]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 200, x1: 66, y1: 56, width: 10, color: 'shade' },
      { op: 'path', points: [[60, 52], [150, 44], [150, 58], [62, 66]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 120, y0: 58, x1: 120, y1: 78, width: 5, color: 'shade' },
      { op: 'blob', cx: 120, cy: 118, rx: 42, ry: 40, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[80, 150], [160, 150], [166, 172], [150, 176], [120, 178], [90, 176], [74, 172]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 120, y0: 130, x1: 120, y1: 182, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 120, cy: 186, rx: 8, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 120, cy: 80, rx: 6, ry: 6, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'night-bloom-spotlight': {
    canvas: [208, 288],
    ops: [
      { op: 'halo', cx: 140, cy: 210, r: 44 },
      { op: 'line', x0: 64, y0: 272, x1: 64, y1: 140, width: 9, color: 'shade' },
      { op: 'path', points: [[56, 140], [100, 116], [124, 148], [88, 168]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[100, 140], [122, 128], [120, 150], [100, 158]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 144, rx: 4, ry: 4, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 140, y0: 256, x1: 140, y1: 210, width: 4, color: 'SAGE.deep' },
      { op: 'blob', cx: 140, cy: 204, rx: 16, ry: 14, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 140, cy: 204, rx: 6, ry: 6, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 96, cy: 270, rx: 74, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'night-compass-disc': {
    canvas: [224, 176],
    ops: [
      { op: 'rect', x: 44, y: 104, w: 136, h: 56, r: 14, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 92, rx: 58, ry: 50, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 92, rx: 42, ry: 36, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[112, 58], [122, 92], [112, 100], [102, 92]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[112, 126], [122, 92], [102, 92]], close: true, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 92, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 158, rx: 70, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'night-insect-sheet': {
    canvas: [200, 288],
    ops: [
      { op: 'line', x0: 52, y0: 272, x1: 52, y1: 40, width: 7, color: 'shade' },
      { op: 'line', x0: 148, y0: 272, x1: 148, y1: 40, width: 7, color: 'shade' },
      { op: 'blob', cx: 52, cy: 274, rx: 24, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 148, cy: 274, rx: 24, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 50, y0: 44, x1: 150, y1: 44, width: 6, color: 'shade' },
      { op: 'path', points: [[56, 52], [144, 52], [148, 236], [100, 250], [52, 236]], close: true, fill: 'PAPER.skyTop', outline: 'INK.line', lineWidth: 5 },
      { op: 'halo', cx: 100, cy: 150, r: 58 },
      { op: 'blob', cx: 100, cy: 150, rx: 18, ry: 18, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 80, cy: 118, rx: 4, ry: 4, fill: 'INK.soft', outline: 'INK.soft', lineWidth: 1 },
      { op: 'blob', cx: 124, cy: 190, rx: 4, ry: 4, fill: 'INK.soft', outline: 'INK.soft', lineWidth: 1 },
    ],
  },
  'night-tea-warmer': {
    canvas: [208, 256],
    ops: [
      { op: 'halo', cx: 104, cy: 196, r: 40 },
      { op: 'rect', x: 52, y: 120, w: 104, h: 120, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 48, y: 108, w: 112, h: 20, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[86, 108], [122, 108], [126, 80], [82, 80]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[122, 86], [140, 90], [136, 100], [124, 98]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 60, y: 176, w: 88, h: 40, r: 8, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 104, cy: 196, rx: 18, ry: 9, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 76, y0: 196, x1: 132, y1: 196, width: 2, color: 'shade' },
    ],
  },
  'noodle-drying-frame': {
    canvas: [208, 256],
    ops: [
      { op: 'line', x0: 56, y0: 244, x1: 80, y1: 56, width: 11, color: 'shade' },
      { op: 'line', x0: 152, y0: 244, x1: 128, y1: 56, width: 11, color: 'shade' },
      { op: 'line', x0: 76, y0: 80, x1: 132, y1: 80, width: 7, color: 'fill' },
      { op: 'line', x0: 72, y0: 124, x1: 136, y1: 124, width: 7, color: 'fill' },
      { op: 'line', x0: 68, y0: 168, x1: 140, y1: 168, width: 7, color: 'fill' },
      { op: 'line', x0: 80, y0: 84, x1: 80, y1: 118, width: 2, color: 'CLAY.pale' },
      { op: 'line', x0: 104, y0: 84, x1: 104, y1: 118, width: 2, color: 'CLAY.pale' },
      { op: 'line', x0: 128, y0: 84, x1: 128, y1: 118, width: 2, color: 'CLAY.pale' },
      { op: 'line', x0: 92, y0: 128, x1: 92, y1: 162, width: 2, color: 'CLAY.pale' },
      { op: 'line', x0: 116, y0: 128, x1: 116, y1: 162, width: 2, color: 'CLAY.pale' },
    ],
  },
  'note-sorting-tree': {
    canvas: [256, 176],
    ops: [
      { op: 'line', x0: 128, y0: 164, x1: 128, y1: 70, width: 12, color: 'shade' },
      { op: 'path', points: [[88, 160], [168, 160], [160, 170], [96, 170]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[128, 80], [60, 60], [24, 84]], close: false, outline: 'INK.line', lineWidth: 7 },
      { op: 'path', points: [[128, 96], [196, 72], [232, 96]], close: false, outline: 'INK.line', lineWidth: 7 },
      { op: 'path', points: [[14, 72], [40, 68], [42, 98], [16, 102]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[52, 52], [78, 48], [80, 78], [54, 82]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[182, 58], [208, 54], [210, 84], [184, 88]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[222, 82], [248, 78], [250, 108], [224, 112]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
    ],
  },
  'open-front-seed-wall': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 28, y: 28, w: 200, h: 136, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 52], [110, 52], [100, 96], [44, 96]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[120, 52], [210, 52], [210, 96], [110, 96]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 108], [120, 108], [110, 152], [44, 152]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[130, 108], [210, 108], [210, 152], [120, 152]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 70, cy: 88, rx: 14, ry: 8, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 160, cy: 144, rx: 14, ry: 8, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 40, y0: 164, x1: 216, y1: 164, width: 4, color: 'shade' },
    ],
  },
  'pack-balance-frame': {
    canvas: [224, 224],
    ops: [
      { op: 'path', points: [[80, 40], [144, 40], [156, 96], [148, 200], [76, 200], [68, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[68, 120], [156, 120], [148, 200], [76, 200]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[88, 40], [96, 18], [128, 18], [136, 40]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 52, y0: 118, x1: 172, y1: 118, width: 5, color: 'shade' },
      { op: 'line', x0: 60, y0: 120, x1: 56, y1: 160, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 164, y0: 120, x1: 168, y1: 160, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 56, cy: 172, rx: 18, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 168, cy: 172, rx: 18, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 112, cy: 118, rx: 6, ry: 6, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'page-turning-weight': {
    canvas: [200, 224],
    ops: [
      { op: 'path', points: [[36, 176], [164, 176], [176, 210], [24, 210]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 206, rx: 78, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 176], [100, 160], [156, 176]], close: false, outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 100, y0: 170, x1: 52, y1: 70, width: 8, color: 'shade' },
      { op: 'line', x0: 100, y0: 170, x1: 148, y1: 70, width: 8, color: 'shade' },
      { op: 'blob', cx: 100, cy: 168, rx: 12, ry: 11, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[36, 60], [68, 52], [74, 82], [44, 90]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[132, 52], [164, 60], [156, 90], [126, 82]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'paint-straining-stand': {
    canvas: [200, 256],
    ops: [
      { op: 'path', points: [[56, 232], [40, 160], [68, 160], [80, 232]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[144, 232], [160, 160], [132, 160], [120, 232]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 62, cy: 228, rx: 26, ry: 10, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 138, cy: 228, rx: 26, ry: 10, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[40, 72], [160, 72], [112, 140], [88, 140]], close: true, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 58, y0: 92, x1: 142, y1: 92, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 68, y0: 110, x1: 132, y1: 110, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 80, y0: 74, x1: 80, y1: 138, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 74, x1: 120, y1: 138, width: 2, color: 'INK.soft' },
    ],
  },
  'pan-lid-tree': {
    canvas: [208, 256],
    ops: [
      { op: 'blob', cx: 104, cy: 238, rx: 56, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 104, y0: 236, x1: 104, y1: 48, width: 12, color: 'shade' },
      { op: 'line', x0: 104, y0: 100, x1: 48, y1: 78, width: 6, color: 'shade' },
      { op: 'line', x0: 104, y0: 150, x1: 160, y1: 128, width: 6, color: 'shade' },
      { op: 'blob', cx: 44, cy: 74, rx: 34, ry: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 164, cy: 124, rx: 40, ry: 13, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 190, rx: 48, ry: 15, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 44, cy: 70, rx: 8, ry: 5, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 164, cy: 120, rx: 8, ry: 5, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'pantry-inventory-rail': {
    canvas: [256, 224],
    ops: [
      { op: 'line', x0: 56, y0: 212, x1: 60, y1: 96, width: 11, color: 'shade' },
      { op: 'line', x0: 200, y0: 212, x1: 196, y1: 96, width: 11, color: 'shade' },
      { op: 'path', points: [[40, 108], [216, 104], [218, 128], [42, 132]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 108], [92, 108], [88, 72], [64, 72]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 128, cy: 86, rx: 18, ry: 20, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[164, 108], [192, 108], [196, 76], [160, 76]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 50, y0: 118, x1: 208, y1: 116, width: 2, color: 'INK.soft' },
    ],
  },
  'paper-flower-cutter': {
    canvas: [200, 224],
    ops: [
      { op: 'rect', x: 40, y: 150, w: 120, h: 58, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 208], [160, 208], [150, 218], [50, 218]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 100, y0: 150, x1: 100, y1: 96, width: 10, color: 'shade' },
      { op: 'line', x0: 100, y0: 96, x1: 172, y1: 70, width: 11, color: 'shade' },
      { op: 'blob', cx: 172, cy: 68, rx: 12, ry: 11, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 138, rx: 24, ry: 12, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 138, rx: 9, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 60, y0: 174, x1: 140, y1: 174, width: 2, color: 'INK.soft' },
    ],
  },
  'paper-lantern-folding-jig': {
    canvas: [176, 248],
    ops: [
      { op: 'path', points: [[40, 236], [136, 236], [128, 246], [48, 246]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[52, 236], [44, 90], [88, 52], [132, 90], [124, 236]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 64, y0: 96, x1: 62, y1: 230, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 80, y0: 78, x1: 80, y1: 230, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 96, y0: 78, x1: 96, y1: 230, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 96, x1: 114, y1: 230, width: 2, color: 'INK.soft' },
      { op: 'path', points: [[44, 90], [88, 72], [132, 90], [88, 108]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 52, rx: 9, ry: 8, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'paper-scrap-comb': {
    canvas: [256, 168],
    ops: [
      { op: 'path', points: [[28, 60], [228, 60], [236, 150], [20, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[24, 150], [232, 150], [226, 160], [30, 160]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 74, y0: 62, x1: 74, y1: 148, width: 7, color: 'shade' },
      { op: 'line', x0: 124, y0: 62, x1: 124, y1: 148, width: 7, color: 'shade' },
      { op: 'line', x0: 174, y0: 62, x1: 174, y1: 148, width: 7, color: 'shade' },
      { op: 'path', points: [[36, 90], [68, 90], [68, 146], [40, 146]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[82, 108], [118, 108], [118, 146], [84, 146]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[132, 124], [168, 124], [168, 146], [134, 146]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'parcel-carry-sled': {
    canvas: [208, 224],
    ops: [
      { op: 'path', points: [[28, 46], [180, 46], [166, 28], [42, 28]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 44, y0: 46, x1: 48, y1: 96, width: 6, color: 'shade' },
      { op: 'line', x0: 160, y0: 46, x1: 156, y1: 96, width: 6, color: 'shade' },
      { op: 'path', points: [[32, 196], [176, 196], [166, 168], [42, 168]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[42, 168], [166, 168], [160, 154], [48, 154]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[40, 196], [60, 196], [80, 210], [36, 210]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[148, 196], [168, 196], [172, 210], [128, 210]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 168, x1: 104, y1: 120, width: 5, color: 'CLAY.deep' },
      { op: 'line', x0: 104, y0: 120, x1: 132, y1: 112, width: 5, color: 'CLAY.deep' },
    ],
  },
  'park-care-route-cart': {
    canvas: [208, 208],
    ops: [
      { op: 'line', x0: 36, y0: 52, x1: 92, y1: 110, width: 9, color: 'shade' },
      { op: 'rect', x: 58, y: 96, w: 120, h: 70, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 70, y: 108, w: 96, h: 20, r: 4, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 118, y0: 108, x1: 118, y1: 128, width: 3, color: 'INK.line' },
      { op: 'line', x0: 70, y0: 144, x1: 166, y1: 144, width: 3, color: 'shade' },
      { op: 'blob', cx: 86, cy: 182, rx: 24, ry: 24, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 86, cy: 182, rx: 7, ry: 7, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 86, y0: 166, x1: 86, y1: 198, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 70, y0: 182, x1: 102, y1: 182, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 160, cy: 182, rx: 18, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
    ],
  },
  'patch-cutting-template': {
    canvas: [200, 224],
    ops: [
      { op: 'rect', x: 34, y: 40, w: 132, h: 172, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 74, cy: 80, rx: 24, ry: 18, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 120, cy: 118, rx: 30, ry: 22, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[52, 150], [96, 148], [92, 178], [50, 180]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[112, 156], [148, 156], [148, 186], [112, 186]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 34, y0: 210, x1: 166, y1: 210, width: 7, color: 'shade' },
      { op: 'line', x0: 44, y0: 52, x1: 156, y1: 52, width: 2, color: 'INK.soft' },
    ],
  },
  'path-condition-board': {
    canvas: [224, 288],
    ops: [
      { op: 'line', x0: 112, y0: 280, x1: 112, y1: 190, width: 13, color: 'shade' },
      { op: 'blob', cx: 112, cy: 278, rx: 40, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 36, y: 44, w: 152, h: 150, r: 14, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'line', x0: 44, y0: 118, x1: 180, y1: 118, width: 4, color: 'INK.soft' },
      { op: 'rect', x: 54, y: 62, w: 48, h: 42, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 146, cy: 84, rx: 22, ry: 18, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 120, y: 132, w: 48, h: 42, r: 8, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 44, y0: 108, x1: 180, y1: 108, width: 2, color: 'shade' },
      { op: 'line', x0: 44, y0: 128, x1: 180, y1: 128, width: 2, color: 'shade' },
    ],
  },
  'path-drain-rake': {
    canvas: [256, 112],
    ops: [
      { op: 'line', x0: 34, y0: 40, x1: 196, y1: 68, width: 11, color: 'shade' },
      { op: 'path', points: [[188, 52], [236, 60], [238, 84], [186, 82]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 196, y0: 80, x1: 196, y1: 104, width: 4, color: 'shade' },
      { op: 'line', x0: 208, y0: 82, x1: 208, y1: 106, width: 4, color: 'shade' },
      { op: 'line', x0: 220, y0: 83, x1: 220, y1: 107, width: 4, color: 'shade' },
      { op: 'line', x0: 232, y0: 83, x1: 232, y1: 107, width: 4, color: 'shade' },
      { op: 'line', x0: 40, y0: 44, x1: 120, y1: 58, width: 2, color: 'INK.soft' },
    ],
  },
  'path-glow-marker': {
    canvas: [176, 288],
    ops: [
      { op: 'halo', cx: 96, cy: 110, r: 56 },
      { op: 'line', x0: 80, y0: 272, x1: 80, y1: 120, width: 10, color: 'shade' },
      { op: 'path', points: [[44, 108], [120, 84], [124, 120], [80, 128]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[80, 120], [120, 114], [112, 140], [84, 140]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 132, rx: 10, ry: 7, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 102, cy: 134, rx: 4, ry: 4, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 80, cy: 268, rx: 40, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'path-gravel-spreader': {
    canvas: [208, 208],
    ops: [
      { op: 'line', x0: 40, y0: 56, x1: 96, y1: 108, width: 9, color: 'shade' },
      { op: 'path', points: [[70, 96], [168, 96], [150, 150], [88, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 78, y: 86, w: 92, h: 16, r: 5, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 94, y0: 150, x1: 144, y1: 150, width: 4, color: 'INK.line' },
      { op: 'speckle', x: 98, y: 150, w: 42, h: 18, count: 9, maxR: 3, alpha: 0.4, color: 'shade' },
      { op: 'blob', cx: 74, cy: 182, rx: 24, ry: 24, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 160, cy: 182, rx: 24, ry: 24, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 74, cy: 182, rx: 7, ry: 7, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 160, cy: 182, rx: 7, ry: 7, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'path-puddle-bypass': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[26, 84], [120, 76], [122, 108], [28, 116]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[118, 78], [210, 86], [208, 118], [120, 108]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 120, cy: 92, rx: 7, ry: 7, fill: 'fill', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 42, y0: 88, x1: 44, y1: 112, width: 3, color: 'shade' },
      { op: 'line', x0: 74, y0: 85, x1: 76, y1: 110, width: 3, color: 'shade' },
      { op: 'line', x0: 150, y0: 90, x1: 152, y1: 114, width: 3, color: 'fill' },
      { op: 'line', x0: 182, y0: 92, x1: 184, y1: 116, width: 3, color: 'fill' },
      { op: 'line', x0: 36, y0: 124, x1: 200, y1: 124, width: 6, color: 'shade' },
    ],
  },
  'pause-and-go-lantern': {
    canvas: [160, 256],
    ops: [
      { op: 'blob', cx: 80, cy: 238, rx: 44, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 228, x1: 80, y1: 192, width: 8, color: 'shade' },
      { op: 'rect', x: 42, y: 60, w: 76, h: 134, r: 18, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[36, 60], [80, 30], [124, 60]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 80, cy: 98, rx: 24, ry: 24, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 80, cy: 156, rx: 24, ry: 24, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 48, y0: 127, x1: 112, y1: 127, width: 3, color: 'INK.soft' },
    ],
  },
  'paw-fastener-tray': {
    canvas: [224, 200],
    ops: [
      { op: 'path', points: [[34, 186], [190, 186], [178, 128], [46, 128]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 150, rx: 24, ry: 18, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 62, cy: 150, rx: 18, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 148, y: 136, w: 30, h: 30, r: 4, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[62, 170], [78, 176], [46, 176]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 162, cy: 172, rx: 15, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 48, y0: 160, x1: 176, y1: 160, width: 2, color: 'INK.soft' },
    ],
  },
  'paw-pad-drying-rack': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 56, y0: 210, x1: 58, y1: 150, width: 11, color: 'shade' },
      { op: 'line', x0: 168, y0: 210, x1: 166, y1: 150, width: 11, color: 'shade' },
      { op: 'path', points: [[36, 148], [188, 144], [190, 168], [38, 172]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 74, cy: 156, rx: 7, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 155, rx: 7, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 150, cy: 156, rx: 7, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[48, 120], [176, 116], [178, 146], [50, 150]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 66, y0: 133, x1: 160, y1: 131, width: 2, color: 'INK.soft' },
    ],
  },
  'paw-paint-walkway': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[24, 100], [232, 92], [236, 134], [20, 138]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'line', x0: 28, y0: 116, x1: 230, y1: 110, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 70, cy: 118, rx: 11, ry: 9, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 70, cy: 104, rx: 4, ry: 4, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 1 },
      { op: 'blob', cx: 124, cy: 122, rx: 11, ry: 9, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 124, cy: 108, rx: 4, ry: 4, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 1 },
      { op: 'blob', cx: 178, cy: 118, rx: 10, ry: 8, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 214, cy: 120, rx: 8, ry: 7, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 1 },
    ],
  },
  'pebble-tone-board': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[28, 118], [228, 118], [222, 150], [34, 150]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[28, 104], [228, 104], [228, 120], [28, 120]], close: true, fill: 'SAGE.shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 62, cy: 78, rx: 22, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 108, cy: 86, rx: 19, ry: 20, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 150, cy: 92, rx: 16, ry: 17, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 188, cy: 98, rx: 13, ry: 14, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 62, y0: 74, x1: 70, y1: 70, width: 2, color: 'INK.soft' },
    ],
  },
  'peekaboo-shutter-box': {
    canvas: [224, 200],
    ops: [
      { op: 'rect', x: 32, y: 56, w: 160, h: 136, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'rect', x: 48, y: 74, w: 54, h: 48, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 122, y: 74, w: 54, h: 48, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[60, 132], [110, 138], [110, 184], [60, 178]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[116, 134], [166, 140], [148, 184], [116, 180]], close: true, fill: 'PAPER.floor', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 134, cy: 162, rx: 12, ry: 12, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 48, y0: 98, x1: 102, y1: 98, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 122, y0: 98, x1: 176, y1: 98, width: 2, color: 'INK.soft' },
    ],
  },
  'pencil-shaving-cup': {
    canvas: [168, 240],
    ops: [
      { op: 'path', points: [[44, 110], [124, 110], [136, 228], [32, 228]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 84, cy: 224, rx: 50, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[40, 108], [128, 108], [124, 84], [44, 84]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 60, w: 64, h: 26, r: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 84, cy: 73, rx: 10, ry: 8, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 84, y0: 60, x1: 84, y1: 40, width: 6, color: 'CLAY.deep' },
      { op: 'line', x0: 56, y0: 140, x1: 110, y1: 138, width: 2, color: 'INK.soft' },
    ],
  },
  'pickle-weight-press': {
    canvas: [200, 208],
    ops: [
      { op: 'path', points: [[48, 110], [152, 110], [160, 196], [40, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 150, rx: 48, ry: 34, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[44, 110], [156, 110], [150, 98], [50, 98]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 100, y0: 98, x1: 100, y1: 58, width: 7, color: 'shade' },
      { op: 'line', x0: 92, y0: 88, x1: 108, y1: 82, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 92, y0: 80, x1: 108, y1: 74, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 100, cy: 48, rx: 34, ry: 18, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 5 },
    ],
  },
  'picnic-brew-box': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 28, y: 92, w: 98, h: 56, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[126, 148], [126, 92], [140, 56], [228, 56], [228, 148]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 126, y0: 92, x1: 126, y1: 148, width: 3, color: 'INK.line' },
      { op: 'rect', x: 150, y: 100, w: 56, h: 40, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 178, cy: 118, rx: 18, ry: 12, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 44, y0: 110, x1: 108, y1: 110, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 44, y0: 130, x1: 108, y1: 130, width: 2, color: 'INK.soft' },
    ],
  },
  'picnic-cloth-wheel': {
    canvas: [224, 200],
    ops: [
      { op: 'blob', cx: 112, cy: 108, rx: 52, ry: 50, n: 14, irregularity: 0.05, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[112, 108], [44, 150], [40, 178], [88, 178], [112, 156]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[112, 108], [180, 150], [184, 178], [136, 178], [112, 156]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 162, x1: 104, y1: 138, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 162, y0: 162, x1: 120, y1: 138, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 112, cy: 104, rx: 22, ry: 22, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 86, x1: 112, y1: 122, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 112, cy: 104, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'pillow-airing-frame': {
    canvas: [224, 288],
    ops: [
      { op: 'line', x0: 56, y0: 278, x1: 104, y1: 48, width: 10, color: 'shade' },
      { op: 'line', x0: 168, y0: 278, x1: 120, y1: 48, width: 10, color: 'shade' },
      { op: 'line', x0: 104, y0: 48, x1: 120, y1: 48, width: 8, color: 'shade' },
      { op: 'path', points: [[78, 108], [150, 108], [156, 128], [72, 128]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[66, 168], [162, 168], [170, 188], [58, 188]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 110, x1: 98, y1: 126, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 110, x1: 128, y1: 126, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 92, y0: 170, x1: 90, y1: 186, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 132, y0: 170, x1: 134, y1: 186, width: 2, color: 'INK.soft' },
    ],
  },
  'pinecone-weather-gauge': {
    canvas: [200, 224],
    ops: [
      { op: 'blob', cx: 100, cy: 206, rx: 50, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 204, x1: 100, y1: 150, width: 10, color: 'shade' },
      { op: 'path', points: [[100, 150], [100, 40], [112, 40], [112, 150]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 60], [60, 44], [58, 58], [100, 76]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 90], [58, 76], [56, 90], [100, 104]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 118], [60, 108], [60, 122], [100, 130]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[112, 72], [152, 58], [154, 72], [112, 86]], close: true, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[112, 100], [154, 90], [154, 104], [112, 114]], close: true, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'pollen-window-screen': {
    canvas: [192, 240],
    ops: [
      { op: 'rect', x: 36, y: 40, w: 120, h: 180, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 54, y: 60, w: 84, h: 140, r: 4, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 54, y0: 96, x1: 138, y1: 96, width: 1.5, color: 'shade' },
      { op: 'line', x0: 54, y0: 132, x1: 138, y1: 132, width: 1.5, color: 'shade' },
      { op: 'line', x0: 54, y0: 168, x1: 138, y1: 168, width: 1.5, color: 'shade' },
      { op: 'line', x0: 82, y0: 60, x1: 82, y1: 200, width: 1.5, color: 'shade' },
      { op: 'line', x0: 110, y0: 60, x1: 110, y1: 200, width: 1.5, color: 'shade' },
      { op: 'path', points: [[156, 110], [180, 104], [180, 128], [156, 134]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 96, cy: 222, rx: 62, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'pollinator-drinking-stone': {
    canvas: [224, 176],
    ops: [
      { op: 'blob', cx: 112, cy: 120, rx: 98, ry: 44, n: 15, irregularity: 0.07, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 108, rx: 72, ry: 28, n: 14, irregularity: 0.08, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 104, rx: 50, ry: 17, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 84, cy: 106, rx: 11, ry: 8, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 120, cy: 112, rx: 13, ry: 9, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 140, cy: 102, rx: 9, ry: 7, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 150, cy: 90, rx: 12, ry: 9, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'pollinator-windbreak': {
    canvas: [256, 224],
    ops: [
      { op: 'line', x0: 52, y0: 212, x1: 52, y1: 90, width: 11, color: 'shade' },
      { op: 'line', x0: 204, y0: 212, x1: 204, y1: 90, width: 11, color: 'shade' },
      { op: 'rect', x: 44, y: 86, w: 168, h: 104, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 84, cy: 118, rx: 9, ry: 9, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 128, cy: 118, rx: 9, ry: 9, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 172, cy: 118, rx: 9, ry: 9, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 106, cy: 158, rx: 9, ry: 9, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 150, cy: 158, rx: 9, ry: 9, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 128, cy: 74, rx: 13, ry: 10, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'pond-edge-kneeler': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[30, 118], [226, 118], [214, 134], [42, 134]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 72, w: 152, h: 42, r: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 92, y0: 76, x1: 92, y1: 110, width: 3, color: 'shade' },
      { op: 'line', x0: 128, y0: 74, x1: 128, y1: 110, width: 3, color: 'shade' },
      { op: 'line', x0: 164, y0: 76, x1: 164, y1: 110, width: 3, color: 'shade' },
      { op: 'path', points: [[34, 64], [58, 64], [58, 82], [34, 82]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[198, 64], [222, 64], [222, 82], [198, 82]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 42, y0: 118, x1: 214, y1: 118, width: 2, color: 'INK.soft' },
    ],
  },
  'pond-escape-ladder': {
    canvas: [208, 256],
    ops: [
      { op: 'blob', cx: 104, cy: 228, rx: 92, ry: 22, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[40, 236], [126, 52], [156, 62], [70, 246]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 110, y0: 92, x1: 140, y1: 102, width: 4, color: 'shade' },
      { op: 'line', x0: 94, y0: 128, x1: 124, y1: 138, width: 4, color: 'shade' },
      { op: 'line', x0: 78, y0: 164, x1: 108, y1: 174, width: 4, color: 'shade' },
      { op: 'line', x0: 62, y0: 200, x1: 92, y1: 210, width: 4, color: 'shade' },
      { op: 'speckle', x: 50, y: 80, w: 90, h: 150, count: 16, maxR: 1.4, alpha: 0.14, color: 'INK.grain' },
    ],
  },
  'pond-skater-platform': {
    canvas: [256, 144],
    ops: [
      { op: 'blob', cx: 128, cy: 108, rx: 108, ry: 24, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 56], [216, 56], [228, 90], [28, 90]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 56, rx: 92, ry: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 68, y0: 52, x1: 78, y1: 62, width: 2, color: 'shade' },
      { op: 'line', x0: 108, y0: 50, x1: 118, y1: 60, width: 2, color: 'shade' },
      { op: 'line', x0: 150, y0: 52, x1: 160, y1: 62, width: 2, color: 'shade' },
      { op: 'line', x0: 188, y0: 50, x1: 198, y1: 60, width: 2, color: 'shade' },
      { op: 'line', x0: 28, y0: 92, x1: 228, y1: 90, width: 3, color: 'WATER.edge' },
    ],
  },
  'pond-tone-float': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[12, 118], [244, 118], [244, 150], [12, 150]], close: true, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 30, y0: 126, x1: 78, y1: 124, width: 2, color: 'WATER.edge' },
      { op: 'line', x0: 170, y0: 132, x1: 222, y1: 130, width: 2, color: 'WATER.edge' },
      { op: 'blob', cx: 128, cy: 110, rx: 68, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 128, cy: 102, rx: 46, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 128, cy: 100, rx: 14, ry: 7, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 128, y0: 134, x1: 128, y1: 150, width: 4, color: 'INK.soft' },
    ],
  },
  'pond-viewing-scope': {
    canvas: [200, 256],
    ops: [
      { op: 'line', x0: 74, y0: 150, x1: 68, y1: 244, width: 10, color: 'shade' },
      { op: 'line', x0: 140, y0: 150, x1: 146, y1: 244, width: 10, color: 'shade' },
      { op: 'path', points: [[92, 40], [150, 60], [136, 150], [70, 150], [62, 80]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 56, rx: 34, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[72, 118], [134, 118], [128, 156], [78, 156]], close: true, fill: 'WATER.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 82, y0: 132, x1: 120, y1: 130, width: 2, color: 'WATER.edge' },
      { op: 'line', x0: 86, y0: 144, x1: 114, y1: 143, width: 2, color: 'WATER.edge' },
      { op: 'line', x0: 74, y0: 90, x1: 132, y1: 98, width: 2, color: 'INK.soft' },
    ],
  },
  'porch-cello-box': {
    canvas: [224, 256],
    ops: [
      { op: 'blob', cx: 112, cy: 240, rx: 56, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 40, y: 150, w: 146, h: 86, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'line', x0: 54, y0: 162, x1: 172, y1: 162, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 54, y0: 224, x1: 172, y1: 224, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 113, y0: 150, x1: 118, y1: 40, width: 8, color: 'shade' },
      { op: 'blob', cx: 119, cy: 36, rx: 12, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 108, y0: 150, x1: 113, y1: 42, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 104, cy: 192, rx: 7, ry: 11, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'porch-foot-hammock': {
    canvas: [256, 144],
    ops: [
      { op: 'line', x0: 40, y0: 134, x1: 40, y1: 48, width: 10, color: 'shade' },
      { op: 'line', x0: 216, y0: 134, x1: 216, y1: 48, width: 10, color: 'shade' },
      { op: 'line', x0: 40, y0: 52, x1: 92, y1: 92, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 216, y0: 52, x1: 164, y1: 92, width: 2, color: 'INK.soft' },
      { op: 'path', points: [[84, 86], [128, 116], [172, 86], [166, 98], [128, 128], [90, 98]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 110, y0: 100, x1: 110, y1: 118, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 146, y0: 100, x1: 146, y1: 118, width: 2, color: 'INK.soft' },
    ],
  },
  'porch-key-stone': {
    canvas: [208, 192],
    ops: [
      { op: 'blob', cx: 104, cy: 178, rx: 78, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[34, 176], [174, 176], [160, 96], [120, 58], [84, 58], [48, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 110], [148, 110], [140, 80], [112, 62], [92, 62], [66, 84]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 104, cy: 134, rx: 30, ry: 26, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 104, cy: 134, rx: 18, ry: 15, fill: 'INK.line', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 104, y0: 134, x1: 126, y1: 120, width: 4, color: 'CLAY.deep' },
      { op: 'speckle', x: 50, y: 120, w: 108, h: 44, count: 7, maxR: 2, alpha: 0.28, color: 'INK.grain' },
    ],
  },
  'porch-moth-light': {
    canvas: [208, 288],
    ops: [
      { op: 'halo', cx: 80, cy: 120, r: 56 },
      { op: 'line', x0: 80, y0: 272, x1: 80, y1: 150, width: 9, color: 'shade' },
      { op: 'path', points: [[48, 150], [112, 150], [100, 96], [60, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 80, cy: 134, rx: 18, ry: 8, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 120, y: 120, w: 56, h: 96, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 134, y0: 134, x1: 134, y1: 204, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 148, y0: 134, x1: 148, y1: 204, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 162, y0: 134, x1: 162, y1: 204, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 100, cy: 270, rx: 68, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'porch-note-slate': {
    canvas: [192, 240],
    ops: [
      { op: 'line', x0: 96, y0: 228, x1: 96, y1: 150, width: 10, color: 'shade' },
      { op: 'path', points: [[54, 228], [138, 228], [130, 206], [62, 206]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 36, y: 36, w: 120, h: 120, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 50, y: 50, w: 92, h: 92, r: 8, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[36, 36], [156, 36], [150, 18], [42, 18]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 62, y: 62, w: 30, h: 30, r: 5, fill: 'fill', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 100, y: 62, w: 30, h: 30, r: 5, fill: 'fill', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 62, y: 100, w: 30, h: 30, r: 5, fill: 'fill', outline: 'INK.soft', lineWidth: 3 },
    ],
  },
  'porch-package-drawer': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 28, y: 48, w: 200, h: 118, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[28, 48], [228, 48], [214, 28], [42, 28]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 92, y: 96, w: 140, h: 58, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 102, y: 104, w: 120, h: 42, r: 6, fill: 'fill', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 150, y: 120, w: 26, h: 10, r: 5, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 48, y0: 72, x1: 78, y1: 72, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 48, y0: 86, x1: 78, y1: 86, width: 3, color: 'INK.soft' },
    ],
  },
  'porch-sun-screen': {
    canvas: [256, 176],
    ops: [
      { op: 'line', x0: 40, y0: 162, x1: 40, y1: 40, width: 8, color: 'shade' },
      { op: 'line', x0: 216, y0: 162, x1: 216, y1: 40, width: 8, color: 'shade' },
      { op: 'path', points: [[28, 44], [228, 44], [228, 30], [28, 30]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 48], [212, 48], [212, 128], [44, 128]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 52, x1: 80, y1: 124, width: 2, color: 'shade' },
      { op: 'line', x0: 128, y0: 52, x1: 128, y1: 124, width: 2, color: 'shade' },
      { op: 'line', x0: 176, y0: 52, x1: 176, y1: 124, width: 2, color: 'shade' },
      { op: 'line', x0: 48, y0: 80, x1: 208, y1: 80, width: 2, color: 'shade' },
      { op: 'line', x0: 48, y0: 104, x1: 208, y1: 104, width: 2, color: 'shade' },
      { op: 'path', points: [[44, 128], [212, 128], [208, 142], [48, 142]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'porch-tea-hatch': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[28, 40], [228, 40], [228, 20], [28, 20]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 44], [216, 44], [216, 72], [40, 72]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[40, 72], [216, 72], [226, 124], [30, 124]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 52, y0: 124, x1: 56, y1: 150, width: 7, color: 'shade' },
      { op: 'line', x0: 204, y0: 124, x1: 200, y1: 150, width: 7, color: 'shade' },
      { op: 'blob', cx: 128, cy: 104, rx: 26, ry: 12, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[150, 98], [170, 94], [172, 104], [152, 108]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 40, y0: 96, x1: 216, y1: 96, width: 2, color: 'INK.soft' },
    ],
  },
  'porch-wind-pocket': {
    canvas: [224, 256],
    ops: [
      { op: 'path', points: [[44, 244], [120, 244], [112, 222], [52, 222]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 228], [64, 40], [120, 30], [176, 52], [170, 228]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[64, 42], [120, 32], [120, 220], [66, 224]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 92, y0: 52, x1: 92, y1: 216, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 140, y0: 50, x1: 140, y1: 216, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 68, y0: 120, x1: 172, y1: 126, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 60, y0: 228, x1: 60, y1: 244, width: 8, color: 'shade' },
      { op: 'line', x0: 170, y0: 228, x1: 170, y1: 244, width: 8, color: 'shade' },
    ],
  },
  'portable-writing-knee': {
    canvas: [256, 152],
    ops: [
      { op: 'path', points: [[28, 86], [228, 78], [236, 110], [20, 118]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[24, 116], [60, 120], [58, 144], [34, 142]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[200, 114], [236, 108], [238, 140], [210, 144]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 84], [180, 78], [182, 98], [46, 104]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 192, y: 62, w: 40, h: 24, r: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 212, cy: 70, rx: 12, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[120, 98], [126, 140], [112, 140], [118, 98]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 60, y0: 92, x1: 150, y1: 88, width: 2, color: 'INK.soft' },
    ],
  },
  'portion-scoop-board': {
    canvas: [240, 208],
    ops: [
      { op: 'line', x0: 72, y0: 196, x1: 78, y1: 150, width: 11, color: 'shade' },
      { op: 'line', x0: 168, y0: 196, x1: 162, y1: 150, width: 11, color: 'shade' },
      { op: 'path', points: [[36, 108], [204, 108], [196, 156], [44, 156]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 80, cy: 128, rx: 22, ry: 13, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 128, cy: 128, rx: 22, ry: 13, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 176, cy: 128, rx: 18, ry: 11, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 56, y0: 108, x1: 56, y1: 152, width: 5, color: 'shade' },
    ],
  },
  'postcard-drying-line': {
    canvas: [224, 256],
    ops: [
      { op: 'line', x0: 40, y0: 244, x1: 48, y1: 60, width: 9, color: 'shade' },
      { op: 'line', x0: 184, y0: 244, x1: 176, y1: 60, width: 9, color: 'shade' },
      { op: 'path', points: [[20, 242], [80, 242], [74, 252], [26, 252]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[144, 242], [204, 242], [198, 252], [150, 252]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[46, 64], [112, 86], [178, 64]], close: false, outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[60, 76], [60, 128], [100, 128], [100, 72]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[124, 72], [124, 124], [164, 128], [164, 76]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 78, y0: 66, x1: 78, y1: 78, width: 4, color: 'CLAY.deep' },
      { op: 'line', x0: 146, y0: 66, x1: 146, y1: 78, width: 4, color: 'CLAY.deep' },
    ],
  },
  'pour-over-stand': {
    canvas: [200, 256],
    ops: [
      { op: 'path', points: [[44, 242], [62, 120], [80, 120], [68, 242]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[156, 242], [138, 120], [120, 120], [132, 242]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[56, 120], [144, 120], [126, 100], [74, 100]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[68, 100], [132, 100], [112, 58], [88, 58]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 58, rx: 24, ry: 7, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 100, y0: 104, x1: 100, y1: 134, width: 3, color: 'INK.soft' },
    ],
  },
  'public-water-flush': {
    canvas: [256, 128],
    ops: [
      { op: 'line', x0: 48, y0: 116, x1: 52, y1: 40, width: 11, color: 'shade' },
      { op: 'path', points: [[44, 40], [120, 40], [150, 60], [44, 60]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 150, y0: 54, x1: 210, y1: 36, width: 8, color: 'shade' },
      { op: 'blob', cx: 150, cy: 54, rx: 8, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 68], [140, 68], [150, 116], [90, 116]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 120, cy: 112, rx: 20, ry: 6, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 120, y0: 74, x1: 120, y1: 104, width: 3, color: 'WATER.edge' },
      { op: 'line', x0: 52, y0: 116, x1: 150, y1: 116, width: 6, color: 'shade' },
    ],
  },
  'puddle-depth-flower': {
    canvas: [200, 256],
    ops: [
      { op: 'blob', cx: 100, cy: 236, rx: 62, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 100, y0: 120, x1: 100, y1: 236, width: 8, color: 'fill' },
      { op: 'path', points: [[100, 118], [68, 72], [88, 108]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 118], [132, 72], [112, 108]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 114], [100, 48], [114, 96]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 114], [86, 96], [100, 48]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 118, rx: 12, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 94, y0: 150, x1: 94, y1: 210, width: 2, color: 'WATER.edge' },
    ],
  },
  'quiet-checkup-mat': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[28, 150], [228, 150], [212, 96], [44, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[52, 140], [204, 140], [192, 104], [64, 104]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 108, cy: 122, rx: 18, ry: 13, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 92, y0: 134, x1: 86, y1: 146, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 124, y0: 134, x1: 130, y1: 146, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 160, cy: 120, rx: 20, ry: 14, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
    ],
  },
  'quiet-company-benchlet': {
    canvas: [256, 144],
    ops: [
      { op: 'line', x0: 52, y0: 134, x1: 56, y1: 92, width: 10, color: 'shade' },
      { op: 'line', x0: 120, y0: 134, x1: 118, y1: 92, width: 10, color: 'shade' },
      { op: 'path', points: [[36, 80], [128, 72], [130, 94], [38, 102]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 138, y0: 134, x1: 140, y1: 104, width: 10, color: 'shade' },
      { op: 'line', x0: 206, y0: 134, x1: 204, y1: 104, width: 10, color: 'shade' },
      { op: 'path', points: [[130, 94], [222, 88], [224, 110], [132, 118]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 60, y0: 84, x1: 80, y1: 83, width: 2, color: 'shade' },
      { op: 'line', x0: 160, y0: 98, x1: 180, y1: 97, width: 2, color: 'shade' },
    ],
  },
  'quiet-corner-screen': {
    canvas: [256, 224],
    ops: [
      { op: 'path', points: [[24, 196], [28, 68], [60, 52], [96, 64], [92, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[96, 196], [100, 56], [136, 40], [172, 56], [168, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[172, 196], [168, 64], [204, 52], [236, 68], [232, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 34, y0: 196, x1: 34, y1: 210, width: 7, color: 'shade' },
      { op: 'line', x0: 136, y0: 196, x1: 136, y1: 214, width: 7, color: 'shade' },
      { op: 'line', x0: 226, y0: 196, x1: 226, y1: 210, width: 7, color: 'shade' },
      { op: 'line', x0: 58, y0: 80, x1: 58, y1: 188, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 134, y0: 68, x1: 134, y1: 188, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 202, y0: 80, x1: 202, y1: 188, width: 2, color: 'INK.soft' },
    ],
  },
  'quiet-hammer-block': {
    canvas: [200, 224],
    ops: [
      { op: 'rect', x: 36, y: 176, w: 128, h: 34, r: 8, fill: 'SAGE.deep', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 62, w: 96, h: 118, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 52, y0: 96, x1: 148, y1: 96, width: 3, color: 'shade' },
      { op: 'line', x0: 52, y0: 130, x1: 148, y1: 130, width: 3, color: 'shade' },
      { op: 'line', x0: 52, y0: 160, x1: 148, y1: 160, width: 3, color: 'shade' },
      { op: 'blob', cx: 84, cy: 74, rx: 7, ry: 5, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 116, cy: 74, rx: 7, ry: 5, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 60, y0: 196, x1: 140, y1: 196, width: 2, color: 'INK.soft' },
    ],
  },
  'quiet-hour-light': {
    canvas: [176, 288],
    ops: [
      { op: 'blob', cx: 88, cy: 262, rx: 56, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[80, 252], [84, 120], [92, 120], [96, 252]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'halo', cx: 88, cy: 74, r: 72 },
      { op: 'path', points: [[44, 116], [58, 52], [118, 52], [132, 116]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 52, rx: 34, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 88, cy: 110, rx: 40, ry: 9, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 68, y0: 84, x1: 108, y1: 84, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 88, cy: 94, rx: 11, ry: 11, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'quiet-practice-pad': {
    canvas: [224, 200],
    ops: [
      { op: 'line', x0: 50, y0: 190, x1: 58, y1: 110, width: 11, color: 'shade' },
      { op: 'line', x0: 174, y0: 190, x1: 166, y1: 110, width: 11, color: 'shade' },
      { op: 'blob', cx: 112, cy: 90, rx: 86, ry: 46, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 112, cy: 86, rx: 66, ry: 34, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 60, y0: 78, x1: 164, y1: 78, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 56, y0: 94, x1: 168, y1: 94, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 112, cy: 86, rx: 10, ry: 6, fill: 'SAGE.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'rain-barrel-level-float': {
    canvas: [200, 256],
    ops: [
      { op: 'path', points: [[48, 96], [152, 96], [160, 244], [40, 244]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 96, rx: 52, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 44, y0: 150, x1: 156, y1: 150, width: 4, color: 'shade' },
      { op: 'line', x0: 42, y0: 200, x1: 158, y1: 200, width: 4, color: 'shade' },
      { op: 'rect', x: 160, y: 110, w: 16, h: 126, r: 7, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 168, cy: 150, rx: 12, ry: 11, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 162, y0: 196, x1: 174, y1: 196, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 162, y0: 222, x1: 174, y1: 222, width: 2, color: 'INK.soft' },
    ],
  },
  'rain-chain-planter': {
    canvas: [176, 288],
    ops: [
      { op: 'path', points: [[44, 206], [54, 272], [122, 272], [132, 206]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 208, rx: 46, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 88, y0: 24, x1: 88, y1: 188, width: 3, color: 'INK.soft' },
      { op: 'path', points: [[60, 56], [116, 56], [108, 84], [68, 84]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[64, 104], [112, 104], [104, 130], [72, 130]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[66, 150], [110, 150], [103, 176], [73, 176]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 88, cy: 70, rx: 20, ry: 6, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 118, rx: 18, ry: 6, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'rain-cloak-dryer': {
    canvas: [224, 288],
    ops: [
      { op: 'line', x0: 112, y0: 250, x1: 112, y1: 92, width: 11, color: 'shade' },
      { op: 'blob', cx: 112, cy: 78, rx: 68, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 74, rx: 48, ry: 15, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 70, y0: 92, x1: 80, y1: 140, width: 3, color: 'shade' },
      { op: 'line', x0: 154, y0: 92, x1: 144, y1: 140, width: 3, color: 'shade' },
      { op: 'blob', cx: 112, cy: 256, rx: 74, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 250, rx: 56, ry: 15, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'rain-drop-counter': {
    canvas: [200, 224],
    ops: [
      { op: 'path', points: [[60, 208], [52, 150], [148, 150], [140, 208]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 150, x1: 100, y1: 92, width: 8, color: 'shade' },
      { op: 'path', points: [[100, 96], [40, 80], [36, 98], [100, 114]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 80], [36, 98], [28, 96], [30, 82]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 108, y0: 140, x1: 160, y1: 140, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 118, cy: 134, rx: 8, ry: 8, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 136, cy: 134, rx: 8, ry: 8, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 154, cy: 134, rx: 8, ry: 8, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 42, cy: 58, rx: 7, ry: 10, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'rain-garden-diverter': {
    canvas: [256, 128],
    ops: [
      { op: 'path', points: [[30, 52], [150, 72], [150, 96], [30, 76]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[144, 68], [230, 84], [228, 114], [142, 100]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 150, cy: 84, rx: 11, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 42, y0: 64, x1: 140, y1: 82, width: 4, color: 'WATER.mid' },
      { op: 'line', x0: 158, y0: 90, x1: 218, y1: 100, width: 4, color: 'WATER.mid' },
      { op: 'line', x0: 150, y0: 84, x1: 190, y1: 56, width: 3, color: 'shade' },
      { op: 'blob', cx: 192, cy: 54, rx: 6, ry: 6, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 48, y0: 106, x1: 210, y1: 118, width: 7, color: 'shade' },
    ],
  },
  'rain-sound-gutter': {
    canvas: [256, 124],
    ops: [
      { op: 'path', points: [[12, 40], [92, 40], [88, 58], [16, 58]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[80, 58], [164, 58], [160, 78], [84, 78]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[152, 78], [244, 78], [240, 100], [156, 100]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 130, cy: 110, rx: 120, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 30, y0: 49, x1: 78, y1: 49, width: 2.5, color: 'WATER.mid' },
      { op: 'line', x0: 100, y0: 69, x1: 150, y1: 69, width: 2.5, color: 'WATER.mid' },
      { op: 'line', x0: 175, y0: 90, x1: 228, y1: 90, width: 2.5, color: 'WATER.mid' },
      { op: 'line', x0: 52, y0: 41, x1: 51, y1: 57, width: 2, color: 'shade' },
      { op: 'line', x0: 122, y0: 60, x1: 121, y1: 77, width: 2, color: 'shade' },
    ],
  },
  'raincoat-button-board': {
    canvas: [184, 240],
    ops: [
      { op: 'blob', cx: 92, cy: 224, rx: 52, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 56, y0: 220, x1: 58, y1: 60, width: 6, color: 'shade' },
      { op: 'line', x0: 128, y0: 220, x1: 126, y1: 60, width: 6, color: 'shade' },
      { op: 'rect', x: 40, y: 44, w: 104, h: 140, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 62, y: 66, w: 60, h: 24, r: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 62, y: 104, w: 60, h: 24, r: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 62, y: 142, w: 60, h: 24, r: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 104, cy: 78, rx: 6, ry: 6, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 80, cy: 116, rx: 6, ry: 6, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'rainstick-bench': {
    canvas: [256, 200],
    ops: [
      { op: 'line', x0: 70, y0: 192, x1: 78, y1: 120, width: 12, color: 'shade' },
      { op: 'line', x0: 196, y0: 192, x1: 188, y1: 104, width: 12, color: 'shade' },
      { op: 'path', points: [[40, 138], [210, 86], [220, 106], [50, 158]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 46, cy: 148, rx: 12, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 215, cy: 96, rx: 12, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 78, y0: 120, x1: 182, y1: 98, width: 3, color: 'INK.soft' },
      { op: 'speckle', x: 60, y: 108, w: 140, h: 44, count: 12, maxR: 2, alpha: 0.3, color: 'INK.grain' },
    ],
  },
  'rainwater-first-flush': {
    canvas: [256, 112],
    ops: [
      { op: 'path', points: [[20, 30], [150, 26], [152, 54], [22, 58]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[150, 30], [210, 46], [206, 78], [148, 60]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 216, cy: 92, rx: 30, ry: 14, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 150, y0: 44, x1: 150, y1: 98, width: 4, color: 'INK.line' },
      { op: 'line', x0: 54, y0: 38, x1: 120, y1: 36, width: 2, color: 'WATER.mid' },
      { op: 'line', x0: 166, y0: 54, x1: 196, y1: 62, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 190, y0: 96, x1: 242, y1: 92, width: 2, color: 'WATER.edge' },
    ],
  },
  'rainy-arrival-canopy': {
    canvas: [256, 176],
    ops: [
      { op: 'line', x0: 44, y0: 162, x1: 48, y1: 92, width: 9, color: 'shade' },
      { op: 'line', x0: 212, y0: 162, x1: 208, y1: 92, width: 9, color: 'shade' },
      { op: 'path', points: [[28, 90], [228, 90], [208, 52], [48, 52]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[28, 90], [228, 90], [224, 104], [32, 104]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[32, 104], [58, 104], [54, 122], [36, 122]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[198, 104], [224, 104], [220, 122], [202, 122]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 70, y0: 68, x1: 190, y1: 68, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 52, x1: 128, y1: 90, width: 2, color: 'INK.soft' },
    ],
  },
  'reading-light-hood': {
    canvas: [176, 288],
    ops: [
      { op: 'halo', cx: 108, cy: 110, r: 70 },
      { op: 'path', points: [[40, 266], [120, 266], [112, 278], [48, 278]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 266], [120, 220], [124, 266]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 250, x1: 120, y1: 150, width: 9, color: 'shade' },
      { op: 'line', x0: 120, y0: 150, x1: 108, y1: 110, width: 9, color: 'shade' },
      { op: 'blob', cx: 120, cy: 150, rx: 9, ry: 8, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[78, 118], [138, 118], [124, 86], [92, 86]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 116, rx: 22, ry: 6, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'reed-boat-dock': {
    canvas: [256, 144],
    ops: [
      { op: 'line', x0: 74, y0: 92, x1: 70, y1: 136, width: 9, color: 'shade' },
      { op: 'line', x0: 182, y0: 92, x1: 186, y1: 136, width: 9, color: 'shade' },
      { op: 'path', points: [[28, 70], [228, 66], [230, 94], [30, 98]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[64, 68], [88, 68], [86, 90], [66, 90]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[124, 68], [148, 68], [146, 90], [126, 90]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[176, 40], [214, 46], [210, 62], [172, 58]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 192, y0: 24, x1: 192, y1: 50, width: 3, color: 'shade' },
      { op: 'line', x0: 30, y0: 98, x1: 230, y1: 94, width: 3, color: 'WATER.edge' },
    ],
  },
  'reed-humming-wheel': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 72, y0: 212, x1: 80, y1: 120, width: 11, color: 'shade' },
      { op: 'line', x0: 156, y0: 212, x1: 148, y1: 120, width: 11, color: 'shade' },
      { op: 'blob', cx: 112, cy: 104, rx: 62, ry: 62, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 112, cy: 104, rx: 16, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 112, y0: 104, x1: 112, y1: 46, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 104, x1: 162, y1: 78, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 104, x1: 162, y1: 130, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 104, x1: 62, y1: 78, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 104, x1: 62, y1: 130, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 104, x1: 112, y1: 162, width: 3, color: 'INK.soft' },
    ],
  },
  'repair-clamp-tree': {
    canvas: [200, 256],
    ops: [
      { op: 'blob', cx: 100, cy: 236, rx: 56, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 232, x1: 100, y1: 40, width: 13, color: 'fill' },
      { op: 'path', points: [[100, 80], [150, 72], [150, 104], [100, 98]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 124], [54, 118], [54, 148], [100, 142]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 166], [148, 160], [148, 190], [100, 184]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 130, y0: 80, x1: 130, y1: 104, width: 3, color: 'shade' },
      { op: 'line', x0: 72, y0: 124, x1: 72, y1: 148, width: 3, color: 'shade' },
      { op: 'line', x0: 128, y0: 166, x1: 128, y1: 190, width: 3, color: 'shade' },
    ],
  },
  'repair-day-apron-rack': {
    canvas: [256, 288],
    ops: [
      { op: 'line', x0: 60, y0: 48, x1: 56, y1: 268, width: 8, color: 'shade' },
      { op: 'line', x0: 196, y0: 48, x1: 200, y1: 268, width: 8, color: 'shade' },
      { op: 'path', points: [[44, 52], [212, 48], [212, 62], [44, 66]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[74, 66], [68, 86], [92, 86], [86, 66]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[68, 86], [60, 196], [100, 196], [92, 86]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[168, 66], [162, 86], [186, 86], [180, 66]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[162, 86], [154, 196], [194, 196], [186, 86]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 128, cy: 74, rx: 13, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 78, y0: 120, x1: 82, y1: 160, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 174, y0: 120, x1: 170, y1: 160, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 48, y0: 268, x1: 208, y1: 268, width: 4, color: 'shade' },
    ],
  },
  'repair-material-cache': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 32, y: 68, w: 192, h: 96, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 96, y0: 68, x1: 96, y1: 164, width: 4, color: 'INK.line' },
      { op: 'line', x0: 160, y0: 68, x1: 160, y1: 164, width: 4, color: 'INK.line' },
      { op: 'path', points: [[36, 68], [60, 52], [88, 68]], close: false, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 68], [128, 52], [156, 68]], close: false, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[164, 68], [192, 52], [220, 68]], close: false, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'speckle', x: 44, y: 92, w: 40, h: 60, count: 8, maxR: 2, alpha: 0.3, color: 'INK.grain' },
      { op: 'line', x0: 108, y0: 104, x1: 148, y1: 104, width: 3, color: 'shade' },
      { op: 'line', x0: 108, y0: 120, x1: 148, y1: 120, width: 3, color: 'shade' },
    ],
  },
  'repair-sequence-board': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 48, y0: 204, x1: 48, y1: 80, width: 12, color: 'shade' },
      { op: 'line', x0: 176, y0: 204, x1: 176, y1: 80, width: 12, color: 'shade' },
      { op: 'rect', x: 40, y: 200, w: 144, h: 18, r: 5, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 52, y: 48, w: 28, h: 48, r: 5, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 86, y: 48, w: 28, h: 48, r: 5, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 120, y: 48, w: 28, h: 48, r: 5, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 154, y: 48, w: 20, h: 48, r: 5, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 58, y0: 72, x1: 74, y1: 72, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 92, y0: 72, x1: 108, y1: 72, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 126, y0: 72, x1: 142, y1: 72, width: 2, color: 'INK.soft' },
    ],
  },
  'rest-bell-pillow': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[36, 148], [26, 96], [60, 72], [196, 72], [230, 96], [220, 148], [160, 142], [96, 142]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 56, cy: 80, rx: 12, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 200, cy: 80, rx: 12, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[114, 124], [112, 100], [144, 100], [142, 124]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 128, cy: 126, rx: 7, ry: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 80, y0: 92, x1: 176, y1: 92, width: 2, color: 'INK.soft' },
    ],
  },
  'rest-choice-carousel': {
    canvas: [224, 256],
    ops: [
      { op: 'blob', cx: 112, cy: 238, rx: 50, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 236, x1: 112, y1: 120, width: 13, color: 'fill' },
      { op: 'blob', cx: 112, cy: 110, rx: 80, ry: 74, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 110, x1: 176, y1: 110, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 40, x1: 112, y1: 180, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 80, cy: 78, rx: 18, ry: 13, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 146, cy: 80, rx: 15, ry: 15, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 146, rx: 16, ry: 12, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 3 },
    ],
  },
  'rest-stop-sundial': {
    canvas: [224, 200],
    ops: [
      { op: 'path', points: [[28, 108], [196, 100], [200, 142], [32, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'line', x0: 52, y0: 150, x1: 56, y1: 188, width: 10, color: 'shade' },
      { op: 'line', x0: 172, y0: 146, x1: 176, y1: 188, width: 10, color: 'shade' },
      { op: 'path', points: [[112, 104], [120, 44], [132, 104]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[120, 104], [58, 114], [62, 124], [122, 116]], close: true, fill: 'INK.soft', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 78, y0: 108, x1: 76, y1: 118, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 106, x1: 102, y1: 116, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 150, y0: 104, x1: 148, y1: 114, width: 2, color: 'INK.soft' },
    ],
  },
  'returning-route-bell': {
    canvas: [176, 224],
    ops: [
      { op: 'path', points: [[44, 210], [60, 150], [116, 150], [132, 210]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 88, y0: 150, x1: 88, y1: 52, width: 11, color: 'shade' },
      { op: 'path', points: [[48, 140], [40, 86], [88, 46], [136, 86], [128, 140]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 140, rx: 44, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 88, cy: 154, rx: 12, ry: 12, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 62, y0: 94, x1: 78, y1: 118, width: 3, color: 'shade' },
      { op: 'blob', cx: 88, cy: 48, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'rice-rinsing-basket': {
    canvas: [208, 224],
    ops: [
      { op: 'path', points: [[52, 152], [156, 152], [144, 210], [64, 210]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 118, rx: 78, ry: 34, n: 12, irregularity: 0.05, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 114, rx: 58, ry: 22, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 62, y0: 108, x1: 146, y1: 108, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 58, y0: 120, x1: 150, y1: 120, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 80, y0: 98, x1: 84, y1: 132, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 98, x1: 124, y1: 132, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 104, cy: 112, rx: 34, ry: 12, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'ripple-measuring-ring': {
    canvas: [224, 224],
    ops: [
      { op: 'blob', cx: 112, cy: 150, rx: 92, ry: 58, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 150, rx: 66, ry: 40, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 150, rx: 42, ry: 25, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 150, rx: 18, ry: 11, fill: 'WATER.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 112, y0: 92, x1: 112, y1: 208, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 20, y0: 150, x1: 204, y1: 150, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 112, cy: 204, rx: 56, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'rolled-textile-hive': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[36, 36], [220, 36], [228, 160], [28, 160]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 80, cy: 72, rx: 30, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 148, cy: 72, rx: 30, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 114, cy: 124, rx: 30, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 182, cy: 124, rx: 30, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 80, cy: 72, rx: 16, ry: 16, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 114, cy: 124, rx: 16, ry: 16, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 36, y0: 160, x1: 220, y1: 160, width: 4, color: 'shade' },
    ],
  },
  'rolling-bell-maze': {
    canvas: [224, 200],
    ops: [
      { op: 'rect', x: 32, y: 92, w: 160, h: 100, r: 14, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'rect', x: 44, y: 104, w: 136, h: 76, r: 8, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 56, y0: 104, x1: 56, y1: 156, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 56, y0: 156, x1: 120, y1: 156, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 156, x1: 120, y1: 118, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 118, x1: 168, y1: 118, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 150, cy: 150, rx: 12, ry: 12, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'rect', x: 40, y: 98, w: 144, h: 18, r: 8, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'rolling-leaf-wheel': {
    canvas: [224, 224],
    ops: [
      { op: 'blob', cx: 112, cy: 204, rx: 56, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 112, cy: 118, rx: 84, ry: 84, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 112, cy: 118, rx: 58, ry: 58, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 58, y0: 92, x1: 166, y1: 144, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 58, y0: 144, x1: 166, y1: 92, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 62, x1: 112, y1: 174, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 96, cy: 108, rx: 15, ry: 10, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 128, cy: 130, rx: 15, ry: 10, fill: 'SAGE.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 118, rx: 7, ry: 7, fill: 'fill', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'rolling-treat-drum': {
    canvas: [256, 160],
    ops: [
      { op: 'line', x0: 36, y0: 118, x1: 220, y1: 118, width: 6, color: 'shade' },
      { op: 'line', x0: 40, y0: 150, x1: 216, y1: 150, width: 6, color: 'shade' },
      { op: 'line', x0: 44, y0: 118, x1: 44, y1: 150, width: 5, color: 'shade' },
      { op: 'line', x0: 212, y0: 118, x1: 212, y1: 150, width: 5, color: 'shade' },
      { op: 'rect', x: 68, y: 58, w: 120, h: 66, r: 30, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 188, cy: 91, rx: 18, ry: 33, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 104, cy: 80, rx: 5, ry: 5, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 128, cy: 98, rx: 5, ry: 5, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 150, cy: 78, rx: 5, ry: 5, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 108, cy: 108, rx: 5, ry: 5, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 188, y0: 62, x1: 188, y1: 120, width: 2, color: 'INK.soft' },
    ],
  },
  'root-protection-bridgelet': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[28, 96], [228, 86], [230, 116], [30, 126]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 94, x1: 50, y1: 124, width: 3, color: 'shade' },
      { op: 'line', x0: 84, y0: 92, x1: 86, y1: 122, width: 3, color: 'shade' },
      { op: 'line', x0: 120, y0: 90, x1: 122, y1: 120, width: 3, color: 'shade' },
      { op: 'line', x0: 156, y0: 88, x1: 158, y1: 118, width: 3, color: 'shade' },
      { op: 'line', x0: 192, y0: 87, x1: 194, y1: 117, width: 3, color: 'shade' },
      { op: 'line', x0: 48, y0: 124, x1: 56, y1: 136, width: 8, color: 'shade' },
      { op: 'line', x0: 210, y0: 118, x1: 216, y1: 134, width: 8, color: 'shade' },
      { op: 'line', x0: 34, y0: 110, x1: 224, y1: 100, width: 2, color: 'INK.soft' },
    ],
  },
  'root-scrubbing-board': {
    canvas: [200, 256],
    ops: [
      { op: 'line', x0: 68, y0: 244, x1: 78, y1: 150, width: 11, color: 'shade' },
      { op: 'line', x0: 134, y0: 244, x1: 124, y1: 150, width: 11, color: 'shade' },
      { op: 'path', points: [[40, 40], [160, 52], [150, 160], [52, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 58, x1: 70, y1: 146, width: 4, color: 'shade' },
      { op: 'line', x0: 86, y0: 56, x1: 92, y1: 148, width: 4, color: 'shade' },
      { op: 'line', x0: 110, y0: 54, x1: 112, y1: 150, width: 4, color: 'shade' },
      { op: 'line', x0: 134, y0: 54, x1: 132, y1: 152, width: 4, color: 'shade' },
      { op: 'path', points: [[100, 150], [112, 150], [120, 168], [92, 168]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
    ],
  },
  'root-viewing-planter': {
    canvas: [200, 256],
    ops: [
      { op: 'path', points: [[44, 96], [56, 236], [144, 236], [156, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 98, rx: 58, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 74, y: 140, w: 56, h: 78, r: 6, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 74, y: 140, w: 56, h: 30, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 102, y0: 172, x1: 102, y1: 216, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 102, y0: 188, x1: 86, y1: 212, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 102, y0: 196, x1: 120, y1: 214, width: 2, color: 'INK.soft' },
      { op: 'grass', x: 92, baseY: 96, height: 40, lean: -7, width: 5, color: 'SAGE.mid' },
      { op: 'grass', x: 112, baseY: 96, height: 34, lean: 9, width: 5, color: 'SAGE.deep' },
    ],
  },
  'rotating-keepsake-drawer': {
    canvas: [208, 208],
    ops: [
      { op: 'blob', cx: 104, cy: 190, rx: 66, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 60], [164, 60], [170, 184], [38, 184]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 60, rx: 62, ry: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 78, x1: 104, y1: 182, width: 3, color: 'shade' },
      { op: 'line', x0: 52, y0: 108, x1: 156, y1: 108, width: 3, color: 'shade' },
      { op: 'line', x0: 50, y0: 146, x1: 158, y1: 146, width: 3, color: 'shade' },
      { op: 'blob', cx: 104, cy: 124, rx: 24, ry: 12, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 80, y0: 60, x1: 78, y1: 40, width: 3, color: 'INK.soft' },
    ],
  },
  'route-choice-post': {
    canvas: [200, 288],
    ops: [
      { op: 'line', x0: 100, y0: 280, x1: 100, y1: 96, width: 12, color: 'shade' },
      { op: 'blob', cx: 100, cy: 278, rx: 34, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[96, 100], [150, 86], [168, 98], [150, 110], [96, 124]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[104, 140], [44, 134], [26, 146], [44, 158], [104, 164]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[96, 182], [148, 196], [166, 210], [146, 220], [96, 206]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 112, rx: 7, ry: 7, fill: 'shade', outline: 'INK.line', lineWidth: 2 },
      { op: 'line', x0: 100, y0: 148, x1: 100, y1: 198, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 100, cy: 108, rx: 5, ry: 5, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'route-memory-wheel': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 112, y0: 212, x1: 112, y1: 140, width: 10, color: 'shade' },
      { op: 'blob', cx: 112, cy: 210, rx: 36, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 112, cy: 104, rx: 76, ry: 76, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'line', x0: 112, y0: 28, x1: 112, y1: 180, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 36, y0: 104, x1: 188, y1: 104, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 112, cy: 56, rx: 12, ry: 12, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 160, cy: 104, rx: 12, ry: 12, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 64, cy: 104, rx: 12, ry: 12, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 104, rx: 8, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'route-readiness-board': {
    canvas: [200, 200],
    ops: [
      { op: 'path', points: [[70, 186], [130, 186], [122, 156], [78, 156]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 40, y: 36, w: 120, h: 124, r: 14, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 58, y0: 66, x1: 142, y1: 66, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 58, y0: 100, x1: 142, y1: 100, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 58, y0: 134, x1: 142, y1: 134, width: 4, color: 'INK.soft' },
      { op: 'rect', x: 120, y: 56, w: 22, h: 20, r: 5, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 62, y: 90, w: 22, h: 20, r: 5, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 92, y: 124, w: 22, h: 20, r: 5, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'route-scent-capsule': {
    canvas: [160, 288],
    ops: [
      { op: 'rect', x: 40, y: 88, w: 80, h: 190, r: 34, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[40, 180], [44, 278], [116, 278], [120, 180]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 46, y: 54, w: 68, h: 40, r: 16, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 54, y0: 126, x1: 106, y1: 126, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 54, y0: 150, x1: 106, y1: 150, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 54, y0: 174, x1: 106, y1: 174, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 54, y0: 198, x1: 106, y1: 198, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 80, cy: 74, rx: 5, ry: 4, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'route-thread-map': {
    canvas: [224, 256],
    ops: [
      { op: 'line', x0: 56, y0: 244, x1: 58, y1: 150, width: 9, color: 'shade' },
      { op: 'line', x0: 168, y0: 244, x1: 166, y1: 150, width: 9, color: 'shade' },
      { op: 'path', points: [[36, 238], [188, 238], [182, 250], [42, 250]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 36, y: 36, w: 152, h: 120, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 130], [88, 90], [130, 108], [164, 60]], close: false, outline: 'CLAY.deep', lineWidth: 4 },
      { op: 'path', points: [[62, 64], [100, 118], [150, 130]], close: false, outline: 'SAGE.deep', lineWidth: 4 },
      { op: 'blob', cx: 60, cy: 130, rx: 6, ry: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 2 },
      { op: 'blob', cx: 164, cy: 60, rx: 6, ry: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 2 },
      { op: 'blob', cx: 150, cy: 130, rx: 6, ry: 6, fill: 'SAGE.deep', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'safe-return-lantern': {
    canvas: [192, 288],
    ops: [
      { op: 'halo', cx: 110, cy: 150, r: 70 },
      { op: 'line', x0: 80, y0: 280, x1: 80, y1: 96, width: 11, color: 'shade' },
      { op: 'blob', cx: 80, cy: 278, rx: 34, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[60, 98], [100, 98], [148, 128], [148, 176], [100, 176], [60, 176]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[100, 118], [140, 138], [140, 160], [100, 160]], close: true, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[58, 98], [100, 84], [100, 98]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 60, y0: 137, x1: 100, y1: 137, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 120, cy: 140, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'salvage-washing-basket': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[24, 108], [36, 150], [220, 150], [232, 108]], close: true, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 110, rx: 104, ry: 16, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 68, y: 78, w: 120, h: 50, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[62, 78], [78, 58], [178, 58], [194, 78]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 92, y0: 82, x1: 92, y1: 126, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 82, x1: 120, y1: 126, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 148, y0: 82, x1: 148, y1: 126, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 76, y0: 104, x1: 180, y1: 104, width: 2, color: 'INK.soft' },
    ],
  },
  'sanding-dust-cowl': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 80, y: 120, w: 96, h: 48, r: 10, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 120], [88, 52], [216, 52], [216, 120]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 120], [80, 120], [88, 52], [88, 52]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 196, y: 44, w: 22, h: 58, r: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 207, y0: 44, x1: 207, y1: 24, width: 8, color: 'CLAY.deep' },
      { op: 'line', x0: 100, y0: 136, x1: 156, y1: 136, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 100, y0: 152, x1: 156, y1: 152, width: 2, color: 'INK.soft' },
    ],
  },
  'scent-trail-stamper': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 58, y0: 150, x1: 150, y1: 62, width: 10, color: 'shade' },
      { op: 'blob', cx: 150, cy: 54, rx: 18, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 58, cy: 156, rx: 40, ry: 40, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 58, cy: 156, rx: 12, ry: 12, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 58, y0: 118, x1: 58, y1: 194, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 20, y0: 156, x1: 96, y1: 156, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 96, cy: 206, rx: 9, ry: 6, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 140, cy: 210, rx: 8, ry: 5, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 182, cy: 208, rx: 7, ry: 5, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'scratch-location-board': {
    canvas: [208, 256],
    ops: [
      { op: 'path', points: [[68, 242], [140, 242], [132, 206], [76, 206]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 40, y: 48, w: 128, h: 160, r: 14, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 58, y: 66, w: 42, h: 58, r: 8, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 108, y: 66, w: 42, h: 58, r: 8, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 58, y: 132, w: 42, h: 58, r: 8, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 108, y: 132, w: 42, h: 58, r: 8, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 129, cy: 161, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'season-rotation-cabinet': {
    canvas: [224, 224],
    ops: [
      { op: 'blob', cx: 112, cy: 206, rx: 60, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[60, 52], [164, 52], [176, 196], [48, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[164, 52], [200, 72], [208, 196], [176, 196]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 56, x1: 112, y1: 192, width: 3, color: 'shade' },
      { op: 'blob', cx: 85, cy: 120, rx: 12, ry: 18, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 140, cy: 120, rx: 12, ry: 18, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 36, rx: 18, ry: 8, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 112, y0: 52, x1: 112, y1: 40, width: 4, color: 'INK.soft' },
    ],
  },
  'season-token-drawer': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 28, y: 56, w: 200, h: 96, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 128, y0: 56, x1: 128, y1: 152, width: 3, color: 'shade' },
      { op: 'line', x0: 28, y0: 104, x1: 228, y1: 104, width: 3, color: 'shade' },
      { op: 'rect', x: 92, y: 80, w: 40, h: 40, r: 4, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 70, cy: 80, rx: 7, ry: 7, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 186, cy: 80, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 70, cy: 128, rx: 7, ry: 7, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 186, cy: 128, rx: 7, ry: 7, fill: 'SAGE.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 128, cy: 150, rx: 110, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'season-turning-wheel': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 112, y0: 108, x1: 112, y1: 214, width: 12, color: 'shade' },
      { op: 'path', points: [[112, 108], [112, 16], [180, 40], [204, 108]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[112, 108], [204, 108], [180, 176], [112, 200]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[112, 108], [112, 200], [44, 176], [20, 108]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[112, 108], [20, 108], [44, 40], [112, 16]], close: true, fill: 'SAGE.deep', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 108, rx: 18, ry: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 108, rx: 6, ry: 6, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'seasonal-door-swag-frame': {
    canvas: [160, 256],
    ops: [
      { op: 'path', points: [[80, 18], [64, 12], [60, 26], [74, 30]], close: false, outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[78, 30], [40, 72], [26, 140], [44, 196], [80, 224]], close: false, outline: 'fill', lineWidth: 7 },
      { op: 'path', points: [[82, 30], [120, 72], [134, 140], [116, 196], [80, 224]], close: false, outline: 'fill', lineWidth: 7 },
      { op: 'path', points: [[80, 224], [72, 244], [76, 252]], close: false, outline: 'shade', lineWidth: 6 },
      { op: 'blob', cx: 80, cy: 34, rx: 13, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 40, cy: 92, rx: 15, ry: 11, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 124, cy: 92, rx: 15, ry: 11, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 30, cy: 150, rx: 13, ry: 10, fill: 'SAGE.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 130, cy: 150, rx: 13, ry: 10, fill: 'SAGE.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 80, cy: 210, rx: 17, ry: 13, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 74, y0: 24, x1: 86, y1: 24, width: 3, color: 'shade' },
      { op: 'line', x0: 74, y0: 42, x1: 86, y1: 42, width: 3, color: 'shade' },
    ],
  },
  'seed-flight-tunnel': {
    canvas: [256, 112],
    ops: [
      { op: 'path', points: [[44, 100], [44, 40], [210, 40], [210, 100]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 50, y0: 104, x1: 50, y1: 98, width: 10, color: 'shade' },
      { op: 'line', x0: 150, y0: 104, x1: 150, y1: 98, width: 10, color: 'shade' },
      { op: 'path', points: [[210, 46], [244, 30], [244, 94], [210, 94]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 52], [12, 38], [12, 88], [44, 90]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 80, y0: 48, x1: 80, y1: 94, width: 2, color: 'shade' },
      { op: 'line', x0: 118, y0: 48, x1: 118, y1: 94, width: 2, color: 'shade' },
      { op: 'line', x0: 156, y0: 48, x1: 156, y1: 94, width: 2, color: 'shade' },
    ],
  },
  'seed-paper-basin': {
    canvas: [224, 224],
    ops: [
      { op: 'path', points: [[36, 120], [188, 120], [208, 208], [16, 208]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 204, rx: 92, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 112, cy: 122, rx: 78, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 120, rx: 58, ry: 11, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 48, y: 96, w: 128, h: 18, r: 4, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 80, y0: 96, x1: 80, y1: 114, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 144, y0: 96, x1: 144, y1: 114, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 96, cy: 118, rx: 5, ry: 4, fill: 'SAGE.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 128, cy: 124, rx: 5, ry: 4, fill: 'SAGE.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'seed-pod-shaker-rack': {
    canvas: [256, 256],
    ops: [
      { op: 'line', x0: 52, y0: 244, x1: 56, y1: 64, width: 11, color: 'shade' },
      { op: 'line', x0: 204, y0: 244, x1: 200, y1: 64, width: 11, color: 'shade' },
      { op: 'path', points: [[42, 60], [214, 60], [214, 82], [42, 82]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 78, y0: 80, x1: 78, y1: 118, width: 2, color: 'shade' },
      { op: 'line', x0: 128, y0: 80, x1: 128, y1: 150, width: 2, color: 'shade' },
      { op: 'line', x0: 178, y0: 80, x1: 178, y1: 188, width: 2, color: 'shade' },
      { op: 'blob', cx: 78, cy: 132, rx: 15, ry: 22, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 128, cy: 166, rx: 17, ry: 26, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 178, cy: 204, rx: 19, ry: 30, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'seed-roasting-pan': {
    canvas: [256, 176],
    ops: [
      { op: 'line', x0: 70, y0: 150, x1: 76, y1: 110, width: 9, color: 'shade' },
      { op: 'line', x0: 150, y0: 150, x1: 144, y1: 110, width: 9, color: 'shade' },
      { op: 'blob', cx: 110, cy: 104, rx: 76, ry: 30, n: 12, irregularity: 0.05, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 110, cy: 100, rx: 58, ry: 18, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 186, y0: 98, x1: 244, y1: 76, width: 9, color: 'shade' },
      { op: 'speckle', x: 68, y: 92, w: 84, h: 16, count: 14, maxR: 2, alpha: 0.4, color: 'INK.soft' },
      { op: 'blob', cx: 110, cy: 100, rx: 34, ry: 9, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'seed-sharing-fence': {
    canvas: [256, 200],
    ops: [
      { op: 'line', x0: 52, y0: 186, x1: 52, y1: 64, width: 11, color: 'shade' },
      { op: 'line', x0: 204, y0: 186, x1: 204, y1: 64, width: 11, color: 'shade' },
      { op: 'path', points: [[30, 84], [226, 80], [228, 104], [32, 108]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[30, 128], [226, 124], [228, 148], [32, 152]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[88, 104], [148, 104], [148, 124], [88, 124]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 118, cy: 110, rx: 14, ry: 7, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 62, y0: 92, x1: 196, y1: 89, width: 2, color: 'shade' },
    ],
  },
  'seed-spacing-wheel': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 112, y0: 140, x1: 188, y1: 48, width: 7, color: 'shade' },
      { op: 'blob', cx: 96, cy: 140, rx: 62, ry: 62, n: 14, irregularity: 0.04, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 96, cy: 140, rx: 18, ry: 18, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 96, cy: 92, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 144, cy: 140, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 96, cy: 188, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 48, cy: 140, rx: 7, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 24, y0: 210, x1: 48, y1: 210, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 72, y0: 210, x1: 96, y1: 210, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 210, x1: 144, y1: 210, width: 4, color: 'INK.soft' },
    ],
  },
  'seedling-carry-tray': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 52, y0: 210, x1: 56, y1: 150, width: 4, color: 'shade' },
      { op: 'line', x0: 172, y0: 210, x1: 168, y1: 150, width: 4, color: 'shade' },
      { op: 'path', points: [[36, 108], [44, 168], [180, 168], [188, 108]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 48, y: 108, w: 128, h: 24, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 80, y0: 110, x1: 80, y1: 166, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 108, x1: 112, y1: 166, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 144, y0: 110, x1: 144, y1: 166, width: 2, color: 'INK.soft' },
      { op: 'path', points: [[28, 108], [20, 72], [40, 68], [48, 104]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[196, 108], [204, 72], [184, 68], [176, 104]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'grass', x: 112, baseY: 110, height: 26, lean: -4, width: 4, color: 'SAGE.light' },
    ],
  },
  'self-watering-spike': {
    canvas: [176, 256],
    ops: [
      { op: 'path', points: [[78, 150], [70, 210], [88, 238], [106, 210], [98, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 96, rx: 52, ry: 56, n: 13, irregularity: 0.05, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 70, cy: 86, rx: 18, ry: 24, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[74, 150], [78, 150], [82, 180], [70, 180]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 48, rx: 12, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 80, y0: 196, x1: 92, y1: 196, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 82, y0: 210, x1: 94, y1: 210, width: 2, color: 'INK.soft' },
    ],
  },
  'sensor-drying-cabinet': {
    canvas: [256, 200],
    ops: [
      { op: 'rect', x: 36, y: 56, w: 184, h: 134, r: 14, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 56, y: 76, w: 144, h: 98, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 70, y0: 92, x1: 186, y1: 92, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 70, y0: 112, x1: 186, y1: 112, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 70, y0: 132, x1: 186, y1: 132, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 70, y0: 152, x1: 186, y1: 152, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 128, cy: 124, rx: 26, ry: 34, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 62, y0: 190, x1: 62, y1: 182, width: 9, color: 'shade' },
      { op: 'line', x0: 194, y0: 190, x1: 194, y1: 182, width: 9, color: 'shade' },
    ],
  },
  'shade-cloth-sail': {
    canvas: [256, 176],
    ops: [
      { op: 'line', x0: 48, y0: 166, x1: 52, y1: 56, width: 9, color: 'shade' },
      { op: 'line', x0: 216, y0: 166, x1: 210, y1: 92, width: 9, color: 'shade' },
      { op: 'path', points: [[44, 52], [212, 88], [214, 116], [46, 80]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 52, y0: 62, x1: 210, y1: 98, width: 2, color: 'shade' },
      { op: 'line', x0: 50, y0: 72, x1: 208, y1: 108, width: 2, color: 'shade' },
      { op: 'line', x0: 48, y0: 58, x1: 44, y1: 48, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 212, y0: 92, x1: 216, y1: 84, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 48, cy: 166, rx: 18, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 216, cy: 166, rx: 18, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'shadow-length-board': {
    canvas: [224, 256],
    ops: [
      { op: 'path', points: [[40, 240], [40, 120], [196, 120], [196, 240]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 248, x1: 48, y1: 238, width: 12, color: 'shade' },
      { op: 'line', x0: 188, y0: 248, x1: 188, y1: 238, width: 12, color: 'shade' },
      { op: 'line', x0: 118, y0: 120, x1: 118, y1: 24, width: 8, color: 'CLAY.mid' },
      { op: 'line', x0: 118, y0: 150, x1: 172, y1: 150, width: 4, color: 'shade' },
      { op: 'line', x0: 118, y0: 180, x1: 172, y1: 180, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 118, y0: 210, x1: 172, y1: 210, width: 3, color: 'INK.soft' },
      { op: 'path', points: [[150, 142], [176, 142], [176, 158], [150, 158]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'shadow-story-wheel': {
    canvas: [224, 200],
    ops: [
      { op: 'line', x0: 52, y0: 188, x1: 56, y1: 110, width: 9, color: 'shade' },
      { op: 'line', x0: 172, y0: 188, x1: 168, y1: 110, width: 9, color: 'shade' },
      { op: 'rect', x: 64, y: 48, w: 96, h: 80, r: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 98, cy: 80, rx: 9, ry: 9, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[124, 72], [140, 64], [140, 96], [124, 90]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 108, rx: 6, ry: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 160, y0: 88, x1: 188, y1: 88, width: 4, color: 'shade' },
      { op: 'blob', cx: 112, cy: 188, rx: 64, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'shared-calendar-tree': {
    canvas: [224, 288],
    ops: [
      { op: 'rect', x: 80, y: 230, w: 64, h: 52, r: 10, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 230, x1: 112, y1: 86, width: 13, color: 'fill' },
      { op: 'line', x0: 112, y0: 150, x1: 58, y1: 104, width: 8, color: 'fill' },
      { op: 'line', x0: 112, y0: 150, x1: 166, y1: 104, width: 8, color: 'fill' },
      { op: 'path', points: [[58, 104], [44, 128], [58, 150], [72, 128]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[112, 86], [98, 112], [112, 138], [126, 112]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[166, 104], [152, 128], [166, 150], [180, 128]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 58, y0: 128, x1: 58, y1: 134, width: 3, color: 'shade' },
      { op: 'line', x0: 166, y0: 128, x1: 166, y1: 134, width: 3, color: 'shade' },
    ],
  },
  'shared-choice-shelf': {
    canvas: [224, 200],
    ops: [
      { op: 'rect', x: 28, y: 52, w: 168, h: 120, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 68], [92, 68], [92, 156], [40, 156]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[132, 68], [184, 68], [184, 156], [132, 156]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 80], [124, 80], [120, 150], [104, 150]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 66, cy: 120, rx: 18, ry: 18, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 158, cy: 120, rx: 18, ry: 18, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 36, y0: 172, x1: 188, y1: 172, width: 4, color: 'shade' },
    ],
  },
  'shared-hypothesis-board': {
    canvas: [224, 256],
    ops: [
      { op: 'path', points: [[56, 240], [40, 180], [184, 180], [168, 240]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 182], [44, 44], [180, 44], [180, 182]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 50, x1: 112, y1: 176, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 68, y0: 80, x1: 68, y1: 160, width: 4, color: 'shade' },
      { op: 'line', x0: 156, y0: 80, x1: 156, y1: 160, width: 4, color: 'shade' },
      { op: 'rect', x: 54, y: 96, w: 28, h: 18, r: 4, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 142, y: 128, w: 28, h: 18, r: 4, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 54, y0: 62, x1: 82, y1: 62, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 142, y0: 62, x1: 170, y1: 62, width: 3, color: 'INK.soft' },
    ],
  },
  'shared-photo-frame': {
    canvas: [256, 288],
    ops: [
      { op: 'path', points: [[52, 260], [204, 260], [210, 276], [46, 276]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 92, y0: 260, x1: 98, y1: 196, width: 11, color: 'shade' },
      { op: 'line', x0: 164, y0: 260, x1: 158, y1: 196, width: 11, color: 'shade' },
      { op: 'path', points: [[40, 52], [216, 52], [216, 200], [40, 200]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 66, y: 78, w: 124, h: 96, r: 8, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[100, 96], [112, 96], [116, 140], [122, 168], [94, 168], [100, 140]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[140, 138], [172, 134], [176, 150], [178, 168], [138, 168], [140, 150]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 228, y0: 260, x1: 230, y1: 150, width: 9, color: 'shade' },
      { op: 'rect', x: 216, y: 126, w: 30, h: 28, r: 7, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 231, cy: 140, rx: 6, ry: 6, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'shared-recipe-board': {
    canvas: [224, 272],
    ops: [
      { op: 'line', x0: 58, y0: 256, x1: 62, y1: 150, width: 11, color: 'shade' },
      { op: 'line', x0: 166, y0: 256, x1: 162, y1: 150, width: 11, color: 'shade' },
      { op: 'rect', x: 34, y: 40, w: 156, h: 120, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 100, x1: 176, y1: 100, width: 3, color: 'shade' },
      { op: 'blob', cx: 72, cy: 74, rx: 18, ry: 18, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[120, 56], [150, 56], [136, 92], [112, 92]], close: true, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 52, y: 116, w: 36, h: 30, r: 5, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 138, cy: 130, rx: 18, ry: 14, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 160, cy: 74, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'shared-sip-tray': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 28, y: 104, w: 200, h: 52, r: 14, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[28, 104], [228, 104], [222, 84], [34, 84]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 78, cy: 96, rx: 30, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 150, cy: 96, rx: 30, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 190, y: 86, w: 34, h: 20, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 207, cy: 96, rx: 7, ry: 5, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'shared-song-banner': {
    canvas: [200, 288],
    ops: [
      { op: 'line', x0: 52, y0: 40, x1: 52, y1: 272, width: 7, color: 'shade' },
      { op: 'path', points: [[40, 272], [64, 272], [60, 260], [44, 260]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[52, 44], [156, 40], [156, 56], [52, 56]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 56], [148, 56], [148, 200], [104, 232], [60, 200]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 92, rx: 18, ry: 18, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[104, 128], [122, 158], [86, 158]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 104, cy: 190, rx: 15, ry: 15, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 60, y0: 74, x1: 148, y1: 74, width: 2, color: 'INK.soft' },
    ],
  },
  'shared-task-token-bowl': {
    canvas: [208, 200],
    ops: [
      { op: 'path', points: [[40, 118], [58, 182], [150, 182], [168, 118]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 118, rx: 66, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 104, cy: 116, rx: 52, ry: 12, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 76, cy: 108, rx: 16, ry: 9, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 118, cy: 104, rx: 16, ry: 9, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 98, rx: 16, ry: 9, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 54, y0: 150, x1: 154, y1: 150, width: 2, color: 'shade' },
    ],
  },
  'shared-wish-planter': {
    canvas: [208, 272],
    ops: [
      { op: 'path', points: [[42, 150], [58, 256], [150, 256], [166, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 150, rx: 64, ry: 16, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 56, y0: 190, x1: 152, y1: 190, width: 3, color: 'shade' },
      { op: 'line', x0: 76, y0: 150, x1: 76, y1: 92, width: 4, color: 'SAGE.deep' },
      { op: 'path', points: [[76, 96], [58, 72], [78, 68], [88, 90]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 120, y0: 150, x1: 124, y1: 86, width: 4, color: 'SAGE.deep' },
      { op: 'path', points: [[124, 90], [118, 64], [140, 68], [138, 92]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[96, 150], [100, 104], [112, 108], [110, 150]], close: true, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
    ],
  },
  'shell-listening-rack': {
    canvas: [256, 224],
    ops: [
      { op: 'line', x0: 56, y0: 210, x1: 60, y1: 70, width: 11, color: 'shade' },
      { op: 'line', x0: 200, y0: 210, x1: 196, y1: 70, width: 11, color: 'shade' },
      { op: 'path', points: [[48, 72], [208, 56], [210, 76], [50, 92]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 88, y0: 86, x1: 86, y1: 124, width: 4, color: 'shade' },
      { op: 'line', x0: 160, y0: 74, x1: 162, y1: 150, width: 4, color: 'shade' },
      { op: 'blob', cx: 86, cy: 138, rx: 22, ry: 18, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 162, cy: 166, rx: 26, ry: 21, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 78, y0: 134, x1: 94, y1: 140, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 152, y0: 162, x1: 172, y1: 168, width: 2, color: 'INK.soft' },
    ],
  },
  'shell-patch-press': {
    canvas: [200, 224],
    ops: [
      { op: 'rect', x: 44, y: 168, w: 112, h: 44, r: 10, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 168, x1: 62, y1: 80, width: 12, color: 'fill' },
      { op: 'line', x0: 138, y0: 168, x1: 138, y1: 80, width: 12, color: 'fill' },
      { op: 'path', points: [[50, 84], [150, 84], [140, 52], [60, 52]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[64, 96], [136, 96], [120, 140], [80, 140]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 150, rx: 18, ry: 8, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 68, rx: 10, ry: 7, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'shell-polishing-turntable': {
    canvas: [208, 224],
    ops: [
      { op: 'path', points: [[78, 210], [130, 210], [124, 168], [84, 168]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 168, rx: 86, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 150, rx: 70, ry: 18, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 150, x1: 168, y1: 150, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 132, x1: 104, y1: 168, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 190, y0: 166, x1: 172, y1: 150, width: 7, color: 'shade' },
      { op: 'blob', cx: 192, cy: 168, rx: 11, ry: 11, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'shell-rinsing-wheel': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 62, y0: 120, x1: 56, y1: 210, width: 10, color: 'shade' },
      { op: 'line', x0: 162, y0: 120, x1: 168, y1: 210, width: 10, color: 'shade' },
      { op: 'blob', cx: 110, cy: 108, rx: 62, ry: 60, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 86, x1: 158, y1: 86, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 54, y0: 108, x1: 166, y1: 108, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 62, y0: 130, x1: 158, y1: 130, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 86, y0: 52, x1: 86, y1: 164, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 134, y0: 52, x1: 134, y1: 164, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 110, cy: 108, rx: 9, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 110, y0: 108, x1: 190, y1: 92, width: 5, color: 'shade' },
      { op: 'blob', cx: 196, cy: 88, rx: 12, ry: 11, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'shell-tone-basin': {
    canvas: [224, 224],
    ops: [
      { op: 'blob', cx: 112, cy: 172, rx: 92, ry: 40, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[28, 150], [44, 196], [180, 196], [196, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 146, rx: 84, ry: 26, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 142, rx: 68, ry: 17, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 76, cy: 140, rx: 15, ry: 12, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 138, rx: 12, ry: 10, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 146, cy: 140, rx: 10, ry: 8, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 90, y0: 146, x1: 120, y1: 144, width: 2, color: 'WATER.edge' },
    ],
  },
  'shore-picnic-anchor': {
    canvas: [160, 256],
    ops: [
      { op: 'path', points: [[68, 120], [92, 120], [88, 150], [72, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[72, 150], [100, 168], [68, 184], [96, 200], [64, 216], [88, 236], [76, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 150, x1: 80, y1: 244, width: 3, color: 'shade' },
      { op: 'path', points: [[40, 96], [120, 96], [116, 122], [44, 122]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 44, y: 104, w: 14, h: 20, r: 4, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'rect', x: 102, y: 104, w: 14, h: 20, r: 4, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 58, y0: 108, x1: 102, y1: 108, width: 2, color: 'SAGE.deep' },
    ],
  },
  'shore-towel-tree': {
    canvas: [200, 256],
    ops: [
      { op: 'line', x0: 100, y0: 40, x1: 100, y1: 236, width: 11, color: 'fill' },
      { op: 'blob', cx: 100, cy: 240, rx: 48, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 100, y0: 78, x1: 48, y1: 56, width: 8, color: 'fill' },
      { op: 'line', x0: 100, y0: 96, x1: 152, y1: 74, width: 8, color: 'fill' },
      { op: 'line', x0: 100, y0: 120, x1: 150, y1: 108, width: 8, color: 'fill' },
      { op: 'path', points: [[36, 52], [78, 62], [72, 140], [30, 128]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 40, y0: 80, x1: 74, y1: 88, width: 2, color: 'SAGE.deep' },
      { op: 'line', x0: 38, y0: 104, x1: 72, y1: 112, width: 2, color: 'SAGE.deep' },
    ],
  },
  'shoreline-litter-sieve': {
    canvas: [200, 256],
    ops: [
      { op: 'line', x0: 66, y0: 150, x1: 60, y1: 244, width: 10, color: 'shade' },
      { op: 'line', x0: 134, y0: 150, x1: 140, y1: 244, width: 10, color: 'shade' },
      { op: 'path', points: [[34, 118], [166, 118], [154, 156], [46, 156]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[50, 52], [150, 52], [166, 118], [34, 118]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 70, y0: 58, x1: 78, y1: 114, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 100, y0: 56, x1: 100, y1: 114, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 130, y0: 58, x1: 122, y1: 114, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 46, y0: 82, x1: 154, y1: 82, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 78, y0: 120, x1: 122, y1: 120, width: 2, color: 'shade' },
    ],
  },
  'shoreline-sample-tray': {
    canvas: [224, 256],
    ops: [
      { op: 'line', x0: 58, y0: 160, x1: 52, y1: 248, width: 11, color: 'shade' },
      { op: 'line', x0: 166, y0: 160, x1: 172, y1: 248, width: 11, color: 'shade' },
      { op: 'path', points: [[36, 108], [188, 108], [176, 168], [48, 168]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 86, y0: 110, x1: 80, y1: 166, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 138, y0: 110, x1: 144, y1: 166, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 62, cy: 140, rx: 9, ry: 7, fill: 'WATER.sand', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 138, rx: 9, ry: 7, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 160, cy: 140, rx: 9, ry: 7, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 62, y0: 166, x1: 62, y1: 182, width: 3, color: 'WATER.edge' },
      { op: 'line', x0: 112, y0: 166, x1: 112, y1: 182, width: 3, color: 'WATER.edge' },
      { op: 'line', x0: 160, y0: 166, x1: 160, y1: 182, width: 3, color: 'WATER.edge' },
    ],
  },
  'shortcut-gate-token': {
    canvas: [192, 288],
    ops: [
      { op: 'line', x0: 96, y0: 280, x1: 96, y1: 150, width: 12, color: 'shade' },
      { op: 'blob', cx: 96, cy: 278, rx: 34, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 96, cy: 92, rx: 62, ry: 62, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[96, 30], [158, 92], [96, 92]], close: true, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[96, 92], [96, 154], [34, 92]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 34, y0: 92, x1: 158, y1: 92, width: 3, color: 'INK.line' },
      { op: 'blob', cx: 96, cy: 92, rx: 9, ry: 9, fill: 'fill', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 124, cy: 64, rx: 5, ry: 5, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'sign-cleaning-squeegee': {
    canvas: [256, 112],
    ops: [
      { op: 'line', x0: 36, y0: 36, x1: 188, y1: 62, width: 11, color: 'shade' },
      { op: 'path', points: [[182, 46], [228, 54], [238, 84], [190, 80]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 192, y0: 82, x1: 236, y1: 86, width: 4, color: 'INK.line' },
      { op: 'blob', cx: 70, cy: 62, rx: 18, ry: 14, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 70, cy: 56, rx: 12, ry: 4, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 188, y0: 56, x1: 210, y1: 60, width: 2, color: 'INK.soft' },
    ],
  },
  'sleep-timer-lantern': {
    canvas: [176, 288],
    ops: [
      { op: 'halo', cx: 88, cy: 200, r: 52 },
      { op: 'path', points: [[48, 272], [48, 90], [128, 90], [128, 272]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[62, 104], [114, 104], [88, 150]], close: true, fill: 'WATER.sand', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[62, 196], [114, 196], [88, 150]], close: true, fill: 'WATER.sand', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[68, 196], [108, 196], [100, 176], [76, 176]], close: true, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 52, y: 214, w: 72, h: 20, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 88, cy: 244, rx: 4, ry: 4, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 270, rx: 48, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'slope-rest-rail': {
    canvas: [200, 288],
    ops: [
      { op: 'line', x0: 72, y0: 280, x1: 80, y1: 110, width: 13, color: 'shade' },
      { op: 'blob', cx: 74, cy: 278, rx: 30, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[58, 118], [160, 52], [172, 68], [70, 134]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 126, x1: 150, y1: 68, width: 2, color: 'INK.soft' },
      { op: 'path', points: [[150, 64], [166, 62], [176, 76], [166, 92], [154, 86]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 80, y0: 150, x1: 104, y1: 150, width: 7, color: 'shade' },
      { op: 'line', x0: 104, y0: 150, x1: 110, y1: 178, width: 7, color: 'shade' },
      { op: 'blob', cx: 110, cy: 182, rx: 7, ry: 7, fill: 'fill', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'small-parts-catchmat': {
    canvas: [256, 160],
    ops: [
      { op: 'blob', cx: 128, cy: 118, rx: 108, ry: 34, n: 14, irregularity: 0.05, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 110, rx: 80, ry: 22, n: 14, irregularity: 0.05, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[20, 118], [40, 92], [216, 92], [236, 118]], close: false, outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 92, x1: 216, y1: 92, width: 7, color: 'shade' },
      { op: 'blob', cx: 108, cy: 110, rx: 5, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 138, cy: 116, rx: 5, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 156, cy: 104, rx: 4, ry: 3, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'snail-crossing-bridge': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[20, 140], [24, 118], [60, 100], [128, 92], [196, 100], [232, 118], [236, 140]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[24, 118], [60, 100], [128, 92], [196, 100], [232, 118], [210, 124], [128, 116], [46, 124]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 60, y0: 110, x1: 80, y1: 105, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 110, y0: 100, x1: 146, y1: 99, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 176, y0: 104, x1: 196, y1: 109, width: 2, color: 'INK.soft' },
      { op: 'speckle', x: 40, y: 96, w: 176, h: 30, count: 18, maxR: 1.5, alpha: 0.16, color: 'INK.grain' },
    ],
  },
  'sniffing-flower-pots': {
    canvas: [256, 200],
    ops: [
      { op: 'blob', cx: 128, cy: 170, rx: 104, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 128, cy: 164, rx: 96, ry: 16, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[52, 158], [58, 118], [92, 118], [98, 158]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[112, 158], [118, 118], [152, 118], [158, 158]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[172, 158], [178, 118], [212, 118], [218, 158]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 75, cy: 104, rx: 18, ry: 15, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 135, cy: 102, rx: 18, ry: 15, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 195, cy: 104, rx: 18, ry: 15, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 128, cy: 174, rx: 7, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'snow-depth-post': {
    canvas: [144, 240],
    ops: [
      { op: 'blob', cx: 72, cy: 224, rx: 44, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[56, 224], [58, 48], [86, 48], [88, 224]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 57, y: 60, w: 30, h: 40, r: 0, fill: 'WATER.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 57, y: 100, w: 30, h: 40, r: 0, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 57, y: 140, w: 30, h: 40, r: 0, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[58, 48], [72, 34], [86, 48]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 57, y0: 180, x1: 87, y1: 180, width: 2, color: 'shade' },
    ],
  },
  'snow-melt-tray': {
    canvas: [224, 192],
    ops: [
      { op: 'line', x0: 64, y0: 186, x1: 70, y1: 120, width: 9, color: 'shade' },
      { op: 'line', x0: 168, y0: 186, x1: 162, y1: 120, width: 9, color: 'shade' },
      { op: 'path', points: [[40, 96], [200, 96], [210, 118], [184, 124], [48, 124], [30, 118]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[200, 100], [230, 86], [226, 100], [204, 110]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 116, cy: 104, rx: 70, ry: 11, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 70, y0: 104, x1: 150, y1: 104, width: 2, color: 'WATER.edge' },
      { op: 'blob', cx: 90, cy: 100, rx: 8, ry: 4, fill: 'PAPER.skyTop', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'soft-alarm-orb': {
    canvas: [200, 288],
    ops: [
      { op: 'halo', cx: 100, cy: 140, r: 62 },
      { op: 'rect', x: 52, y: 232, w: 96, h: 24, r: 10, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[56, 232], [44, 150], [100, 80], [156, 150], [144, 232]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 150, rx: 40, ry: 40, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 60, y0: 120, x1: 140, y1: 120, width: 2, color: 'shade' },
      { op: 'line', x0: 52, y0: 160, x1: 148, y1: 160, width: 2, color: 'shade' },
      { op: 'line', x0: 80, y0: 88, x1: 74, y1: 228, width: 2, color: 'shade' },
      { op: 'line', x0: 120, y0: 88, x1: 126, y1: 228, width: 2, color: 'shade' },
      { op: 'blob', cx: 100, cy: 150, rx: 7, ry: 7, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'soft-block-builder': {
    canvas: [224, 224],
    ops: [
      { op: 'rect', x: 48, y: 150, w: 120, h: 58, r: 18, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[72, 148], [108, 84], [144, 148]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 150, cy: 120, rx: 30, ry: 30, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 86, cy: 172, rx: 4, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 132, cy: 172, rx: 4, ry: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 108, cy: 138, rx: 4, ry: 4, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 60, y0: 180, x1: 156, y1: 180, width: 2, color: 'INK.soft' },
    ],
  },
  'soft-step-runner': {
    canvas: [256, 112],
    ops: [
      { op: 'path', points: [[28, 52], [228, 48], [232, 86], [24, 90]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 30, y: 50, w: 22, h: 22, r: 4, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 204, y: 48, w: 22, h: 22, r: 4, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 36, y0: 62, x1: 220, y1: 58, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 34, y0: 78, x1: 222, y1: 74, width: 2, color: 'INK.soft' },
      { op: 'speckle', x: 68, y: 54, w: 120, h: 30, count: 6, maxR: 2, alpha: 0.26, color: 'INK.grain' },
    ],
  },
  'soil-layer-column': {
    canvas: [160, 256],
    ops: [
      { op: 'path', points: [[54, 240], [50, 80], [110, 80], [106, 240]], close: true, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 80, cy: 244, rx: 40, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[52, 240], [107, 240], [107, 200], [53, 200]], close: true, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[53, 200], [107, 200], [106, 160], [53, 160]], close: true, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[53, 160], [106, 160], [105, 124], [52, 124]], close: true, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[52, 124], [105, 124], [104, 90], [51, 90]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 78, cy: 80, rx: 29, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[64, 80], [64, 60], [94, 60], [94, 80]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'soil-moisture-flower': {
    canvas: [176, 256],
    ops: [
      { op: 'line', x0: 88, y0: 244, x1: 88, y1: 120, width: 6, color: 'shade' },
      { op: 'path', points: [[80, 244], [70, 232], [88, 228], [106, 232], [96, 244]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[88, 120], [60, 92], [64, 64], [88, 78]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[88, 120], [116, 92], [112, 64], [88, 78]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[88, 114], [68, 116], [58, 138], [80, 134]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[88, 114], [108, 116], [118, 138], [96, 134]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 88, cy: 104, rx: 13, ry: 13, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 88, y0: 150, x1: 74, y1: 176, width: 2, color: 'INK.soft' },
    ],
  },
  'sound-direction-ring': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 112, y0: 210, x1: 112, y1: 140, width: 12, color: 'shade' },
      { op: 'blob', cx: 112, cy: 214, rx: 40, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 112, cy: 124, rx: 54, ry: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 120, rx: 36, ry: 11, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[58, 118], [20, 90], [12, 108], [50, 128]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 18, cy: 98, rx: 13, ry: 18, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[166, 118], [204, 90], [212, 108], [174, 128]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 206, cy: 98, rx: 13, ry: 18, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'sound-follow-posts': {
    canvas: [256, 224],
    ops: [
      { op: 'path', points: [[52, 210], [58, 140], [70, 140], [64, 210]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[120, 210], [124, 108], [136, 108], [132, 210]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[188, 210], [192, 170], [204, 170], [200, 210]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[52, 132], [72, 132], [68, 164], [56, 164]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[118, 100], [140, 100], [136, 138], [122, 138]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[186, 162], [208, 162], [204, 192], [192, 192]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 129, cy: 138, rx: 7, ry: 6, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 60, y0: 140, x1: 64, y1: 160, width: 2, color: 'INK.soft' },
    ],
  },
  'sound-map-table': {
    canvas: [224, 224],
    ops: [
      { op: 'line', x0: 76, y0: 212, x1: 84, y1: 150, width: 11, color: 'shade' },
      { op: 'line', x0: 148, y0: 212, x1: 140, y1: 150, width: 11, color: 'shade' },
      { op: 'blob', cx: 112, cy: 118, rx: 86, ry: 40, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 112, cy: 114, rx: 62, ry: 28, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 34, y0: 118, x1: 190, y1: 118, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 82, x1: 112, y1: 154, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 86, cy: 104, rx: 8, ry: 6, fill: 'SAGE.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 140, cy: 128, rx: 8, ry: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 118, cy: 98, rx: 6, ry: 5, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'soup-tasting-stand': {
    canvas: [240, 224],
    ops: [
      { op: 'line', x0: 80, y0: 212, x1: 84, y1: 150, width: 11, color: 'shade' },
      { op: 'line', x0: 160, y0: 212, x1: 156, y1: 150, width: 11, color: 'shade' },
      { op: 'path', points: [[40, 134], [200, 134], [196, 156], [44, 156]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[110, 134], [130, 134], [126, 84], [114, 84]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 120, cy: 72, rx: 26, ry: 16, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[50, 120], [78, 120], [74, 134], [54, 134]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[162, 120], [190, 120], [186, 134], [166, 134]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'specimen-shadow-shelf': {
    canvas: [176, 256],
    ops: [
      { op: 'rect', x: 36, y: 28, w: 104, h: 210, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 50, y: 44, w: 76, h: 52, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 50, y: 110, w: 76, h: 52, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 50, y: 176, w: 76, h: 52, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[78, 90], [98, 90], [92, 52], [84, 52]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 88, cy: 140, rx: 18, ry: 14, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[72, 222], [104, 222], [100, 196], [76, 196]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 48, y0: 238, x1: 128, y1: 238, width: 4, color: 'shade' },
    ],
  },
  'spice-drawer-wheel': {
    canvas: [208, 208],
    ops: [
      { op: 'line', x0: 104, y0: 196, x1: 104, y1: 166, width: 13, color: 'shade' },
      { op: 'blob', cx: 104, cy: 188, rx: 50, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 104, cy: 120, rx: 84, ry: 48, n: 14, irregularity: 0.04, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 74, x1: 104, y1: 166, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 26, y0: 134, x1: 104, y1: 120, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 182, y0: 134, x1: 104, y1: 120, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 56, cy: 118, rx: 15, ry: 18, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 104, cy: 108, rx: 15, ry: 18, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 152, cy: 118, rx: 15, ry: 18, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'spice-steam-pot': {
    canvas: [200, 240],
    ops: [
      { op: 'blob', cx: 100, cy: 170, rx: 68, ry: 56, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 182, rx: 54, ry: 40, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[46, 124], [154, 124], [140, 86], [60, 86]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 82, rx: 14, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'speckle', x: 64, y: 94, w: 72, h: 24, count: 8, maxR: 3, alpha: 0.6, color: 'INK.soft' },
      { op: 'line', x0: 46, y0: 124, x1: 154, y1: 124, width: 2, color: 'INK.soft' },
    ],
  },
  'spring-ribbon-tree': {
    canvas: [200, 288],
    ops: [
      { op: 'path', points: [[84, 272], [92, 140], [108, 140], [116, 272]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 150, x1: 52, y1: 86, width: 7, color: 'shade' },
      { op: 'line', x0: 100, y0: 150, x1: 100, y1: 54, width: 7, color: 'shade' },
      { op: 'line', x0: 100, y0: 150, x1: 148, y1: 86, width: 7, color: 'shade' },
      { op: 'path', points: [[52, 86], [40, 108], [52, 116], [58, 98]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[100, 54], [88, 76], [100, 84], [108, 68]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[148, 86], [160, 108], [148, 116], [142, 98]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 68, y0: 272, x1: 132, y1: 272, width: 4, color: 'shade' },
    ],
  },
  'spring-tension-tester': {
    canvas: [200, 224],
    ops: [
      { op: 'rect', x: 56, y: 188, w: 88, h: 24, r: 7, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 74, y: 44, w: 52, h: 150, r: 16, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 58, x1: 100, y1: 120, width: 4, color: 'shade' },
      { op: 'path', points: [[100, 120], [88, 128], [112, 136], [88, 144], [112, 152], [100, 160]], close: false, outline: 'CLAY.deep', lineWidth: 4 },
      { op: 'path', points: [[100, 160], [94, 176], [106, 176]], close: true, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 74, y0: 108, x1: 86, y1: 108, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 74, y0: 80, x1: 86, y1: 80, width: 2, color: 'INK.soft' },
    ],
  },
  'sprout-protection-ring': {
    canvas: [200, 224],
    ops: [
      { op: 'blob', cx: 100, cy: 196, rx: 74, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[34, 90], [40, 196], [160, 196], [166, 90]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 90, rx: 66, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 58, y0: 94, x1: 60, y1: 190, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 80, y0: 92, x1: 81, y1: 192, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 100, y0: 92, x1: 100, y1: 192, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 120, y0: 92, x1: 119, y1: 192, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 142, y0: 94, x1: 140, y1: 190, width: 4, color: 'INK.soft' },
      { op: 'grass', x: 94, baseY: 120, height: 30, lean: -5, width: 4, color: 'SAGE.light' },
    ],
  },
  'squirrel-feeding-puzzle': {
    canvas: [208, 224],
    ops: [
      { op: 'line', x0: 104, y0: 214, x1: 104, y1: 120, width: 13, color: 'shade' },
      { op: 'blob', cx: 104, cy: 214, rx: 40, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 104, cy: 92, rx: 62, ry: 62, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 92, rx: 22, ry: 22, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 104, y0: 92, x1: 104, y1: 32, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 92, x1: 162, y1: 108, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 92, x1: 56, y1: 140, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 104, cy: 40, rx: 12, ry: 10, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 150, cy: 120, rx: 11, ry: 9, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'stack-lock-crates': {
    canvas: [256, 224],
    ops: [
      { op: 'rect', x: 40, y: 120, w: 176, h: 80, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 56, y: 48, w: 144, h: 72, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[72, 48], [88, 48], [88, 36], [72, 36]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[168, 48], [184, 48], [184, 36], [168, 36]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[88, 120], [104, 120], [104, 108], [88, 108]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[152, 120], [168, 120], [168, 108], [152, 108]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 52, y0: 200, x1: 52, y1: 210, width: 8, color: 'shade' },
      { op: 'line', x0: 204, y0: 200, x1: 204, y1: 210, width: 8, color: 'shade' },
      { op: 'line', x0: 80, y0: 160, x1: 176, y1: 160, width: 3, color: 'shade' },
    ],
  },
  'stamp-carving-block': {
    canvas: [176, 232],
    ops: [
      { op: 'path', points: [[28, 196], [148, 196], [156, 216], [20, 216]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 92, w: 72, h: 96, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 40, y: 78, w: 96, h: 18, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 118, x1: 136, y1: 118, width: 7, color: 'shade' },
      { op: 'line', x0: 48, y0: 104, x1: 48, y1: 132, width: 4, color: 'CLAY.deep' },
      { op: 'line', x0: 128, y0: 104, x1: 128, y1: 132, width: 4, color: 'CLAY.deep' },
      { op: 'path', points: [[78, 140], [98, 140], [100, 168], [76, 168]], close: true, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'speckle', x: 56, y: 188, w: 64, h: 8, count: 5, maxR: 2, alpha: 0.3, color: 'INK.grain' },
    ],
  },
  'star-viewing-hood': {
    canvas: [256, 176],
    ops: [
      { op: 'line', x0: 72, y0: 160, x1: 80, y1: 96, width: 8, color: 'shade' },
      { op: 'line', x0: 184, y0: 160, x1: 176, y1: 96, width: 8, color: 'shade' },
      { op: 'path', points: [[48, 104], [128, 48], [208, 104], [196, 128], [128, 96], [60, 128]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[72, 108], [128, 72], [184, 108], [176, 120], [128, 96], [80, 120]], close: true, fill: 'SAGE.deep', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 100, y0: 84, x1: 108, y1: 110, width: 2, color: 'SAGE.shade' },
      { op: 'line', x0: 156, y0: 84, x1: 148, y1: 110, width: 2, color: 'SAGE.shade' },
      { op: 'blob', cx: 128, cy: 162, rx: 78, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'starlight-photo-rest': {
    canvas: [224, 200],
    ops: [
      { op: 'line', x0: 60, y0: 188, x1: 104, y1: 104, width: 7, color: 'shade' },
      { op: 'line', x0: 164, y0: 188, x1: 120, y1: 104, width: 7, color: 'shade' },
      { op: 'line', x0: 112, y0: 188, x1: 112, y1: 104, width: 7, color: 'shade' },
      { op: 'path', points: [[72, 104], [152, 104], [148, 72], [76, 72]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[120, 88], [152, 72], [156, 96], [122, 104]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 98, cy: 88, rx: 12, ry: 12, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 98, cy: 88, rx: 5, ry: 5, fill: 'INK.soft', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 188, rx: 68, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'steam-chime-kettle': {
    canvas: [224, 224],
    ops: [
      { op: 'blob', cx: 108, cy: 150, rx: 74, ry: 58, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 166, rx: 56, ry: 40, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[148, 128], [196, 104], [206, 116], [166, 146]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 100], [156, 100], [148, 118], [68, 118]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 92, y: 62, w: 32, h: 40, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 108, cy: 62, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[64, 100], [44, 68], [60, 58], [78, 92]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'stepping-stone-lifter': {
    canvas: [256, 112],
    ops: [
      { op: 'line', x0: 30, y0: 34, x1: 174, y1: 58, width: 12, color: 'shade' },
      { op: 'path', points: [[168, 40], [206, 52], [206, 72], [170, 64]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[200, 48], [236, 58], [230, 82], [196, 72]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 206, y0: 74, x1: 206, y1: 104, width: 7, color: 'shade' },
      { op: 'rect', x: 174, y: 94, w: 68, h: 14, r: 4, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 188, cy: 48, rx: 9, ry: 9, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 36, y0: 38, x1: 110, y1: 50, width: 2, color: 'INK.soft' },
    ],
  },
  'storm-candle-box': {
    canvas: [160, 272],
    ops: [
      { op: 'blob', cx: 80, cy: 256, rx: 52, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 36, y: 64, w: 88, h: 184, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 96, w: 56, h: 130, r: 8, fill: 'PAPER.skyBottom', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 52, y: 60, w: 56, h: 130, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 80, y0: 60, x1: 80, y1: 24, width: 4, color: 'shade' },
      { op: 'blob', cx: 80, cy: 150, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 36, y0: 110, x1: 124, y1: 110, width: 2.5, color: 'shade' },
      { op: 'line', x0: 50, y0: 240, x1: 110, y1: 240, width: 3, color: 'shade' },
    ],
  },
  'story-choice-spinner': {
    canvas: [208, 224],
    ops: [
      { op: 'line', x0: 104, y0: 214, x1: 104, y1: 150, width: 10, color: 'shade' },
      { op: 'path', points: [[60, 210], [148, 210], [140, 220], [68, 220]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 98, rx: 68, ry: 64, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 98, x1: 150, y1: 74, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 98, x1: 150, y1: 122, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 98, x1: 58, y1: 74, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 138, cy: 74, rx: 8, ry: 7, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 138, cy: 122, rx: 8, ry: 7, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 70, cy: 74, rx: 8, ry: 7, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[104, 98], [160, 92], [120, 112]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 104, cy: 98, rx: 10, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'story-listening-lantern': {
    canvas: [176, 288],
    ops: [
      { op: 'halo', cx: 80, cy: 150, r: 70 },
      { op: 'line', x0: 80, y0: 274, x1: 80, y1: 210, width: 9, color: 'shade' },
      { op: 'blob', cx: 80, cy: 274, rx: 34, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'rect', x: 44, y: 94, w: 72, h: 116, r: 14, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 58, y: 112, w: 44, h: 80, r: 8, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[44, 94], [80, 66], [116, 94]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[116, 118], [140, 118], [140, 180], [116, 180]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 80, cy: 150, rx: 6, ry: 6, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'story-object-stage': {
    canvas: [224, 272],
    ops: [
      { op: 'path', points: [[36, 196], [188, 196], [170, 236], [54, 236]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 252, rx: 78, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 196], [44, 60], [180, 60], [180, 196]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 52, y0: 74, x1: 172, y1: 74, width: 7, color: 'fill' },
      { op: 'blob', cx: 92, cy: 160, rx: 22, ry: 34, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[126, 196], [126, 150], [156, 150], [156, 196]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'halo', cx: 112, cy: 86, r: 40 },
      { op: 'blob', cx: 112, cy: 76, rx: 6, ry: 6, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'story-quilt-frame': {
    canvas: [224, 224],
    ops: [
      { op: 'rect', x: 36, y: 36, w: 152, h: 152, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 56, y: 56, w: 48, h: 48, r: 4, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 120, y: 56, w: 48, h: 48, r: 4, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 56, y: 120, w: 48, h: 48, r: 4, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 120, y: 120, w: 48, h: 48, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 80, y0: 188, x1: 80, y1: 210, width: 8, color: 'shade' },
      { op: 'line', x0: 144, y0: 188, x1: 144, y1: 210, width: 8, color: 'shade' },
    ],
  },
  'story-scroll-box': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 28, y: 92, w: 200, h: 70, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[28, 92], [228, 92], [224, 72], [32, 72]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[90, 84], [166, 84], [166, 128], [90, 128]], close: true, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 70, cy: 106, rx: 16, ry: 24, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 186, cy: 106, rx: 16, ry: 24, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 94, x1: 104, y1: 120, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 152, y0: 94, x1: 152, y1: 120, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 48, y0: 156, x1: 208, y1: 156, width: 2, color: 'shade' },
    ],
  },
  'story-sound-box': {
    canvas: [256, 200],
    ops: [
      { op: 'rect', x: 36, y: 72, w: 184, h: 118, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[32, 72], [52, 48], [204, 48], [224, 72]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 128, y0: 80, x1: 128, y1: 182, width: 4, color: 'shade' },
      { op: 'path', points: [[44, 120], [120, 120], [120, 160], [36, 160]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[136, 110], [212, 110], [212, 150], [136, 150]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 80, cy: 140, rx: 6, ry: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 2 },
      { op: 'blob', cx: 172, cy: 130, rx: 6, ry: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 2 },
      { op: 'line', x0: 52, y0: 92, x1: 204, y1: 92, width: 2, color: 'INK.soft' },
    ],
  },
  'string-plucking-post': {
    canvas: [200, 288],
    ops: [
      { op: 'blob', cx: 100, cy: 270, rx: 48, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 56, y0: 262, x1: 58, y1: 56, width: 13, color: 'shade' },
      { op: 'line', x0: 144, y0: 262, x1: 142, y1: 56, width: 13, color: 'shade' },
      { op: 'path', points: [[44, 44], [156, 44], [156, 68], [44, 68]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 74, y0: 62, x1: 74, y1: 250, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 92, y0: 62, x1: 92, y1: 250, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 110, y0: 62, x1: 110, y1: 250, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 62, x1: 128, y1: 250, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 110, cy: 150, rx: 7, ry: 7, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'summer-shadow-theater': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[24, 56], [24, 160], [60, 160], [60, 48]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 48], [60, 160], [196, 160], [196, 48]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[196, 48], [196, 160], [232, 160], [232, 56]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 78, y: 66, w: 100, h: 62, r: 4, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[96, 128], [104, 96], [118, 108], [126, 84], [140, 116], [152, 98], [160, 128]], close: false, outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 78, y0: 140, x1: 178, y1: 140, width: 4, color: 'shade' },
      { op: 'line', x0: 42, y0: 58, x1: 42, y1: 156, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 214, y0: 58, x1: 214, y1: 156, width: 2, color: 'INK.soft' },
    ],
  },
  'sun-hat-shaping-block': {
    canvas: [216, 216],
    ops: [
      { op: 'rect', x: 70, y: 150, w: 76, h: 56, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 150, rx: 96, ry: 22, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 102, rx: 50, ry: 56, n: 14, irregularity: 0.05, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 148, rx: 56, ry: 12, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 30, cy: 150, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 186, cy: 150, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 108, cy: 168, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'sun-warmed-lounger': {
    canvas: [256, 176],
    ops: [
      { op: 'line', x0: 56, y0: 162, x1: 62, y1: 126, width: 9, color: 'shade' },
      { op: 'line', x0: 196, y0: 162, x1: 190, y1: 126, width: 9, color: 'shade' },
      { op: 'path', points: [[40, 128], [120, 124], [124, 52], [156, 50], [150, 124], [216, 122], [212, 142], [44, 146]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 126, y: 58, w: 24, h: 20, r: 3, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 126, y: 82, w: 24, h: 20, r: 3, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 56, y0: 134, x1: 206, y1: 132, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 80, y0: 126, x1: 80, y1: 144, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 172, y0: 126, x1: 172, y1: 144, width: 2, color: 'INK.soft' },
    ],
  },
  'sunrise-curtain-clock': {
    canvas: [216, 208],
    ops: [
      { op: 'blob', cx: 108, cy: 192, rx: 80, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[30, 40], [186, 40], [180, 56], [36, 56]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 56], [40, 180], [88, 180], [80, 56]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[136, 56], [128, 180], [176, 180], [172, 56]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 58, y0: 64, x1: 56, y1: 176, width: 2, color: 'SAGE.deep' },
      { op: 'line', x0: 154, y0: 64, x1: 156, y1: 176, width: 2, color: 'SAGE.deep' },
      { op: 'blob', cx: 108, cy: 48, rx: 10, ry: 10, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 108, y0: 56, x1: 108, y1: 120, width: 2.5, color: 'shade' },
      { op: 'blob', cx: 108, cy: 124, rx: 5, ry: 5, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'sunset-thermos-dock': {
    canvas: [224, 256],
    ops: [
      { op: 'line', x0: 66, y0: 150, x1: 60, y1: 244, width: 10, color: 'shade' },
      { op: 'line', x0: 158, y0: 150, x1: 164, y1: 244, width: 10, color: 'shade' },
      { op: 'path', points: [[34, 118], [190, 118], [178, 160], [46, 160]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 78, cy: 120, rx: 24, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 146, cy: 120, rx: 24, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[64, 72], [92, 72], [90, 120], [66, 120]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 78, cy: 72, rx: 15, ry: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 46, y0: 160, x1: 178, y1: 160, width: 2, color: 'INK.soft' },
    ],
  },
  'switchback-model': {
    canvas: [176, 288],
    ops: [
      { op: 'rect', x: 36, y: 40, w: 104, h: 238, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 52, y0: 54, x1: 52, y1: 268, width: 3, color: 'shade' },
      { op: 'line', x0: 124, y0: 54, x1: 124, y1: 268, width: 3, color: 'shade' },
      { op: 'path', points: [[54, 258], [122, 222], [54, 186], [122, 150], [54, 114], [122, 80]], close: false, fill: 'SAGE.deep', outline: 'INK.soft', lineWidth: 6 },
      { op: 'blob', cx: 54, cy: 258, rx: 9, ry: 9, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 122, cy: 222, rx: 7, ry: 7, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 54, cy: 186, rx: 7, ry: 7, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 122, cy: 150, rx: 7, ry: 7, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 54, cy: 114, rx: 7, ry: 7, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 122, cy: 80, rx: 9, ry: 9, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[44, 276], [132, 276], [140, 283], [36, 283]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'tabletop-pot-warmer': {
    canvas: [176, 256],
    ops: [
      { op: 'path', points: [[44, 196], [132, 196], [140, 236], [36, 236]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 120], [116, 120], [132, 196], [44, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 172, rx: 24, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'halo', cx: 88, cy: 174, r: 34 },
      { op: 'blob', cx: 88, cy: 178, rx: 7, ry: 12, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 88, cy: 108, rx: 58, ry: 22, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 100, rx: 40, ry: 12, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
    ],
  },
  'target-touch-tree': {
    canvas: [200, 256],
    ops: [
      { op: 'blob', cx: 100, cy: 238, rx: 52, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[90, 232], [94, 72], [106, 72], [110, 232]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 110, x1: 48, y1: 92, width: 7, color: 'shade' },
      { op: 'line', x0: 100, y0: 150, x1: 152, y1: 134, width: 7, color: 'shade' },
      { op: 'blob', cx: 42, cy: 88, rx: 26, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 158, cy: 130, rx: 26, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 62, rx: 30, ry: 28, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 62, rx: 12, ry: 11, fill: 'ROBOT.accent', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 42, cy: 88, rx: 11, ry: 10, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 158, cy: 130, rx: 11, ry: 10, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'tea-brick-press': {
    canvas: [200, 224],
    ops: [
      { op: 'rect', x: 46, y: 120, w: 108, h: 88, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[46, 120], [154, 120], [146, 100], [54, 100]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 76, y: 74, w: 48, h: 28, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 74, x1: 100, y1: 36, width: 9, color: 'shade' },
      { op: 'path', points: [[68, 36], [132, 36], [128, 24], [72, 24]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 160, x1: 138, y1: 160, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 62, y0: 184, x1: 138, y1: 184, width: 2, color: 'INK.soft' },
    ],
  },
  'tea-cloth-rack': {
    canvas: [224, 256],
    ops: [
      { op: 'line', x0: 52, y0: 236, x1: 56, y1: 72, width: 11, color: 'shade' },
      { op: 'line', x0: 172, y0: 236, x1: 168, y1: 72, width: 11, color: 'shade' },
      { op: 'path', points: [[40, 64], [184, 60], [186, 78], [42, 82]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 78, cy: 90, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 146, cy: 90, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[96, 86], [148, 86], [140, 196], [88, 196]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 98, y0: 120, x1: 136, y1: 120, width: 2, color: 'SAGE.shade' },
    ],
  },
  'tea-leaf-rinser': {
    canvas: [200, 224],
    ops: [
      { op: 'path', points: [[86, 196], [78, 212], [122, 212], [114, 196]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 196, rx: 40, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[40, 120], [160, 120], [136, 192], [64, 192]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 120, rx: 62, ry: 15, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 118, rx: 46, ry: 10, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'speckle', x: 66, y: 138, w: 68, h: 44, count: 11, maxR: 3, alpha: 0.55, color: 'INK.soft' },
    ],
  },
  'tea-scoop-rest': {
    canvas: [224, 160],
    ops: [
      { op: 'path', points: [[40, 96], [60, 140], [164, 140], [184, 96], [150, 116], [74, 116]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[74, 116], [150, 116], [140, 104], [84, 104]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 140, rx: 58, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[96, 108], [128, 108], [124, 86], [100, 86]], close: true, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 112, y0: 86, x1: 112, y1: 62, width: 5, color: 'shade' },
      { op: 'line', x0: 62, y0: 124, x1: 162, y1: 124, width: 2, color: 'INK.soft' },
    ],
  },
  'tea-sharing-stool': {
    canvas: [256, 144],
    ops: [
      { op: 'line', x0: 52, y0: 134, x1: 56, y1: 92, width: 11, color: 'shade' },
      { op: 'line', x0: 120, y0: 134, x1: 118, y1: 92, width: 11, color: 'shade' },
      { op: 'line', x0: 172, y0: 134, x1: 174, y1: 56, width: 11, color: 'shade' },
      { op: 'line', x0: 232, y0: 134, x1: 230, y1: 56, width: 11, color: 'shade' },
      { op: 'path', points: [[34, 92], [136, 90], [138, 108], [36, 110]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[156, 56], [244, 54], [246, 72], [158, 74]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 50, y0: 99, x1: 120, y1: 98, width: 2, color: 'shade' },
      { op: 'line', x0: 170, y0: 63, x1: 232, y1: 62, width: 2, color: 'shade' },
    ],
  },
  'tea-timer-float': {
    canvas: [200, 176],
    ops: [
      { op: 'blob', cx: 100, cy: 150, rx: 84, ry: 18, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 146, rx: 60, ry: 11, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[100, 140], [78, 108], [100, 118], [122, 108]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[100, 118], [84, 86], [100, 72], [116, 86]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 100, rx: 9, ry: 9, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 52, y0: 156, x1: 148, y1: 156, width: 2, color: 'WATER.deep' },
    ],
  },
  'tea-water-filter': {
    canvas: [144, 256],
    ops: [
      { op: 'path', points: [[48, 236], [44, 80], [100, 80], [96, 236]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 72, cy: 80, rx: 30, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 118], [100, 118], [98, 108], [46, 108]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[44, 158], [98, 158], [97, 148], [45, 148]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'speckle', x: 48, y: 162, w: 48, h: 30, count: 7, maxR: 4, alpha: 0.6, color: 'INK.soft' },
      { op: 'path', points: [[58, 236], [64, 250], [80, 250], [86, 236]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'temperature-color-stones': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[20, 128], [28, 96], [228, 96], [236, 128]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[28, 98], [228, 98], [224, 84], [32, 84]], close: true, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 66, cy: 78, rx: 30, ry: 18, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 130, cy: 76, rx: 30, ry: 18, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 194, cy: 78, rx: 30, ry: 18, fill: 'WATER.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 50, y0: 72, x1: 62, y1: 68, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 178, y0: 72, x1: 190, y1: 68, width: 2, color: 'INK.soft' },
    ],
  },
  'test-roll-ramp': {
    canvas: [256, 144],
    ops: [
      { op: 'path', points: [[24, 128], [24, 52], [160, 128]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 24, y0: 52, x1: 160, y1: 128, width: 4, color: 'shade' },
      { op: 'line', x0: 60, y0: 76, x1: 68, y1: 74, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 96, y0: 100, x1: 104, y1: 98, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 40, y0: 52, x1: 24, y1: 52, width: 7, color: 'shade' },
      { op: 'path', points: [[164, 108], [236, 108], [226, 134], [174, 134]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 178, y0: 112, x1: 178, y1: 132, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 200, y0: 110, x1: 200, y1: 134, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 222, y0: 112, x1: 222, y1: 132, width: 2, color: 'INK.soft' },
    ],
  },
  'thank-you-stamp-tree': {
    canvas: [224, 288],
    ops: [
      { op: 'rect', x: 84, y: 234, w: 56, h: 48, r: 10, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 234, x1: 112, y1: 80, width: 13, color: 'fill' },
      { op: 'line', x0: 112, y0: 140, x1: 56, y1: 110, width: 7, color: 'fill' },
      { op: 'line', x0: 112, y0: 140, x1: 168, y1: 110, width: 7, color: 'fill' },
      { op: 'rect', x: 40, y: 110, w: 34, h: 34, r: 4, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 94, y: 78, w: 34, h: 34, r: 4, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 152, y: 110, w: 34, h: 34, r: 4, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 57, y0: 110, x1: 57, y1: 116, width: 2, color: 'shade' },
      { op: 'line', x0: 169, y0: 110, x1: 169, y1: 116, width: 2, color: 'shade' },
    ],
  },
  'thread-wax-dock': {
    canvas: [224, 176],
    ops: [
      { op: 'rect', x: 36, y: 108, w: 152, h: 56, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[32, 108], [48, 80], [176, 80], [192, 108]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 64, y0: 94, x1: 160, y1: 94, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 62, y0: 124, x1: 162, y1: 124, width: 4, color: 'CLAY.deep' },
      { op: 'line', x0: 62, y0: 138, x1: 162, y1: 138, width: 4, color: 'CLAY.deep' },
      { op: 'line', x0: 62, y0: 152, x1: 162, y1: 152, width: 4, color: 'CLAY.deep' },
      { op: 'line', x0: 108, y0: 84, x1: 116, y1: 84, width: 7, color: 'CLAY.blossom' },
    ],
  },
  'threshold-flower-pocket': {
    canvas: [160, 256],
    ops: [
      { op: 'path', points: [[44, 244], [116, 244], [108, 150], [52, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[52, 150], [108, 150], [112, 134], [48, 134]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 52, y0: 170, x1: 52, y1: 230, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 80, y0: 134, x1: 78, y1: 74, width: 4, color: 'SAGE.deep' },
      { op: 'line', x0: 68, y0: 134, x1: 52, y1: 92, width: 3, color: 'SAGE.deep' },
      { op: 'line', x0: 92, y0: 134, x1: 108, y1: 96, width: 3, color: 'SAGE.deep' },
      { op: 'blob', cx: 78, cy: 62, rx: 18, ry: 16, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 50, cy: 86, rx: 12, ry: 11, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 110, cy: 90, rx: 11, ry: 10, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'threshold-memory-tile': {
    canvas: [200, 200],
    ops: [
      { op: 'blob', cx: 100, cy: 182, rx: 74, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 36, y: 40, w: 128, h: 138, r: 14, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 52, y: 56, w: 96, h: 106, r: 10, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[100, 72], [124, 108], [100, 140], [76, 108]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 108, rx: 12, ry: 12, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 3 },
      { op: 'speckle', x: 56, y: 60, w: 88, h: 98, count: 8, maxR: 2, alpha: 0.22, color: 'INK.grain' },
    ],
  },
  'threshold-night-strip': {
    canvas: [160, 256],
    ops: [
      { op: 'path', points: [[40, 244], [120, 244], [114, 210], [46, 210]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 210, x1: 80, y1: 58, width: 9, color: 'shade' },
      { op: 'path', points: [[44, 58], [116, 58], [120, 40], [40, 40]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 40], [120, 40], [112, 96], [48, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'halo', cx: 80, cy: 92, r: 40 },
      { op: 'path', points: [[54, 84], [106, 84], [104, 104], [56, 104]], close: true, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 62, y0: 94, x1: 98, y1: 94, width: 3, color: 'INK.soft' },
    ],
  },
  'thunder-comfort-drum': {
    canvas: [216, 200],
    ops: [
      { op: 'blob', cx: 108, cy: 182, rx: 76, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[42, 96], [174, 96], [182, 170], [34, 170]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 96, rx: 70, ry: 24, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 92, rx: 52, ry: 16, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 42, y0: 116, x1: 174, y1: 116, width: 3, color: 'shade' },
      { op: 'line', x0: 56, y0: 96, x1: 50, y1: 116, width: 2.5, color: 'shade' },
      { op: 'line', x0: 90, y0: 96, x1: 86, y1: 116, width: 2.5, color: 'shade' },
      { op: 'line', x0: 126, y0: 96, x1: 130, y1: 116, width: 2.5, color: 'shade' },
      { op: 'line', x0: 160, y0: 96, x1: 166, y1: 116, width: 2.5, color: 'shade' },
    ],
  },
  'tiny-drawer-stair': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[28, 116], [100, 116], [100, 162], [28, 162]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[100, 80], [170, 80], [170, 162], [100, 162]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[170, 44], [238, 44], [238, 162], [170, 162]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 44, y: 124, w: 40, h: 26, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 116, y: 92, w: 40, h: 26, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 186, y: 56, w: 40, h: 26, r: 4, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 64, cy: 137, rx: 5, ry: 5, fill: 'CLAY.deep' },
      { op: 'line', x0: 36, y0: 162, x1: 230, y1: 162, width: 4, color: 'shade' },
    ],
  },
  'tool-edge-inspector': {
    canvas: [224, 200],
    ops: [
      { op: 'rect', x: 44, y: 166, w: 136, h: 26, r: 8, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 166, x1: 80, y1: 96, width: 11, color: 'fill' },
      { op: 'line', x0: 80, y0: 100, x1: 150, y1: 70, width: 10, color: 'fill' },
      { op: 'blob', cx: 160, cy: 66, rx: 34, ry: 34, n: 14, irregularity: 0.03, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 160, cy: 66, rx: 22, ry: 22, n: 12, irregularity: 0.03, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 40, y0: 104, x1: 76, y1: 96, width: 6, color: 'CLAY.light' },
      { op: 'line', x0: 150, y0: 98, x1: 150, y1: 166, width: 2, color: 'INK.soft' },
    ],
  },
  'tool-outline-panel': {
    canvas: [176, 256],
    ops: [
      { op: 'rect', x: 30, y: 24, w: 116, h: 216, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 60, y0: 52, x1: 60, y1: 120, width: 4, color: 'shade' },
      { op: 'line', x0: 60, y0: 120, x1: 78, y1: 120, width: 4, color: 'shade' },
      { op: 'line', x0: 108, y0: 52, x1: 108, y1: 128, width: 4, color: 'shade' },
      { op: 'blob', cx: 108, cy: 150, rx: 16, ry: 12, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 54, y0: 160, x1: 54, y1: 214, width: 6, color: 'CLAY.deep' },
      { op: 'path', points: [[44, 160], [64, 160], [60, 176], [48, 176]], close: true, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 60, cy: 50, rx: 4, ry: 4, fill: 'INK.soft' },
      { op: 'blob', cx: 108, cy: 50, rx: 4, ry: 4, fill: 'INK.soft' },
      { op: 'line', x0: 40, y0: 240, x1: 136, y1: 240, width: 4, color: 'shade' },
    ],
  },
  'toy-repair-bench': {
    canvas: [224, 256],
    ops: [
      { op: 'line', x0: 56, y0: 238, x1: 62, y1: 150, width: 12, color: 'shade' },
      { op: 'line', x0: 168, y0: 238, x1: 162, y1: 150, width: 12, color: 'shade' },
      { op: 'rect', x: 36, y: 126, w: 152, h: 26, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[100, 126], [100, 86], [88, 86], [88, 72], [124, 72], [124, 86], [112, 86], [112, 126]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 106, cy: 80, rx: 18, ry: 8, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 56, cy: 122, rx: 20, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 166, cy: 122, rx: 20, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 166, cy: 120, rx: 10, ry: 5, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 50, y0: 140, x1: 174, y1: 140, width: 2, color: 'INK.soft' },
    ],
  },
  'toy-return-chute': {
    canvas: [208, 208],
    ops: [
      { op: 'path', points: [[40, 44], [168, 64], [150, 120], [44, 104]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[62, 62], [150, 76], [140, 106], [66, 96]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[44, 104], [160, 118], [172, 184], [36, 184]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 80, cy: 160, rx: 18, ry: 18, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 124, cy: 166, rx: 15, ry: 15, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 40, y0: 44, x1: 36, y1: 104, width: 5, color: 'shade' },
      { op: 'blob', cx: 104, cy: 190, rx: 68, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'trail-count-clicker': {
    canvas: [200, 208],
    ops: [
      { op: 'line', x0: 56, y0: 196, x1: 56, y1: 96, width: 11, color: 'shade' },
      { op: 'line', x0: 144, y0: 196, x1: 144, y1: 96, width: 11, color: 'shade' },
      { op: 'rect', x: 36, y: 80, w: 128, h: 48, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 128], [60, 84], [100, 128]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[104, 128], [140, 84], [140, 128]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 100, cy: 104, rx: 7, ry: 7, fill: 'fill', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 42, y0: 196, x1: 158, y1: 196, width: 6, color: 'shade' },
      { op: 'line', x0: 48, y0: 92, x1: 80, y1: 92, width: 2, color: 'INK.soft' },
    ],
  },
  'trail-edge-stringline': {
    canvas: [256, 144],
    ops: [
      { op: 'line', x0: 46, y0: 134, x1: 50, y1: 52, width: 9, color: 'shade' },
      { op: 'line', x0: 210, y0: 134, x1: 206, y1: 52, width: 9, color: 'shade' },
      { op: 'line', x0: 50, y0: 60, x1: 206, y1: 60, width: 3, color: 'INK.line' },
      { op: 'line', x0: 50, y0: 88, x1: 206, y1: 88, width: 3, color: 'INK.line' },
      { op: 'blob', cx: 206, cy: 74, rx: 13, ry: 13, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 206, y0: 74, x1: 216, y1: 74, width: 3, color: 'shade' },
      { op: 'blob', cx: 50, cy: 50, rx: 7, ry: 7, fill: 'fill', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 206, cy: 50, rx: 7, ry: 7, fill: 'fill', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'trail-gear-cubby': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 24, y: 36, w: 208, h: 128, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 96, y0: 40, x1: 96, y1: 160, width: 3, color: 'shade' },
      { op: 'line', x0: 160, y0: 40, x1: 160, y1: 160, width: 3, color: 'shade' },
      { op: 'line', x0: 160, y0: 100, x1: 228, y1: 100, width: 3, color: 'shade' },
      { op: 'path', points: [[36, 56], [84, 56], [84, 150], [36, 150]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 60, cy: 118, rx: 18, ry: 22, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[108, 150], [148, 150], [140, 52], [116, 52]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 36, y0: 164, x1: 232, y1: 164, width: 4, color: 'shade' },
    ],
  },
  'trail-paint-stencil-roll': {
    canvas: [208, 208],
    ops: [
      { op: 'line', x0: 52, y0: 58, x1: 104, y1: 120, width: 9, color: 'shade' },
      { op: 'line', x0: 104, y0: 120, x1: 104, y1: 160, width: 7, color: 'shade' },
      { op: 'rect', x: 56, y: 108, w: 96, h: 56, r: 26, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 56, cy: 136, rx: 12, ry: 28, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 152, cy: 136, rx: 12, ry: 28, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[92, 124], [104, 116], [116, 124], [104, 132]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[92, 148], [104, 140], [116, 148], [104, 156]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 48, y0: 54, x1: 58, y1: 48, width: 7, color: 'shade' },
    ],
  },
  'trail-register-box': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 40, y: 78, w: 176, h: 88, r: 14, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[40, 120], [46, 166], [210, 166], [216, 120]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[36, 80], [220, 80], [208, 38], [48, 38]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 58, x1: 208, y1: 58, width: 2, color: 'INK.soft' },
      { op: 'rect', x: 92, y: 96, w: 72, h: 54, r: 8, fill: 'PAPER.floor', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 128, cy: 122, rx: 15, ry: 15, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 118, y0: 122, x1: 138, y1: 122, width: 2, color: 'INK.line' },
      { op: 'line', x0: 128, y0: 112, x1: 128, y1: 132, width: 2, color: 'INK.line' },
    ],
  },
  'trail-repair-satchel': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[44, 78], [212, 78], [200, 168], [56, 168]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[50, 120], [56, 168], [200, 168], [206, 120]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[40, 78], [216, 78], [210, 46], [46, 46]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 80, x1: 102, y1: 166, width: 3, color: 'shade' },
      { op: 'line', x0: 160, y0: 80, x1: 160, y1: 166, width: 3, color: 'shade' },
      { op: 'rect', x: 116, y: 56, w: 34, h: 18, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 62, y0: 110, x1: 92, y1: 110, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 133, cy: 64, rx: 4, ry: 4, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'trail-ribbon-reel': {
    canvas: [200, 200],
    ops: [
      { op: 'line', x0: 68, y0: 190, x1: 74, y1: 120, width: 10, color: 'shade' },
      { op: 'line', x0: 132, y0: 190, x1: 126, y1: 120, width: 10, color: 'shade' },
      { op: 'blob', cx: 100, cy: 98, rx: 58, ry: 58, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 100, cy: 98, rx: 36, ry: 36, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 100, cy: 98, rx: 12, ry: 12, fill: 'fill', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[156, 86], [186, 80], [188, 96], [158, 104]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[186, 76], [200, 88], [186, 100]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 100, y0: 68, x1: 100, y1: 128, width: 2, color: 'INK.soft' },
    ],
  },
  'trail-snack-cache': {
    canvas: [256, 176],
    ops: [
      { op: 'rect', x: 34, y: 74, w: 188, h: 92, r: 34, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[34, 120], [42, 166], [214, 166], [222, 120]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'rect', x: 44, y: 40, w: 168, h: 40, r: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 78, x1: 208, y1: 78, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 128, cy: 40, rx: 18, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 74, y0: 110, x1: 74, y1: 150, width: 3, color: 'shade' },
      { op: 'line', x0: 182, y0: 110, x1: 182, y1: 150, width: 3, color: 'shade' },
      { op: 'blob', cx: 128, cy: 58, rx: 5, ry: 4, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'trail-story-pavilion-kit': {
    canvas: [288, 192],
    ops: [
      { op: 'path', points: [[40, 64], [144, 28], [248, 64], [228, 82], [144, 52], [60, 82]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 60, y0: 80, x1: 52, y1: 172, width: 8, color: 'shade' },
      { op: 'line', x0: 228, y0: 80, x1: 236, y1: 172, width: 8, color: 'shade' },
      { op: 'line', x0: 144, y0: 52, x1: 144, y1: 172, width: 8, color: 'shade' },
      { op: 'path', points: [[100, 72], [144, 58], [188, 72], [176, 84], [144, 74], [112, 84]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 40, y0: 64, x1: 144, y1: 28, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 248, y0: 64, x1: 144, y1: 28, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 52, y0: 170, x1: 236, y1: 170, width: 4, color: 'shade' },
    ],
  },
  'trail-water-rest': {
    canvas: [224, 288],
    ops: [
      { op: 'line', x0: 56, y0: 280, x1: 60, y1: 120, width: 12, color: 'shade' },
      { op: 'line', x0: 168, y0: 280, x1: 164, y1: 120, width: 12, color: 'shade' },
      { op: 'path', points: [[36, 118], [188, 114], [190, 140], [38, 144]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[66, 118], [66, 90], [78, 90], [78, 118]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[146, 118], [146, 90], [158, 90], [158, 118]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[62, 90], [62, 80], [82, 80], [82, 90]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[142, 90], [142, 80], [162, 80], [162, 90]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 112, cy: 131, rx: 40, ry: 7, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'travel-bed-roll': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[36, 150], [40, 104], [216, 104], [220, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 52, cy: 98, rx: 18, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 204, cy: 98, rx: 18, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 98, y: 96, w: 18, h: 58, r: 3, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 146, y: 96, w: 18, h: 58, r: 3, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 76, y0: 120, x1: 182, y1: 120, width: 2, color: 'INK.soft' },
    ],
  },
  'travel-tea-caddy': {
    canvas: [256, 176],
    ops: [
      { op: 'path', points: [[92, 52], [100, 28], [156, 28], [164, 52]], close: false, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 118, rx: 78, ry: 50, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 128, cy: 62, rx: 78, ry: 22, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 50, y0: 62, x1: 206, y1: 62, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 40, x1: 128, y1: 84, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 74, y0: 140, x1: 182, y1: 140, width: 2, color: 'shade' },
    ],
  },
  'treasure-cloth-map': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[28, 140], [36, 52], [224, 44], [230, 136]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'line', x0: 54, y0: 110, x1: 92, y1: 78, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 92, y0: 78, x1: 140, y1: 104, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 140, y0: 104, x1: 190, y1: 72, width: 3, color: 'INK.soft' },
      { op: 'rect', x: 60, y: 96, w: 26, h: 26, r: 4, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 128, y: 90, w: 26, h: 26, r: 4, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[178, 60], [202, 60], [190, 84]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 190, cy: 72, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'treat-count-abacus': {
    canvas: [224, 200],
    ops: [
      { op: 'path', points: [[40, 188], [44, 60], [60, 60], [56, 188]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[168, 188], [164, 60], [180, 60], [184, 188]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 40, y: 54, w: 144, h: 14, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 50, y0: 98, x1: 174, y1: 98, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 50, y0: 146, x1: 174, y1: 146, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 72, cy: 98, rx: 17, ry: 17, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 110, cy: 98, rx: 17, ry: 17, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 150, cy: 146, rx: 17, ry: 17, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 110, cy: 146, rx: 17, ry: 17, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'tree-guard-expander': {
    canvas: [176, 256],
    ops: [
      { op: 'line', x0: 88, y0: 244, x1: 88, y1: 60, width: 12, color: 'shade' },
      { op: 'path', points: [[42, 96], [88, 84], [88, 156], [42, 168]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[88, 84], [134, 96], [134, 168], [88, 156]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 42, y0: 120, x1: 134, y1: 120, width: 3, color: 'INK.line' },
      { op: 'line', x0: 42, y0: 144, x1: 134, y1: 144, width: 3, color: 'INK.line' },
      { op: 'blob', cx: 88, cy: 130, rx: 6, ry: 6, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 88, cy: 244, rx: 30, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'tree-ring-viewer': {
    canvas: [224, 256],
    ops: [
      { op: 'path', points: [[60, 240], [52, 160], [172, 160], [164, 240]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 68, y0: 248, x1: 68, y1: 238, width: 12, color: 'shade' },
      { op: 'line', x0: 156, y0: 248, x1: 156, y1: 238, width: 12, color: 'shade' },
      { op: 'blob', cx: 112, cy: 100, rx: 72, ry: 68, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 100, rx: 50, ry: 48, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 100, rx: 30, ry: 29, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 100, rx: 12, ry: 11, fill: 'CLAY.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[44, 68], [52, 60], [60, 90], [50, 96]], close: true, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[180, 68], [172, 60], [164, 90], [174, 96]], close: true, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'tug-anchor-post': {
    canvas: [200, 224],
    ops: [
      { op: 'blob', cx: 100, cy: 204, rx: 58, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[84, 196], [92, 96], [108, 96], [116, 196]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[100, 96], [140, 72], [156, 84]], close: false, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 96, rx: 12, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 160, cy: 92, rx: 20, ry: 18, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 160, cy: 92, rx: 9, ry: 8, fill: 'PAPER.floor', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 94, y0: 140, x1: 106, y1: 140, width: 3, color: 'INK.soft' },
    ],
  },
  'tunnel-window-run': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[36, 150], [40, 96], [72, 62], [184, 62], [216, 96], [220, 150], [188, 150], [186, 100], [166, 82], [90, 82], [70, 100], [68, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 128, cy: 118, rx: 62, ry: 34, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 98, y: 92, w: 60, h: 36, r: 10, fill: 'PAPER.floor', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 74, y0: 76, x1: 182, y1: 76, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 52, y0: 110, x1: 52, y1: 148, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 204, y0: 110, x1: 204, y1: 148, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 128, cy: 110, rx: 9, ry: 8, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'umbrella-drying-wheel': {
    canvas: [208, 224],
    ops: [
      { op: 'blob', cx: 104, cy: 202, rx: 64, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 104, y0: 196, x1: 104, y1: 92, width: 8, color: 'shade' },
      { op: 'blob', cx: 104, cy: 84, rx: 70, ry: 22, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 38, y0: 80, x1: 30, y1: 150, width: 4, color: 'shade' },
      { op: 'line', x0: 104, y0: 84, x1: 104, y1: 162, width: 4, color: 'shade' },
      { op: 'line', x0: 170, y0: 80, x1: 178, y1: 150, width: 4, color: 'shade' },
      { op: 'line', x0: 38, y0: 84, x1: 170, y1: 84, width: 2.5, color: 'INK.soft' },
      { op: 'blob', cx: 104, cy: 80, rx: 34, ry: 11, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 104, cy: 60, rx: 9, ry: 9, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'under-bench-slidebox': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[28, 40], [228, 40], [228, 58], [28, 58]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 58, x1: 52, y1: 92, width: 10, color: 'shade' },
      { op: 'line', x0: 208, y0: 58, x1: 204, y1: 92, width: 10, color: 'shade' },
      { op: 'rect', x: 40, y: 92, w: 176, h: 48, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 96, y0: 116, x1: 160, y1: 116, width: 6, color: 'shade' },
      { op: 'blob', cx: 68, cy: 146, rx: 13, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 188, cy: 146, rx: 13, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 120, y0: 92, x1: 136, y1: 92, width: 3, color: 'INK.soft' },
    ],
  },
  'utensil-rinse-pot': {
    canvas: [192, 256],
    ops: [
      { op: 'path', points: [[48, 96], [144, 96], [156, 232], [36, 232]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 96], [148, 96], [152, 84], [40, 84]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 96, y0: 92, x1: 96, y1: 170, width: 5, color: 'shade' },
      { op: 'line', x0: 52, y0: 196, x1: 140, y1: 196, width: 3, color: 'shade' },
      { op: 'line', x0: 78, y0: 62, x1: 82, y1: 92, width: 7, color: 'fill' },
      { op: 'line', x0: 110, y0: 58, x1: 108, y1: 92, width: 7, color: 'fill' },
      { op: 'blob', cx: 96, cy: 214, rx: 48, ry: 12, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'vegetable-washing-cradle': {
    canvas: [224, 256],
    ops: [
      { op: 'line', x0: 60, y0: 244, x1: 68, y1: 176, width: 11, color: 'shade' },
      { op: 'line', x0: 164, y0: 244, x1: 156, y1: 176, width: 11, color: 'shade' },
      { op: 'blob', cx: 112, cy: 188, rx: 78, ry: 34, n: 12, irregularity: 0.05, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 118], [180, 118], [160, 168], [64, 168]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 78, y0: 124, x1: 86, y1: 162, width: 3, color: 'shade' },
      { op: 'line', x0: 104, y0: 122, x1: 108, y1: 164, width: 3, color: 'shade' },
      { op: 'line', x0: 130, y0: 122, x1: 124, y1: 164, width: 3, color: 'shade' },
      { op: 'line', x0: 152, y0: 124, x1: 140, y1: 162, width: 3, color: 'shade' },
      { op: 'blob', cx: 112, cy: 210, rx: 58, ry: 12, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'victory-rest-station': {
    canvas: [256, 144],
    ops: [
      { op: 'blob', cx: 128, cy: 118, rx: 110, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 128, cy: 114, rx: 98, ry: 16, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[42, 108], [54, 80], [96, 80], [86, 108]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 69, cy: 84, rx: 22, ry: 7, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[108, 108], [112, 72], [150, 72], [146, 108]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 196, cy: 92, rx: 26, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 196, cy: 92, rx: 8, ry: 8, fill: 'ROBOT.accent', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 112, y0: 86, x1: 146, y1: 86, width: 2, color: 'INK.soft' },
    ],
  },
  'vine-training-wheel': {
    canvas: [208, 224],
    ops: [
      { op: 'line', x0: 104, y0: 216, x1: 104, y1: 140, width: 8, color: 'shade' },
      { op: 'blob', cx: 104, cy: 92, rx: 72, ry: 72, n: 15, irregularity: 0.03, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 92, rx: 50, ry: 50, n: 14, irregularity: 0.03, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 104, y0: 20, x1: 104, y1: 164, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 32, y0: 92, x1: 176, y1: 92, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 53, y0: 41, x1: 155, y1: 143, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 155, y0: 41, x1: 53, y1: 143, width: 3, color: 'INK.soft' },
      { op: 'path', points: [[104, 92], [132, 70], [150, 84], [128, 104]], close: true, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 104, cy: 92, rx: 9, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'visitor-choice-bench': {
    canvas: [256, 160],
    ops: [
      { op: 'line', x0: 48, y0: 150, x1: 52, y1: 104, width: 11, color: 'shade' },
      { op: 'line', x0: 120, y0: 150, x1: 120, y1: 104, width: 11, color: 'shade' },
      { op: 'line', x0: 208, y0: 150, x1: 204, y1: 104, width: 11, color: 'shade' },
      { op: 'path', points: [[28, 100], [120, 96], [120, 116], [30, 120]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[140, 96], [230, 100], [228, 120], [140, 116]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 120, y0: 108, x1: 140, y1: 108, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 50, y0: 104, x1: 74, y1: 103, width: 2, color: 'shade' },
      { op: 'line', x0: 168, y0: 104, x1: 192, y1: 104, width: 2, color: 'shade' },
    ],
  },
  'visor-cleaning-dock': {
    canvas: [176, 256],
    ops: [
      { op: 'path', points: [[44, 242], [132, 242], [124, 200], [52, 200]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 62, y0: 200, x1: 62, y1: 110, width: 12, color: 'shade' },
      { op: 'rect', x: 40, y: 92, w: 46, h: 26, r: 11, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 50, y0: 105, x1: 76, y1: 105, width: 3, color: 'INK.soft' },
      { op: 'path', points: [[100, 200], [126, 200], [130, 140], [96, 140]], close: true, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 102, y: 118, w: 22, h: 26, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 113, cy: 165, rx: 10, ry: 18, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'volunteer-checkout-board': {
    canvas: [176, 256],
    ops: [
      { op: 'line', x0: 52, y0: 244, x1: 52, y1: 56, width: 9, color: 'shade' },
      { op: 'line', x0: 124, y0: 244, x1: 124, y1: 56, width: 9, color: 'shade' },
      { op: 'rect', x: 34, y: 40, w: 108, h: 150, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 62, cy: 74, rx: 11, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 62, y0: 74, x1: 62, y1: 98, width: 4, color: 'shade' },
      { op: 'blob', cx: 114, cy: 74, rx: 11, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 114, y0: 74, x1: 98, y1: 94, width: 4, color: 'shade' },
      { op: 'blob', cx: 62, cy: 138, rx: 11, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 62, y0: 138, x1: 78, y1: 158, width: 4, color: 'shade' },
      { op: 'blob', cx: 114, cy: 138, rx: 5, ry: 5, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'walk-token-press': {
    canvas: [200, 208],
    ops: [
      { op: 'rect', x: 40, y: 150, w: 120, h: 48, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 150, rx: 40, ry: 12, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 150, y0: 188, x1: 150, y1: 40, width: 11, color: 'shade' },
      { op: 'path', points: [[150, 44], [78, 30], [74, 52], [150, 62]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 92, y0: 52, x1: 92, y1: 138, width: 9, color: 'shade' },
      { op: 'blob', cx: 92, cy: 146, rx: 22, ry: 7, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 80, cy: 42, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'walking-pace-wheel': {
    canvas: [200, 224],
    ops: [
      { op: 'line', x0: 100, y0: 54, x1: 92, y1: 150, width: 11, color: 'shade' },
      { op: 'rect', x: 70, y: 36, w: 60, h: 22, r: 11, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 92, cy: 166, rx: 50, ry: 50, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'blob', cx: 92, cy: 166, rx: 13, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 92, y0: 120, x1: 92, y1: 212, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 48, y0: 166, x1: 136, y1: 166, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 61, y0: 135, x1: 123, y1: 197, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 123, y0: 135, x1: 61, y1: 197, width: 3, color: 'INK.soft' },
    ],
  },
  'walking-stick-dock': {
    canvas: [200, 288],
    ops: [
      { op: 'path', points: [[56, 60], [152, 60], [148, 236], [60, 236]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 6 },
      { op: 'path', points: [[60, 150], [148, 150], [148, 236], [60, 236]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 70, y0: 72, x1: 108, y1: 138, width: 7, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 72, x1: 142, y1: 138, width: 7, color: 'INK.soft' },
      { op: 'line', x0: 86, y0: 54, x1: 118, y1: 120, width: 5, color: 'shade' },
      { op: 'blob', cx: 104, cy: 266, rx: 54, ry: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 104, cy: 262, rx: 40, ry: 11, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 104, y0: 236, x1: 104, y1: 250, width: 3, color: 'shade' },
    ],
  },
  'warm-milk-pan': {
    canvas: [256, 176],
    ops: [
      { op: 'blob', cx: 108, cy: 124, rx: 80, ry: 40, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 110, rx: 80, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 108, rx: 58, ry: 20, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[186, 104], [244, 98], [246, 116], [188, 122]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[244, 98], [252, 100], [252, 116], [246, 116]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 48, y0: 120, x1: 168, y1: 120, width: 2, color: 'INK.soft' },
    ],
  },
  'warm-stone-pocket': {
    canvas: [224, 176],
    ops: [
      { op: 'path', points: [[52, 162], [44, 80], [80, 52], [144, 52], [180, 80], [172, 162]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[72, 68], [152, 68], [148, 92], [76, 92]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 112, cy: 76, rx: 34, ry: 22, n: 12, irregularity: 0.1, fill: 'CLAY.mid', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 62, y0: 108, x1: 162, y1: 108, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 60, y0: 132, x1: 164, y1: 132, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 102, cy: 72, rx: 9, ry: 6, fill: 'CLAY.light', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'warming-flask-sleeve': {
    canvas: [256, 144],
    ops: [
      { op: 'rect', x: 44, y: 40, w: 168, h: 92, r: 22, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 84], [212, 84], [208, 132], [48, 132]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[176, 52], [224, 60], [220, 84], [176, 80]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 208, cy: 70, rx: 9, ry: 9, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 60, y0: 58, x1: 160, y1: 56, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 60, y0: 112, x1: 190, y1: 110, width: 2, color: 'INK.soft' },
    ],
  },
  'water-clarity-disc': {
    canvas: [200, 256],
    ops: [
      { op: 'line', x0: 100, y0: 24, x1: 100, y1: 150, width: 4, color: 'INK.soft' },
      { op: 'blob', cx: 100, cy: 184, rx: 68, ry: 58, fill: 'CLAY.pale', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[100, 128], [160, 160], [100, 184]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[100, 184], [40, 208], [100, 240]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 100, y0: 128, x1: 100, y1: 240, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 40, y0: 184, x1: 160, y1: 184, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 100, cy: 184, rx: 12, ry: 12, fill: 'WATER.deep', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'water-drop-metronome': {
    canvas: [200, 288],
    ops: [
      { op: 'blob', cx: 100, cy: 270, rx: 52, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[80, 210], [120, 210], [136, 260], [64, 260]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 208, rx: 42, ry: 16, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 206, rx: 30, ry: 9, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 100, y0: 206, x1: 104, y1: 70, width: 7, color: 'shade' },
      { op: 'path', points: [[104, 108], [148, 104], [148, 120], [104, 124]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 104, cy: 162, rx: 7, ry: 11, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 104, y0: 76, x1: 104, y1: 104, width: 2, color: 'INK.soft' },
    ],
  },
  'water-plant-anchor': {
    canvas: [160, 256],
    ops: [
      { op: 'blob', cx: 80, cy: 222, rx: 56, ry: 24, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 150, x1: 80, y1: 222, width: 9, color: 'fill' },
      { op: 'blob', cx: 80, cy: 138, rx: 40, ry: 18, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 80, cy: 134, rx: 26, ry: 11, fill: 'WATER.edge', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 68, y0: 134, x1: 52, y1: 40, width: 5, color: 'SAGE.deep' },
      { op: 'line', x0: 80, y0: 134, x1: 84, y1: 34, width: 5, color: 'SAGE.mid' },
      { op: 'line', x0: 92, y0: 134, x1: 110, y1: 48, width: 5, color: 'SAGE.deep' },
      { op: 'path', points: [[84, 34], [78, 18], [92, 24]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'water-reflection-frame': {
    canvas: [200, 256],
    ops: [
      { op: 'line', x0: 52, y0: 120, x1: 48, y1: 244, width: 9, color: 'shade' },
      { op: 'line', x0: 148, y0: 120, x1: 152, y1: 244, width: 9, color: 'shade' },
      { op: 'rect', x: 36, y: 28, w: 128, h: 104, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 56, y: 48, w: 88, h: 64, r: 6, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 62, y0: 80, x1: 138, y1: 80, width: 2, color: 'WATER.mid' },
      { op: 'line', x0: 66, y0: 94, x1: 134, y1: 94, width: 2, color: 'WATER.deep' },
      { op: 'blob', cx: 50, cy: 244, rx: 30, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 150, cy: 244, rx: 30, ry: 9, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'water-sound-cup-chain': {
    canvas: [200, 256],
    ops: [
      { op: 'line', x0: 40, y0: 24, x1: 40, y1: 244, width: 9, color: 'shade' },
      { op: 'blob', cx: 40, cy: 244, rx: 40, ry: 11, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[44, 60], [120, 52], [128, 84], [52, 92]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 128], [120, 120], [128, 152], [52, 160]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 196], [120, 188], [128, 220], [52, 228]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 92, x1: 40, y1: 120, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 40, y0: 160, x1: 40, y1: 188, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 60, y0: 68, x1: 116, y1: 62, width: 2, color: 'WATER.mid' },
    ],
  },
  'water-wheel-test-bench': {
    canvas: [224, 256],
    ops: [
      { op: 'path', points: [[28, 240], [28, 150], [196, 150], [196, 240]], close: true, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[34, 156], [190, 156], [190, 176], [34, 176]], close: true, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 40, y0: 248, x1: 40, y1: 238, width: 12, color: 'shade' },
      { op: 'line', x0: 184, y0: 248, x1: 184, y1: 238, width: 12, color: 'shade' },
      { op: 'blob', cx: 112, cy: 108, rx: 56, ry: 56, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 108, rx: 14, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 112, y0: 54, x1: 112, y1: 162, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 58, y0: 108, x1: 166, y1: 108, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 74, y0: 70, x1: 150, y1: 146, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 150, y0: 70, x1: 74, y1: 146, width: 4, color: 'INK.soft' },
      { op: 'path', points: [[88, 52], [100, 46], [104, 62], [92, 68]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'waypoint-kite': {
    canvas: [192, 304],
    ops: [
      { op: 'line', x0: 96, y0: 296, x1: 96, y1: 96, width: 11, color: 'shade' },
      { op: 'path', points: [[64, 288], [128, 288], [136, 296], [56, 296]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 96, y0: 104, x1: 160, y1: 196, width: 2.5, color: 'INK.soft' },
      { op: 'path', points: [[96, 40], [150, 92], [96, 150], [42, 92]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[96, 40], [150, 92], [96, 150]], close: true, fill: 'fill', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 96, y0: 40, x1: 96, y1: 150, width: 2.5, color: 'INK.soft' },
      { op: 'line', x0: 42, y0: 92, x1: 150, y1: 92, width: 2.5, color: 'INK.soft' },
      { op: 'path', points: [[150, 92], [176, 108], [170, 124], [150, 112]], close: true, fill: 'SAGE.mid', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 96, cy: 92, rx: 7, ry: 7, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'weather-cloak-stand': {
    canvas: [216, 240],
    ops: [
      { op: 'blob', cx: 108, cy: 224, rx: 56, ry: 15, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 108, y0: 220, x1: 108, y1: 60, width: 8, color: 'shade' },
      { op: 'path', points: [[28, 64], [188, 64], [180, 84], [36, 84]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[42, 84], [34, 176], [78, 176], [72, 84]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[144, 84], [150, 176], [186, 176], [174, 84]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 50, y0: 100, x1: 46, y1: 174, width: 2.5, color: 'shade' },
      { op: 'line', x0: 168, y0: 100, x1: 172, y1: 174, width: 2.5, color: 'shade' },
      { op: 'blob', cx: 56, cy: 180, rx: 5, ry: 4, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 162, cy: 180, rx: 5, ry: 4, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'weather-note-capsule': {
    canvas: [160, 224],
    ops: [
      { op: 'blob', cx: 80, cy: 208, rx: 44, ry: 13, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[52, 72], [108, 72], [100, 200], [60, 200]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 56, y0: 110, x1: 104, y1: 110, width: 2, color: 'shade' },
      { op: 'line', x0: 58, y0: 150, x1: 102, y1: 150, width: 2, color: 'shade' },
      { op: 'blob', cx: 80, cy: 70, rx: 34, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 80, cy: 60, rx: 24, ry: 9, fill: 'fill', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 80, y0: 60, x1: 96, y1: 52, width: 3, color: 'shade' },
      { op: 'blob', cx: 80, cy: 60, rx: 4, ry: 4, fill: 'CLAY.blossom', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'weighted-comfort-pad': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[28, 150], [34, 86], [222, 86], [228, 150]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 72, cy: 108, rx: 26, ry: 18, n: 10, irregularity: 0.08, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 128, cy: 104, rx: 26, ry: 18, n: 10, irregularity: 0.08, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 184, cy: 108, rx: 26, ry: 18, n: 10, irregularity: 0.08, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 100, y0: 90, x1: 100, y1: 148, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 156, y0: 90, x1: 156, y1: 148, width: 2, color: 'INK.soft' },
    ],
  },
  'welcome-boot-tray': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[28, 150], [228, 150], [212, 96], [44, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[44, 96], [212, 96], [206, 80], [50, 80]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[60, 80], [196, 80], [192, 62], [64, 62]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 74, y0: 72, x1: 182, y1: 72, width: 3, color: 'shade' },
      { op: 'line', x0: 80, y0: 118, x1: 80, y1: 140, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 118, x1: 112, y1: 140, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 144, y0: 118, x1: 144, y1: 140, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 176, y0: 118, x1: 176, y1: 140, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 60, y0: 128, x1: 196, y1: 128, width: 2, color: 'INK.soft' },
    ],
  },
  'welcome-chime-shutter': {
    canvas: [200, 208],
    ops: [
      { op: 'path', points: [[44, 194], [156, 194], [148, 166], [52, 166]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 100, y0: 166, x1: 100, y1: 42, width: 7, color: 'shade' },
      { op: 'path', points: [[52, 42], [148, 42], [152, 26], [48, 26]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[64, 42], [136, 42], [124, 96], [76, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 98, rx: 30, ry: 10, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 58, y: 104, w: 84, h: 54, r: 6, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 120, y0: 104, x1: 120, y1: 158, width: 3, color: 'shade' },
      { op: 'line', x0: 120, y0: 130, x1: 150, y1: 130, width: 4, color: 'shade' },
    ],
  },
  'welcome-light-chain': {
    canvas: [256, 176],
    ops: [
      { op: 'halo', cx: 60, cy: 74, r: 30 },
      { op: 'halo', cx: 196, cy: 74, r: 30 },
      { op: 'line', x0: 40, y0: 62, x1: 216, y1: 62, width: 4, color: 'shade' },
      { op: 'line', x0: 60, y0: 160, x1: 60, y1: 86, width: 8, color: 'shade' },
      { op: 'line', x0: 128, y0: 160, x1: 128, y1: 92, width: 8, color: 'shade' },
      { op: 'line', x0: 196, y0: 160, x1: 196, y1: 86, width: 8, color: 'shade' },
      { op: 'path', points: [[44, 86], [40, 60], [80, 60], [76, 86]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[112, 92], [108, 66], [148, 66], [144, 92]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[180, 86], [176, 60], [216, 60], [212, 86]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 60, cy: 76, rx: 5, ry: 5, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
    ],
  },
  'welcome-table-extension': {
    canvas: [288, 176],
    ops: [
      { op: 'rect', x: 96, y: 76, w: 96, h: 22, r: 6, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[16, 86], [96, 72], [96, 98], [20, 112]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[272, 86], [192, 72], [192, 98], [268, 112]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 96, x1: 108, y1: 160, width: 8, color: 'shade' },
      { op: 'line', x0: 176, y0: 96, x1: 180, y1: 160, width: 8, color: 'shade' },
      { op: 'line', x0: 40, y0: 104, x1: 44, y1: 158, width: 6, color: 'shade' },
      { op: 'line', x0: 248, y0: 104, x1: 244, y1: 158, width: 6, color: 'shade' },
      { op: 'blob', cx: 96, cy: 85, rx: 7, ry: 12, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'blob', cx: 192, cy: 85, rx: 7, ry: 12, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 40, y0: 158, x1: 248, y1: 158, width: 4, color: 'shade' },
    ],
  },
  'wet-dry-split-basket': {
    canvas: [256, 160],
    ops: [
      { op: 'path', points: [[36, 48], [220, 48], [206, 148], [50, 148]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 128, y0: 48, x1: 128, y1: 148, width: 6, color: 'shade' },
      { op: 'line', x0: 62, y0: 62, x1: 58, y1: 138, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 86, y0: 62, x1: 84, y1: 138, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 110, y0: 62, x1: 110, y1: 138, width: 3, color: 'INK.soft' },
      { op: 'path', points: [[140, 60], [196, 60], [190, 136], [146, 136]], close: true, fill: 'SAGE.light', outline: 'INK.soft', lineWidth: 3 },
      { op: 'blob', cx: 128, cy: 48, rx: 58, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 50, y0: 148, x1: 206, y1: 148, width: 4, color: 'shade' },
    ],
  },
  'wheel-truing-fork': {
    canvas: [224, 208],
    ops: [
      { op: 'rect', x: 48, y: 178, w: 128, h: 22, r: 7, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[60, 178], [60, 70], [80, 70], [80, 178]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[164, 178], [164, 70], [144, 70], [144, 178]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 104, rx: 48, ry: 48, n: 16, irregularity: 0.03, fill: 'WATER.edge', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 104, rx: 12, ry: 12, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 112, y0: 104, x1: 112, y1: 58, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 104, x1: 152, y1: 128, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 112, y0: 104, x1: 72, y1: 128, width: 2, color: 'INK.soft' },
    ],
  },
  'wildlife-track-sandbed': {
    canvas: [224, 224],
    ops: [
      { op: 'path', points: [[28, 210], [20, 116], [112, 96], [204, 116], [196, 210]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 132], [112, 118], [184, 132], [178, 200], [46, 200]], close: true, fill: 'WATER.sand', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 86, cy: 160, rx: 7, ry: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 104, cy: 172, rx: 7, ry: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 122, cy: 160, rx: 7, ry: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 104, cy: 146, rx: 6, ry: 5, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 40, y0: 132, x1: 40, y1: 200, width: 2, color: 'shade' },
      { op: 'line', x0: 184, y0: 132, x1: 178, y1: 200, width: 2, color: 'shade' },
    ],
  },
  'wildlife-water-leveler': {
    canvas: [224, 200],
    ops: [
      { op: 'path', points: [[36, 110], [52, 176], [172, 176], [188, 110]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 110, rx: 80, ry: 24, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 106, rx: 62, ry: 15, fill: 'WATER.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 112, y0: 104, x1: 162, y1: 74, width: 5, color: 'shade' },
      { op: 'blob', cx: 166, cy: 70, rx: 18, ry: 13, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 112, y0: 104, x1: 112, y1: 88, width: 4, color: 'INK.soft' },
      { op: 'line', x0: 80, y0: 104, x1: 104, y1: 102, width: 2, color: 'WATER.edge' },
    ],
  },
  'wind-direction-ribbons': {
    canvas: [176, 256],
    ops: [
      { op: 'blob', cx: 88, cy: 240, rx: 52, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 88, y0: 236, x1: 88, y1: 120, width: 8, color: 'shade' },
      { op: 'blob', cx: 88, cy: 84, rx: 52, ry: 52, n: 16, irregularity: 0.04, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 88, cy: 84, rx: 32, ry: 32, n: 16, irregularity: 0.04, fill: 'PAPER.skyBottom', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[136, 80], [168, 70], [164, 84], [136, 90]], close: true, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[136, 96], [166, 100], [160, 112], [136, 106]], close: true, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 3 },
      { op: 'path', points: [[40, 80], [10, 72], [14, 86], [40, 90]], close: true, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 3 },
      { op: 'line', x0: 88, y0: 122, x1: 88, y1: 136, width: 3, color: 'shade' },
    ],
  },
  'wind-load-model': {
    canvas: [224, 224],
    ops: [
      { op: 'blob', cx: 150, cy: 206, rx: 50, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 136, y0: 204, x1: 136, y1: 120, width: 8, color: 'shade' },
      { op: 'line', x0: 172, y0: 204, x1: 172, y1: 120, width: 8, color: 'shade' },
      { op: 'path', points: [[120, 124], [154, 90], [192, 124]], close: false, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[120, 124], [154, 90], [192, 124], [188, 134], [154, 102], [124, 134]], close: true, fill: 'CLAY.light', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 40, y0: 200, x1: 40, y1: 110, width: 8, color: 'shade' },
      { op: 'blob', cx: 40, cy: 204, rx: 24, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 40, cy: 96, rx: 36, ry: 36, fill: 'SAGE.light', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 40, y0: 76, x1: 40, y1: 116, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 20, y0: 96, x1: 60, y1: 96, width: 3, color: 'INK.soft' },
    ],
  },
  'wind-tempo-spinner': {
    canvas: [224, 288],
    ops: [
      { op: 'blob', cx: 112, cy: 272, rx: 44, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 112, y0: 266, x1: 112, y1: 120, width: 9, color: 'shade' },
      { op: 'line', x0: 52, y0: 118, x1: 172, y1: 118, width: 7, color: 'shade' },
      { op: 'line', x0: 112, y0: 150, x1: 112, y1: 86, width: 7, color: 'shade' },
      { op: 'blob', cx: 48, cy: 118, rx: 20, ry: 17, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 176, cy: 118, rx: 20, ry: 17, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 82, rx: 17, ry: 20, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 44, cy: 118, rx: 9, ry: 8, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 118, rx: 8, ry: 8, fill: 'shade', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'windbreak-planter-wall': {
    canvas: [216, 240],
    ops: [
      { op: 'blob', cx: 108, cy: 224, rx: 80, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[36, 152], [180, 152], [190, 216], [26, 216]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 108, cy: 152, rx: 74, ry: 14, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'rect', x: 44, y: 40, w: 128, h: 112, r: 4, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 72, y0: 40, x1: 72, y1: 152, width: 2.5, color: 'shade' },
      { op: 'line', x0: 108, y0: 40, x1: 108, y1: 152, width: 2.5, color: 'shade' },
      { op: 'line', x0: 144, y0: 40, x1: 144, y1: 152, width: 2.5, color: 'shade' },
      { op: 'line', x0: 44, y0: 78, x1: 172, y1: 78, width: 2.5, color: 'shade' },
      { op: 'line', x0: 44, y0: 116, x1: 172, y1: 116, width: 2.5, color: 'shade' },
      { op: 'blob', cx: 70, cy: 150, rx: 12, ry: 8, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 3 },
    ],
  },
  'window-candle-bridge': {
    canvas: [256, 224],
    ops: [
      { op: 'halo', cx: 128, cy: 110, r: 54 },
      { op: 'rect', x: 36, y: 150, w: 184, h: 36, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 92, y: 80, w: 72, h: 76, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 104, y: 94, w: 48, h: 56, r: 6, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'line', x0: 128, y0: 150, x1: 128, y1: 120, width: 5, color: 'shade' },
      { op: 'blob', cx: 128, cy: 116, rx: 7, ry: 12, fill: 'ROBOT.accent', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 48, y0: 168, x1: 208, y1: 168, width: 2, color: 'shade' },
      { op: 'blob', cx: 128, cy: 186, rx: 96, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
    ],
  },
  'window-nap-sling': {
    canvas: [256, 160],
    ops: [
      { op: 'line', x0: 40, y0: 150, x1: 40, y1: 52, width: 11, color: 'shade' },
      { op: 'line', x0: 216, y0: 150, x1: 216, y1: 52, width: 11, color: 'shade' },
      { op: 'line', x0: 40, y0: 150, x1: 216, y1: 150, width: 9, color: 'shade' },
      { op: 'path', points: [[44, 58], [100, 96], [156, 96], [212, 58], [212, 70], [156, 110], [100, 110], [44, 70]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 80, y0: 78, x1: 86, y1: 92, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 86, x1: 128, y1: 100, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 176, y0: 78, x1: 170, y1: 92, width: 2, color: 'INK.soft' },
    ],
  },
  'winter-cloche-vent': {
    canvas: [200, 256],
    ops: [
      { op: 'blob', cx: 100, cy: 228, rx: 74, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[34, 228], [40, 128], [80, 80], [120, 80], [160, 128], [166, 228]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 100, cy: 228, rx: 66, ry: 13, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[80, 80], [100, 98], [120, 80], [100, 64]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[88, 72], [72, 46], [96, 52]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[112, 72], [128, 46], [104, 52]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 62, y0: 140, x1: 58, y1: 210, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 138, y0: 140, x1: 142, y1: 210, width: 2, color: 'INK.soft' },
      { op: 'grass', x: 100, baseY: 200, height: 30, lean: 0, width: 4, color: 'SAGE.light' },
    ],
  },
  'winter-foot-pocket': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 44, y: 62, w: 168, h: 88, r: 40, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 84, cy: 106, rx: 24, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'blob', cx: 172, cy: 106, rx: 24, ry: 30, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'line', x0: 50, y0: 78, x1: 206, y1: 78, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 50, y0: 134, x1: 206, y1: 134, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 128, y0: 66, x1: 128, y1: 146, width: 3, color: 'shade' },
    ],
  },
  'winter-light-window': {
    canvas: [200, 288],
    ops: [
      { op: 'path', points: [[64, 264], [60, 210], [140, 210], [136, 264]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'halo', cx: 100, cy: 118, r: 94 },
      { op: 'rect', x: 42, y: 36, w: 116, h: 178, r: 12, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'rect', x: 60, y: 54, w: 80, h: 142, r: 6, fill: 'CLAY.pale', outline: 'INK.soft', lineWidth: 3 },
      { op: 'path', points: [[60, 196], [60, 150], [78, 134], [96, 154], [114, 128], [132, 150], [140, 160], [140, 196]], close: true, fill: 'SAGE.deep', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 100, y0: 54, x1: 100, y1: 196, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 60, y0: 118, x1: 140, y1: 118, width: 2, color: 'INK.soft' },
      { op: 'blob', cx: 100, cy: 94, rx: 13, ry: 13, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 2 },
    ],
  },
  'winter-seed-snow-roof': {
    canvas: [288, 224],
    ops: [
      { op: 'line', x0: 70, y0: 210, x1: 72, y1: 110, width: 11, color: 'shade' },
      { op: 'line', x0: 218, y0: 210, x1: 216, y1: 110, width: 11, color: 'shade' },
      { op: 'rect', x: 78, y: 172, w: 132, h: 34, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[90, 180], [198, 178], [196, 196], [92, 198]], close: true, fill: 'CLAY.mid', outline: 'INK.soft', lineWidth: 2 },
      { op: 'path', points: [[24, 108], [264, 102], [252, 72], [36, 78]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[24, 108], [264, 102], [262, 118], [26, 124]], close: true, fill: 'shade', outline: 'INK.soft', lineWidth: 2 },
      { op: 'line', x0: 80, y0: 92, x1: 210, y1: 88, width: 2, color: 'shade' },
      { op: 'speckle', x: 88, y: 180, w: 110, h: 18, count: 12, maxR: 1.3, alpha: 0.14, color: 'INK.grain' },
    ],
  },
  'wood-steaming-box': {
    canvas: [256, 160],
    ops: [
      { op: 'rect', x: 28, y: 92, w: 200, h: 62, r: 10, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[24, 92], [40, 68], [216, 68], [232, 92]], close: true, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 120, y0: 62, x1: 136, y1: 62, width: 9, color: 'CLAY.deep' },
      { op: 'path', points: [[56, 148], [64, 108], [120, 104], [176, 108], [184, 148]], close: false, outline: 'CLAY.mid', lineWidth: 7 },
      { op: 'line', x0: 48, y0: 120, x1: 52, y1: 146, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 204, y0: 120, x1: 200, y1: 146, width: 2, color: 'INK.soft' },
    ],
  },
  'worm-casting-sieve': {
    canvas: [224, 256],
    ops: [
      { op: 'rect', x: 48, y: 156, w: 128, h: 68, r: 8, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'path', points: [[40, 224], [40, 200], [184, 200], [184, 224]], close: false, fill: 'shade', outline: 'INK.soft', lineWidth: 3 },
      { op: 'rect', x: 44, y: 78, w: 136, h: 54, r: 6, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 72, y0: 80, x1: 72, y1: 130, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 104, y0: 80, x1: 104, y1: 130, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 136, y0: 80, x1: 136, y1: 130, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 46, y0: 96, x1: 178, y1: 96, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 46, y0: 114, x1: 178, y1: 114, width: 2, color: 'INK.soft' },
      { op: 'line', x0: 62, y0: 132, x1: 52, y1: 156, width: 4, color: 'shade' },
      { op: 'line', x0: 162, y0: 132, x1: 172, y1: 156, width: 4, color: 'shade' },
    ],
  },
  'year-end-memory-scale': {
    canvas: [224, 224],
    ops: [
      { op: 'path', points: [[100, 210], [104, 90], [120, 90], [124, 210]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 112, cy: 210, rx: 48, ry: 12, fill: 'shade', outline: 'INK.line', lineWidth: 4 },
      { op: 'path', points: [[36, 84], [188, 84], [186, 96], [38, 96]], close: true, fill: 'fill', outline: 'INK.line', lineWidth: 5 },
      { op: 'line', x0: 48, y0: 90, x1: 48, y1: 130, width: 3, color: 'INK.soft' },
      { op: 'line', x0: 176, y0: 90, x1: 176, y1: 130, width: 3, color: 'INK.soft' },
      { op: 'blob', cx: 48, cy: 146, rx: 34, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 176, cy: 146, rx: 34, ry: 18, fill: 'shade', outline: 'INK.line', lineWidth: 5 },
      { op: 'blob', cx: 42, cy: 140, rx: 10, ry: 7, fill: 'CLAY.blossom', outline: 'INK.line', lineWidth: 2 },
      { op: 'blob', cx: 176, cy: 140, rx: 10, ry: 7, fill: 'SAGE.mid', outline: 'INK.line', lineWidth: 2 },
      { op: 'blob', cx: 112, cy: 82, rx: 11, ry: 9, fill: 'CLAY.deep', outline: 'INK.line', lineWidth: 3 },
    ],
  },
};
